<template>
  <div class="draw-template-setup-container">


    <!-- Header -->
    <div class="template-setup-header">
      <div v-if="isContractorTemplate" class="contractor-template-notice">
        <span class="notice-icon">👷</span>
        <span class="notice-text">Contractor/Lender Template - For budget reconciliation and contractor tracking (no draw details)</span>
      </div>
      <div class="template-tabs">
        <div class="tab" :class="{ active: activeTab === 'draw_detail' }" @click="activeTab = 'draw_detail'">
          {{ isContractorTemplate ? 'Budget Detail' : 'Draw Detail' }}
        </div>
        <div class="tab" :class="{ active: activeTab === 'g703' }" @click="activeTab = 'g703'">
          G703
        </div>
      </div>
    </div>


    <!-- Loading State -->
    <div v-if="isLoading && !isProcessing" class="loading-section">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Processing...</p>
      </div>
    </div>

    <!-- Processing State -->
    <div v-if="isProcessing" class="processing-section">
      <div class="processing-content">
        <div class="processing-spinner">
          <div class="spinner"></div>
        </div>
        <h3>Document Processing In Progress</h3>
        <p class="processing-status">{{ processingStatus }}</p>
        <div class="processing-progress">
          <div class="progress-bar">
            <div class="progress-fill indeterminate"></div>
          </div>
        </div>
        <!-- Removed G703-specific messaging - just show "Document Processing In Progress" -->
      </div>
    </div>

    <!-- NEW: PDF Page Selection Step (Step 0) -->
    <div v-if="currentStep === 'pdf_page_selection'" class="pdf-page-selection-step">
      <div class="page-selection-header">
        <h3>🔍 Select Pages to Process</h3>
        <p>Your PDF has been converted to page images. Select which pages contain G702/G703 data and specify the document type for each.</p>
        
        <!-- Selection Summary -->
        <div class="selection-summary">
          <div class="summary-stats">
            <span class="stat-item">
              <strong>{{ selectedPdfPages.length }}</strong> of <strong>{{ pdfPagePreviews.length }}</strong> pages selected
            </span>
            <span class="stat-item">
              <strong>{{ selectedPdfPages.filter(p => p.documentType === 'gc_703').length }}</strong> General Contractor G703
            </span>
            <span class="stat-item">
              <strong>{{ selectedPdfPages.filter(p => p.documentType === 'sub_702').length }}</strong> Subcontractor G702
            </span>
            <span class="stat-item">
              <strong>{{ selectedPdfPages.filter(p => p.documentType === 'gc_702').length }}</strong> GC G702
            </span>
            <span class="stat-item">
              <strong>{{ selectedPdfPages.filter(p => p.documentType === 'lw').length }}</strong> Lien Waivers
            </span>
            <span class="stat-item">
              <strong>{{ selectedPdfPages.filter(p => p.documentType === 'invoice').length }}</strong> Invoices
            </span>
          </div>
          <div class="summary-actions">
            <button @click="selectAllPages" class="btn-select-all">Select All Pages</button>
            <button @click="clearAllSelections" class="btn-clear-all">Clear All</button>
          </div>
        </div>
      </div>

      <!-- Page Grid -->
      <div class="pdf-page-grid">
        <div v-for="page in pdfPagePreviews" :key="page.pageNumber" class="pdf-page-card">
          <!-- Page Preview Image -->
          <div class="page-preview">
            <img 
              :src="page.imageUrl" 
              :alt="`Page ${page.pageNumber}`" 
              class="page-image" 
              @click="openImageModal(page)"
              title="Click to enlarge"
            />
            <div class="page-number">Page {{ page.pageNumber }}</div>
            <div class="magnify-hint">🔍 Click to enlarge</div>
          </div>

          <!-- Page Selection Controls -->
          <div class="page-controls">
            <!-- Document Type Selection (auto-selects page) -->
            <div class="doc-type-checkboxes">
              <div class="checkbox-group">
                <label class="doc-type-checkbox">
                  <input 
                    type="checkbox" 
                    value="gc_702"
                    :checked="page.documentType === 'gc_702'"
                    @change="toggleDocumentType(page, 'gc_702')"
                  />
                  <span class="checkbox-label">GC 702</span>
                </label>
                <label class="doc-type-checkbox">
                  <input 
                    type="checkbox" 
                    value="gc_703"
                    :checked="page.documentType === 'gc_703'"
                    @change="toggleDocumentType(page, 'gc_703')"
                  />
                  <span class="checkbox-label">GC 703</span>
                </label>
                <label class="doc-type-checkbox">
                  <input 
                    type="checkbox" 
                    value="sub_702"
                    :checked="page.documentType === 'sub_702'"
                    @change="toggleDocumentType(page, 'sub_702')"
                  />
                  <span class="checkbox-label">Sub 702</span>
                </label>
                <label class="doc-type-checkbox">
                  <input 
                    type="checkbox" 
                    value="lw"
                    :checked="page.documentType === 'lw'"
                    @change="toggleDocumentType(page, 'lw')"
                  />
                  <span class="checkbox-label">LW</span>
                </label>
                <label class="doc-type-checkbox">
                  <input 
                    type="checkbox" 
                    value="invoice"
                    :checked="page.documentType === 'invoice'"
                    @change="toggleDocumentType(page, 'invoice')"
                  />
                  <span class="checkbox-label">Invoice</span>
                </label>
              </div>
            </div>

            <!-- AI Suggestion (if available) -->
            <div v-if="page.aiSuggestion" class="ai-suggestion">
              <small>💡 AI suggests: {{ getDocTypeLabel(page.aiSuggestion) }}</small>
              <button @click="acceptAiSuggestion(page)" class="btn-accept-ai">Accept</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Image Enlargement Modal -->
      <div v-if="showImageModal" class="image-modal-overlay" @click="closeImageModal">
        <div class="image-modal-content" @click.stop>
          <div class="modal-header">
            <h3>Page {{ selectedImagePage?.pageNumber }}</h3>
            <button @click="closeImageModal" class="modal-close">×</button>
          </div>
          <div class="modal-image-container">
            <img 
              :src="selectedImagePage?.imageUrl" 
              :alt="`Page ${selectedImagePage?.pageNumber} - Enlarged`"
              class="modal-image"
            />
          </div>
          <div class="modal-footer">
            <p>Click outside or press X to close</p>
          </div>
        </div>
      </div>

      <!-- Selected Pages Summary -->
      <div v-if="selectedPdfPages.length > 0" class="selected-pages-summary">
        <h4>📋 Selected for Processing ({{ selectedPdfPages.length }} pages):</h4>
        <div class="selected-list">
          <div v-for="page in selectedPdfPages" :key="page.pageNumber" class="selected-item">
            <span class="page-info">Page {{ page.pageNumber }}</span>
            <span class="doc-type-badge" :class="`type-${page.documentType}`">
              {{ getDocTypeLabel(page.documentType) }}
            </span>
            <button @click="removePageSelection(page)" class="btn-remove">×</button>
          </div>
        </div>
      </div>

      <!-- Process Button -->
      <div class="page-selection-actions">
        <button 
          @click="processPdfPages" 
          :disabled="selectedPdfPages.length === 0 || !allSelectedPagesHaveTypes"
          class="btn-process-pages"
        >
          🚀 Process {{ selectedPdfPages.length }} Selected Pages with AI Enabled OCR
        </button>
        <p v-if="selectedPdfPages.length > 0 && !allSelectedPagesHaveTypes" class="selection-warning">
          ⚠️ Please select a document type for all selected pages before processing.
        </p>
      </div>
    </div>


    <!-- Raw G703 Data Handler -->
    <div v-if="documentData && (documentData.raw_g703_data || documentData.data?.raw_table_data)" class="raw-g703-section">
      <h4>G703 Data Processed Successfully!</h4>
      <p>Found {{ documentData.raw_g703_data?.length }} line items extracted from your G703 document.</p>
      
      <!-- G703 Data Display -->
      <div class="g703-data-preview">
        <h4>Extracted G703 Line Items</h4>
        <p>Review the extracted data and proceed to map these items to DDA terms.</p>
        
        <table class="g703-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Original Budget</th>
              <th>Current Request</th>
              <th>Previously Funded</th>
              <th>Total Completed</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in documentData.raw_g703_data" :key="index">
              <td class="description-cell">{{ item.description || 'N/A' }}</td>
              <td class="currency">{{ formatCurrency(item.original_budget) }}</td>
              <td class="currency">{{ formatCurrency(item.current_request) }}</td>
              <td class="currency">{{ formatCurrency(item.previously_funded) }}</td>
              <td class="currency">{{ formatCurrency(item.total_completed_to_date) }}</td>
            </tr>
          </tbody>
                </table>
      </div>
      
      <!-- G703 Table Setup Step -->
      <div v-if="currentStep === 'g703_table_setup'" class="g703-table-setup-step">
        <div class="setup-header">
          <h4>G703 Table Structure Setup</h4>
          <p>First, identify the header row that contains column titles like "Description of Work", "Scheduled Value", etc.</p>
          <p class="setup-note">
            <strong>Tip:</strong> Look for the row with column headers. This is usually near the top and contains text like "DESCRIPTION OF WORK", "SCHEDULED VALUE", etc.
          </p>
          
          <!-- Debug info -->
          <div class="debug-info" style="background: #f0f0f0; padding: 10px; margin: 10px 0; border-radius: 4px;">
            <h5>Debug Info:</h5>
            <p><strong>Current Step:</strong> {{ currentStep }}</p>
            <p><strong>G703 Raw Data Length:</strong> {{ g703RawData?.length || 0 }}</p>
            <p><strong>G703 Header Row:</strong> {{ g703HeaderRow }}</p>
            <p><strong>Document Data URL:</strong> {{ documentData?.data?.document_url ? 'Available' : 'Not Available' }}</p>
            <p><strong>Data Columns:</strong> {{ g703RawData?.[0]?.length || 0 }}</p>
            <p><strong>Total Extracted Text:</strong> {{ g703RawData?.[0]?.filter(cell => cell && cell.trim()).length || 0 }} non-empty cells</p>
          </div>
        </div>
        
        <!-- Document Image Display -->
        <div v-if="documentData?.data?.document_url" class="document-preview">
          <h5>Original G703 Document:</h5>
          <div class="document-image-container">
            <!-- Direct link approach since iframe is blocked by X-Frame-Options -->
            <div class="document-viewer-card">
              <div class="viewer-placeholder">
                <div class="document-icon">📄</div>
                <h6>{{ documentData.data.filename || 'G703 Document' }}</h6>
                <p class="doc-info">Template ID: {{ route.params.templateId }}</p>
                <div class="viewer-actions">
                  <a :href="`${apiBase}documents/${documentData.data.document_id || templateInfo?.document_id}/image`" 
                     target="_blank" 
                     class="btn-view-doc">
                    🔍 View Document
                  </a>
                  <button @click="openDocumentViewer" class="btn-viewer">
                    📱 Open in Viewer
                  </button>
                </div>
              </div>
            </div>
          </div>
          <p class="pdf-note">
            <strong>💡 Tip:</strong> Click "View Document" to see the source G703 document while mapping columns below.
          </p>
        </div>
        
        <div class="g703-table-preview">
          <h5>Click on the Header Row:</h5>
          <p class="table-info">Showing {{ g703RawData?.[0]?.filter(cell => cell && cell.trim()).length || 0 }} extracted data points from your G703 document</p>
          <div class="raw-table-container">
            <table class="raw-g703-table">
              <tbody>
                <tr v-for="(row, rowIndex) in g703RawData" :key="rowIndex"
                    :class="{ 'selected-header-row': g703HeaderRow === rowIndex }"
                    @click="selectG703HeaderRow(rowIndex)">
                  <td class="row-number">{{ rowIndex }}</td>
                  <td v-for="(cell, cellIndex) in row.filter(cell => cell && cell.trim())" :key="cellIndex" class="table-cell">
                    {{ cell }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="extraction-explanation">
            <h6>📋 What happened during extraction?</h6>
            <p><strong>OCR Result:</strong> Your G703 PDF was processed and extracted as a single row with {{ g703RawData?.[0]?.length || 0 }} data points.</p>
            <p><strong>Why this happens:</strong> G703 forms have complex layouts that OCR sometimes reads as continuous text rather than structured tables.</p>
            <p><strong>Next step:</strong> Click on row 1 above to select it as your header row. This tells the system where your column headers are located.</p>
            <p class="tip">💡 <strong>Tip:</strong> The extracted data contains all your G703 form fields - we'll help you map them to the correct columns in the next step.</p>
          </div>
        </div>
      </div>
      
      <!-- G703 Column Mapping Step -->
      <div v-if="currentStep === 'g703_column_mapping'" class="g703-column-mapping-step">
        <div class="mapping-header">
          <h4>G703 Column Mapping</h4>
          <p>Now map each column to the correct G703 field. Click on the columns below to assign them:</p>
          
          <div class="mapping-legend">
            <div class="legend-item">
              <span class="legend-color" style="background: #2196F3;"></span>
              <span>Description of Work</span>
            </div>
            <div class="legend-item">
              <span class="legend-color" style="background: #4caf50;"></span>
              <span>Scheduled Value (Original Budget)</span>
            </div>
            <div class="legend-item">
              <span class="legend-color" style="background: #ff9800;"></span>
              <span>Work Completed Previous</span>
            </div>
            <div class="legend-item">
              <span class="legend-color" style="background: #9c27b0;"></span>
              <span>Work Completed This Period</span>
            </div>
            <div class="legend-item">
              <span class="legend-color" style="background: #f44336;"></span>
              <span>Materials Stored</span>
            </div>
            <div class="legend-item">
              <span class="legend-color" style="background: #607d8b;"></span>
              <span>Total Completed to Date</span>
            </div>
          </div>
        </div>
        
        <div class="column-selection-interface">
          <div class="current-mapping" v-if="g703HeaderRow !== null">
            <h5>Current Selection:</h5>
            
            <div class="template-actions">
              <button @click="applyDefaultG703Mapping" class="btn-secondary template-btn">
                🎯 Apply Template 33 (98% Match)
              </button>
              <button @click="loadSavedG703Template" class="btn-secondary template-btn">
                📁 Load Saved Template
              </button>
              <button @click="saveCurrentMappingAsDefault" class="btn-primary template-btn">
                💾 Save as Default
              </button>
            </div>
            
            <div class="selection-buttons">
              <!-- Core G703 Fields -->
              <button 
                :class="{ active: currentG703MappingField === 'trade_code_column' }"
                @click="currentG703MappingField = 'trade_code_column'"
                class="mapping-btn trade-code-btn">
                🔢 Trade Code
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'description_column' }"
                @click="currentG703MappingField = 'description_column'"
                class="mapping-btn description-btn">
                📝 Description of Work
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'contractor_names_column' }"
                @click="currentG703MappingField = 'contractor_names_column'"
                class="mapping-btn contractor-btn">
                👷 Contractor Names
              </button>
              
              <!-- Budget/Schedule Values -->
              <button 
                :class="{ active: currentG703MappingField === 'scheduled_value_column' }"
                @click="currentG703MappingField = 'scheduled_value_column'"
                class="mapping-btn scheduled-btn">
                💰 Scheduled Value
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'budget_transfer_column' }"
                @click="currentG703MappingField = 'budget_transfer_column'"
                class="mapping-btn budget-transfer-btn">
                💸 Budget Transfer
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'owner_previous_approved_column' }"
                @click="currentG703MappingField = 'owner_previous_approved_column'"
                class="mapping-btn owner-previous-btn">
                ✅ Owner Previous Approved
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'owner_approved_completed_column' }"
                @click="currentG703MappingField = 'owner_approved_completed_column'"
                class="mapping-btn owner-approved-btn">
                ✅ Owner Approved Completed
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'adjusted_schedule_values_column' }"
                @click="currentG703MappingField = 'adjusted_schedule_values_column'"
                class="mapping-btn adjusted-btn">
                📊 Adjusted Schedule Values
              </button>
              
              <!-- Work Completed Fields -->
              <button 
                :class="{ active: currentG703MappingField === 'subcontractor_completed_column' }"
                @click="currentG703MappingField = 'subcontractor_completed_column'"
                class="mapping-btn subcontractor-btn">
                🔧 Subcontractor Completed
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'work_completed_previous_column' }"
                @click="currentG703MappingField = 'work_completed_previous_column'"
                class="mapping-btn previous-btn">
                📊 Work Previous
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'work_completed_this_period_column' }"
                @click="currentG703MappingField = 'work_completed_this_period_column'"
                class="mapping-btn current-btn">
                🔨 Work This Period
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'materials_stored_column' }"
                @click="currentG703MappingField = 'materials_stored_column'"
                class="mapping-btn materials-btn">
                📦 Materials Stored
              </button>
              
              <!-- Totals and Balance -->
              <button 
                :class="{ active: currentG703MappingField === 'total_completed_column' }"
                @click="currentG703MappingField = 'total_completed_column'"
                class="mapping-btn total-btn">
                ✅ Total Completed
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'percent_complete_column' }"
                @click="currentG703MappingField = 'percent_complete_column'"
                class="mapping-btn percent-btn">
                📊 % Complete
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'balance_to_finish_column' }"
                @click="currentG703MappingField = 'balance_to_finish_column'"
                class="mapping-btn balance-btn">
                🎯 Balance to Finish
              </button>
              
              <!-- Retainage Fields -->
              <button 
                :class="{ active: currentG703MappingField === 'retainage_percent_column' }"
                @click="currentG703MappingField = 'retainage_percent_column'"
                class="mapping-btn retainage-percent-btn">
                🔒 Retainage %
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'retainage_this_period_column' }"
                @click="currentG703MappingField = 'retainage_this_period_column'"
                class="mapping-btn retainage-period-btn">
                🔒 Retainage This Period
              </button>
              <button 
                :class="{ active: currentG703MappingField === 'retainage_total_column' }"
                @click="currentG703MappingField = 'retainage_total_column'"
                class="mapping-btn retainage-total-btn">
                🔒 Retainage Total
              </button>
            </div>
            
            <p class="mapping-instruction">
              <strong>Step:</strong> Currently mapping <strong>{{ g703MappingLabels[currentG703MappingField] || 'Description of Work' }}</strong> - click on the corresponding column in the table below.
              <span v-if="g703ColumnMapping[currentG703MappingField] !== null" class="current-mapping">
                (Currently mapped to Column {{ g703ColumnMapping[currentG703MappingField] + 1 }})
              </span>
            </p>
          </div>
          
          <div class="g703-table-preview">
            <div class="raw-table-container">
              <table class="raw-g703-table mapping-mode">
                <tbody>
                  <tr v-for="(row, rowIndex) in g703RawData" :key="rowIndex"
                      :class="{ 'selected-header-row': g703HeaderRow === rowIndex }">
                    <td class="row-number">{{ rowIndex }}</td>
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex" 
                        class="table-cell clickable-cell"
                        :class="getG703ColumnHighlight(cellIndex, currentG703MappingField)"
                        @click="selectG703Column(cellIndex, currentG703MappingField)">
                      {{ cell || '' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div class="mapping-summary">
            <h5>Column Mappings:</h5>
            <div class="mapping-list">
                          <div v-for="(label, mappingKey) in g703MappingLabels" :key="mappingKey" class="mapping-summary-item">
              <span class="field-label">{{ label }}:</span>
              <span class="mapped-column" :class="{ mapped: g703ColumnMapping[mappingKey] !== null }">
                {{ g703ColumnMapping[mappingKey] !== null ? `Column ${g703ColumnMapping[mappingKey] + 1}` : 'Column Null' }}
              </span>
            </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- G703 Manual Correction Step -->
      <div v-if="currentStep === 'g703_correction'" class="g703-correction-step">
        <div class="correction-header">
          <h4>G703 Data Manual Correction</h4>
          <p>Review and correct the OCR-extracted data below. This is critical for accurate G703 processing.</p>
          <p class="correction-note">
            <strong>Important:</strong> OCR may have misread descriptions or amounts. Please verify and correct any errors before proceeding.
          </p>
        </div>
        
        <div class="editable-g703-table-container">
          <table class="editable-g703-table">
            <thead>
              <tr>
                <th>Description of Work</th>
                <th>Original Budget</th>
                <th>Current Request</th>
                <th>Previously Funded</th>
                <th>Total Completed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in editableG703Data" :key="index" class="editable-row">
                <td class="description-edit-cell">
                  <textarea 
                    v-model="item.description" 
                    class="description-input"
                    rows="2"
                    placeholder="Enter description of work..."
                  ></textarea>
                </td>
                <td class="currency-edit-cell">
                  <input 
                    type="number" 
                    v-model.number="item.original_budget" 
                    class="currency-input"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </td>
                <td class="currency-edit-cell">
                  <input 
                    type="number" 
                    v-model.number="item.current_request" 
                    class="currency-input"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </td>
                <td class="currency-edit-cell">
                  <input 
                    type="number" 
                    v-model.number="item.previously_funded" 
                    class="currency-input"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </td>
                <td class="currency-edit-cell">
                  <input 
                    type="number" 
                    v-model.number="item.total_completed_to_date" 
                    class="currency-input"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </td>
                <td class="actions-cell">
                  <button 
                    @click="removeG703Item(index)" 
                    class="delete-btn"
                    title="Remove this line item"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="correction-actions">
          <button @click="addG703Item" class="btn-secondary add-item-btn">
            + Add New Line Item
          </button>
          
          <div class="correction-summary">
            <p><strong>{{ editableG703Data.length }}</strong> line items ready for review</p>
          </div>
          
          <button @click="proceedFromCorrection" class="btn-primary continue-btn">
            Continue to Review
          </button>
        </div>
      </div>
      
      <!-- G703 Review Step -->
      <div v-if="currentStep === 'g703_review'" class="g703-review-step">
        <div class="review-actions">
          <p>The system has successfully extracted {{ documentData.raw_g703_data?.length }} line items from your G703 document.</p>
          <p>Click "Continue to DDA Mapping" to proceed with assigning these items to DDA terms.</p>
          
          <div class="action-buttons">
            <button class="btn-primary" @click="proceedToDDAMapping">
              Continue to DDA Mapping
            </button>
          </div>
        </div>
      </div>
      
      <!-- G703 DDA Mapping Step -->
      <div v-if="currentStep === 'g703_dda_mapping'" class="g703-dda-mapping-step">
        <div class="mapping-interface">
          <h4>G703 DDA Term Classification</h4>
          <p>Assign each G703 line item to the appropriate DDA term. Use the search to find matching terms.</p>
          
          <div class="dda-mapping-table-container">
            <table class="dda-mapping-table">
              <thead>
                <tr>
                  <th class="dda-term-header">DDA Term</th>
                  <th class="description-header">Description of Work</th>
                  <th class="budget-header">Original Budget</th>
                  <th class="billing-header">Current Request</th>
                  <th class="billing-header">Previously Funded</th>
                  <th class="billing-header">Total Completed</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in classificationItems" :key="index"
                    :class="{ 'selected-row': selectedExcelItem === index }"
                    @click="selectExcelItem(index)">
                  <td class="dda-term-cell">
                    <div class="dda-term-wrapper">
                      <span v-if="item.auto_matched" class="auto-match-badge">AUTO</span>
                      <div class="dda-autocomplete" :class="{ 'auto-matched': item.auto_matched }">
                        <input 
                          type="text" 
                          v-model="item.searchTerm"
                          :placeholder="item.dda_term_id ? getSelectedTermName(item.dda_term_id) : 'Search DDA Terms...'"
                          @input="searchDDATerms(item, $event.target.value)"
                          @focus="handleFocus(item)"
                          @blur="handleBlur(item)"
                          class="dda-search-input"
                          :class="{ 'has-selection': item.dda_term_id, 'auto-matched': item.auto_matched }"
                        />
                        <div v-if="item.showDropdown && item.searchResults" class="dda-dropdown">
                          <div v-if="item.searchResults.length === 0" class="no-results">
                            No matching DDA terms found
                          </div>
                          <div 
                            v-for="term in item.searchResults.slice(0, 10)" 
                            :key="term.id"
                            class="dda-option"
                            @mousedown="selectDDATermForItem(item, term)"
                            :class="{ 'selected': item.dda_term_id === term.id }"
                          >
                            <div class="term-name">{{ term.term }}</div>
                            <div v-if="term.category_name" class="term-category">{{ term.category_name }}</div>
                          </div>
                        </div>
                        <div v-if="item.dda_term_id" class="selected-term-display">
                          <span class="selected-term-name">{{ getSelectedTermName(item.dda_term_id) }}</span>
                          <button @click="clearSelection(item)" class="clear-selection" type="button">×</button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="description-cell">{{ item.description }}</td>
                  <td class="currency">{{ formatCurrency(item.original_budget) }}</td>
                  <td class="currency">{{ formatCurrency(item.current_request) }}</td>
                  <td class="currency">{{ formatCurrency(item.previously_funded) }}</td>
                  <td class="currency">{{ formatCurrency(item.total_completed_to_date) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="mapping-progress">
            <div class="progress-info">
              <strong>Classification Progress:</strong> 
              {{ classifiedCount }} of {{ classificationItems.length }} items mapped 
              ({{ Math.round(classificationProgress) }}%)
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: classificationProgress + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- NEW STREAMLINED EXCEL WORKFLOW -->
    <div v-if="documentData && documentData.raw_excel_data" class="new-excel-workflow">
      
      <!-- Step 1: Tab Selection -->
      <div v-if="currentStep === 'tab_selection'" class="tab-selection-step">
        <!-- Clean Progress Bar -->
        <div class="step-header-layout">
          <div class="progress-container">
            <div class="progress-steps">
              <div class="progress-step active">
                <div class="step-circle">1</div>
                <div class="step-title">Select the Tab</div>
              </div>
              <div class="progress-step">
                <div class="step-circle">2</div>
                <div class="step-title">Select the Extents</div>
              </div>
              <div class="progress-step">
                <div class="step-circle">3</div>
                <div class="step-title">Select the Columns</div>
              </div>
              <div class="progress-step">
                <div class="step-circle">4</div>
                <div class="step-title">Select the Rows</div>
              </div>
              <div class="progress-step">
                <div class="step-circle">5</div>
                <div class="step-title">Review & Lock</div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="tabs-grid">
          <div v-for="(tab, index) in availableTabs" :key="index" 
               class="tab-card" 
               :class="{ selected: selectedTabs.includes(index) }"
               @click="toggleTabSelection(index)">
            <div class="tab-header">
              <input type="checkbox" 
                     :checked="selectedTabs.includes(index)"
                     class="tab-checkbox" 
                     readonly />
              <div class="tab-name">{{ tab.name }}</div>
            </div>
            <div class="tab-stats">
              <div class="stat"><strong>{{ tab.rows }}</strong> rows</div>
              <div class="stat"><strong>{{ tab.cols }}</strong> columns</div>
            </div>
            <div class="tab-preview">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th v-for="(cell, cellIdx) in tab.preview[0]?.slice(0, 4)" :key="cellIdx">
                      {{ cell || 'Col ' + (cellIdx + 1) }}
                    </th>
                    <th v-if="tab.preview[0]?.length > 4">...</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIdx) in tab.preview.slice(1, 4)" :key="rowIdx">
                    <td v-for="(cell, cellIdx) in row.slice(0, 4)" :key="cellIdx">
                      {{ cell || '-' }}
                    </td>
                    <td v-if="row.length > 4">...</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <div v-if="selectedTabs.length > 0" class="step-1-footer">
          <div class="selection-summary">
            <span class="summary-text">
              <span class="summary-icon">✅</span>
              {{ selectedTabs.length }} worksheet{{ selectedTabs.length === 1 ? '' : 's' }} selected
            </span>
          </div>
        </div>
      </div>

      <!-- Step 2: Visual Boundary Definition -->
      <div v-if="currentStep === 'boundary_definition'" class="boundary-definition-step">
        <!-- Clean Progress Bar -->
        <div class="step-header-layout">
          <div class="progress-container">
            <div class="progress-steps">
              <div class="progress-step completed">
                <div class="step-circle">✓</div>
                <div class="step-title">Select the Tab</div>
              </div>
              <div class="progress-step active">
                <div class="step-circle">2</div>
                <div class="step-title">Select the Extents</div>
              </div>
              <div class="progress-step">
                <div class="step-circle">3</div>
                <div class="step-title">Select the Columns</div>
              </div>
              <div class="progress-step">
                <div class="step-circle">4</div>
                <div class="step-title">Select the Rows</div>
              </div>
              <div class="progress-step">
                <div class="step-circle">5</div>
                <div class="step-title">Review & Lock</div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="boundary-tabs">
          <div class="tab-selector">
            <button v-for="tabIndex in selectedTabs" :key="tabIndex" 
                    class="tab-btn"
                    :class="{ active: currentBoundaryTab === tabIndex }"
                    @click="currentBoundaryTab = tabIndex">
              {{ availableTabs[tabIndex].name }}
            </button>
          </div>
          
          <div v-if="currentBoundaryTab !== null" class="boundary-instructions">
            <div class="instruction-card">
              <div class="instruction-step">
                <span class="step-number">1</span>
                <span class="step-text">Click on the <strong>header row</strong> (contains column names like "Description", "Budget", etc.)</span>
              </div>
              <div class="instruction-step">
                <span class="step-number">2</span>
                <span class="step-text"><strong>Drag to select</strong> your data area (exclude totals, notes, empty rows)</span>
              </div>
              <div class="instruction-step">
                <span class="step-number">3</span>
                <span class="step-text">Header row = <span style="color: #3b82f6; font-weight: 600;">Blue</span>, Data area = <span style="color: #2563eb; font-weight: 600;">Light Blue</span>, Dragging = <span style="color: #f59e0b; font-weight: 600;">Orange</span></span>
              </div>
            </div>
            
            <div class="current-selection" v-if="tabBoundaries[currentBoundaryTab]">
              <strong>Current Selection:</strong>
              Header Row: {{ tabBoundaries[currentBoundaryTab].headerRow }}, 
              Data Rows: {{ tabBoundaries[currentBoundaryTab].dataStartRow }}-{{ tabBoundaries[currentBoundaryTab].dataEndRow || 'Last' }}, 
              Columns: {{ tabBoundaries[currentBoundaryTab].startColumn }}-{{ tabBoundaries[currentBoundaryTab].endColumn }}
              <div v-if="tabBoundaries[currentBoundaryTab].combinedHeaders" class="multi-header-indicator">
                ✨ <strong>Smart multi-row headers detected!</strong> Column names have been intelligently combined from multiple rows.
              </div>
            </div>
          </div>
          
          <div v-if="currentBoundaryTab !== null" class="full-excel-view">
            <div class="excel-table-container">
              <table class="visual-excel-table">
                <tbody>
                  <tr v-for="(row, rowIndex) in getAllRowsForTab(currentBoundaryTab)" :key="rowIndex"
                       :class="getRowClasses(rowIndex)"
                       :data-row-index="rowIndex"
                       @click="handleRowClick(rowIndex)"
                       @mousedown="startSelection(rowIndex, $event)"
                       @mouseover="extendSelection(rowIndex)"
                       @mouseup="endSelection()"
                       style="user-select: none;">
                    <td class="row-number">{{ rowIndex }}</td>
                    <td v-for="(cell, colIndex) in row" :key="colIndex" 
                        class="excel-cell"
                        :class="getCellClasses(rowIndex, colIndex)"
                        @click.stop="handleCellClick(rowIndex, colIndex)">
                      {{ formatCellValue(cell) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
          </div>
        </div>
      </div>



      <!-- Step 3: Visual Column Mapping with Excel Grid -->
      <div v-if="currentStep === 'column_mapping'" class="column-mapping-step">
        <!-- Clean Progress Bar -->
        <div class="step-header-layout">
          <div class="progress-container">
            <div class="progress-steps">
              <div class="progress-step completed">
                <div class="step-circle">✓</div>
                <div class="step-title">Select the Tab</div>
              </div>
              <div class="progress-step completed">
                <div class="step-circle">✓</div>
                <div class="step-title">Select the Extents</div>
              </div>
              <div class="progress-step active">
                <div class="step-circle">3</div>
                <div class="step-title">Select the Columns</div>
              </div>
              <div class="progress-step">
                <div class="step-circle">4</div>
                <div class="step-title">Select the Rows</div>
              </div>
              <div class="progress-step">
                <div class="step-circle">5</div>
                <div class="step-title">Review & Lock</div>
              </div>
            </div>
          </div>
        </div>
        

        <!-- Current Field Selector -->
        <div class="field-selector">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <h4 style="margin: 0;">Select field to map:</h4>
            <button @click="autoDetectColumns" class="auto-detect-btn" :disabled="isAutoDetecting">
              <span v-if="!isAutoDetecting">🤖 Auto-Detect Columns</span>
              <span v-else>🔄 Detecting...</span>
            </button>
          </div>
          <div class="field-buttons">
            <button @click="currentMappingField = 'description'" 
                    :class="{ active: currentMappingField === 'description' }"
                    class="field-btn required">
              <span class="field-dot description-dot"></span> Description ({{ simpleColumnMappings.description.length }} columns)
            </button>
            <button @click="currentMappingField = 'original_budget'" 
                    :class="{ active: currentMappingField === 'original_budget' }"
                    class="field-btn required">
              <span class="field-dot original-budget-dot"></span> Original Budget {{ simpleColumnMappings.original_budget !== null ? '✓' : '' }}
            </button>
            <button @click="currentMappingField = 'current_budget'" 
                    :class="{ active: currentMappingField === 'current_budget' }"
                    class="field-btn required">
              <span class="field-dot current-budget-dot"></span> Current Budget {{ simpleColumnMappings.current_budget !== null ? '✓' : '' }}
            </button>

            <button @click="currentMappingField = 'spent_to_date'" 
                    :class="{ active: currentMappingField === 'spent_to_date' }"
                    class="field-btn optional">
              <span class="field-dot spent-to-date-dot"></span> Spent to Date {{ simpleColumnMappings.spent_to_date !== null ? '✓' : '' }}
            </button>
            <button @click="currentMappingField = 'remaining_budget'" 
                    :class="{ active: currentMappingField === 'remaining_budget' }"
                    class="field-btn optional">
              <span class="field-dot remaining-budget-dot"></span> Remaining Budget {{ simpleColumnMappings.remaining_budget !== null ? '✓' : '' }}
            </button>
            <button @click="currentMappingField = 'funded_at_closing'" 
                    :class="{ active: currentMappingField === 'funded_at_closing' }"
                    class="field-btn optional">
              <span class="field-dot funded-at-closing-dot"></span> Funded at Closing {{ simpleColumnMappings.funded_at_closing.length > 0 ? '✓' : '' }}
            </button>
            <button @click="currentMappingField = 'current_draw'" 
                    :class="{ active: currentMappingField === 'current_draw' }"
                    class="field-btn optional">
              <span class="field-dot current-draw-dot"></span> Current Draw (can select multiple) {{ simpleColumnMappings.current_draw !== null ? '✓' : '' }}
            </button>
          </div>
          <p class="mapping-instruction">
            Currently mapping: <strong>{{ getMappingFieldLabel(currentMappingField) }}</strong>
            <span v-if="currentMappingField === 'description'"> - Click multiple columns for description hierarchy</span>
            <span v-else-if="currentMappingField === 'funded_at_closing'"> - Click multiple columns to sum for total funded amount</span>
            <span v-else> - Click one column to assign</span>
          </p>
          
        </div>

        <!-- Excel Grid for Column Mapping -->
        <div class="excel-grid-mapping">

          <div class="excel-table-container">
            <p><strong>🔍 Debug:</strong> Rows: {{ getCleanDataForMapping().length }}, First row columns: {{ getCleanDataForMapping()[0]?.length || 0 }}</p>
            <table class="visual-excel-table">
              <thead>
                <!-- Show original multi-row headers for better context -->
                <tr v-for="(headerRow, headerRowIndex) in getMultiRowHeadersForDisplay(selectedTabs[0])" :key="`header-${headerRowIndex}`"
                    :class="`header-row-${headerRowIndex}`">
                  <th class="row-header" v-if="headerRowIndex === 0">#</th>
                  <th class="row-header-spacer" v-else></th>
                  <th v-for="(header, idx) in headerRow" :key="`${headerRowIndex}-${idx}`" 
                      class="data-header"
                      :class="[getMappingColumnClasses(idx), { 'primary-header': headerRowIndex === getMultiRowHeadersForDisplay(selectedTabs[0]).length - 1 }]"
                      :title="header || 'Column'"
                      @click="headerRowIndex === getMultiRowHeadersForDisplay(selectedTabs[0]).length - 1 ? toggleColumnMapping(idx) : null">
                    {{ formatHeaderValue(header) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in getCleanDataForMapping().slice(0, 1000)" :key="rowIndex"
                   :class="getExcelRowClasses(rowIndex)">
                  <td class="row-number">
                    {{ rowIndex + 1 }}
                  </td>
                  <td v-for="(cell, colIndex) in row" :key="colIndex" 
                      class="excel-cell"
                      :class="getMappingColumnClasses(colIndex)"
                      @click="toggleColumnMapping(colIndex)">
                    {{ formatCellValue(cell) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Mapping Summary -->
        <div class="mapping-summary">
          <h4>📋 Current Mappings</h4>
          <div class="mapping-summary-grid">
            <div class="mapping-item">
              <span class="field-label">📝 Description:</span>
              <span class="mapped-columns">
                {{ simpleColumnMappings.description.length > 0 ? 
                   simpleColumnMappings.description.map(col => `Column ${col + 1}`).join(', ') : 
                   'Not mapped' }}
              </span>
            </div>
            <div class="mapping-item">
              <span class="field-label">💰 Original Budget:</span>
              <span class="mapped-columns">
                {{ simpleColumnMappings.original_budget !== null ? 
                   `Column ${simpleColumnMappings.original_budget + 1}` : 
                   'Not mapped' }}
              </span>
            </div>
            <div class="mapping-item">
              <span class="field-label">📊 Current Budget:</span>
              <span class="mapped-columns">
                {{ simpleColumnMappings.current_budget !== null ? 
                   `Column ${simpleColumnMappings.current_budget + 1}` : 
                   'Not mapped' }}
              </span>
            </div>

            <div class="mapping-item">
              <span class="field-label">💸 Spent to Date:</span>
              <span class="mapped-columns">
                {{ simpleColumnMappings.spent_to_date !== null ? 
                   `Column ${simpleColumnMappings.spent_to_date + 1}` : 
                   'Not mapped (optional)' }}
              </span>
            </div>
            <div class="mapping-item">
              <span class="field-label">💰 Remaining Budget:</span>
              <span class="mapped-columns">
                {{ simpleColumnMappings.remaining_budget !== null ? 
                   `Column ${simpleColumnMappings.remaining_budget + 1}` : 
                   'Not mapped (optional)' }}
              </span>
            </div>
            <div class="mapping-item">
              <span class="field-label">🏦 Funded at Closing:</span>
              <span class="mapped-columns">
                <span v-if="simpleColumnMappings.funded_at_closing.length > 0">
                  {{ simpleColumnMappings.funded_at_closing.map(idx => `Column ${idx + 1}`).join(' + ') }}
                  <span class="sum-indicator"> (summed)</span>
                </span>
                <span v-else>Not mapped (optional)</span>
              </span>
            </div>
            <div class="mapping-item">
              <span class="field-label">📅 Current Draw (will be summed):</span>
              <span class="mapped-columns">
                {{ simpleColumnMappings.current_draw !== null && simpleColumnMappings.current_draw.length > 0 ? 
                   `Column ${simpleColumnMappings.current_draw + 1}` : 
                   'Not mapped (optional)' }}
              </span>
            </div>
          </div>
        </div>


        </div>
      </div>
    </div>

    <!-- Step 4: Total Rows Selection -->
    <div v-if="currentStep === 'total_rows_selection'" class="total-rows-selection-step">
      <!-- Clean Progress Bar -->
      <div class="step-header-layout">
        <div class="progress-container">
          <div class="progress-steps">
            <div class="progress-step completed">
              <div class="step-circle">✓</div>
              <div class="step-title">Select the Tab</div>
            </div>
            <div class="progress-step completed">
              <div class="step-circle">✓</div>
              <div class="step-title">Select the Extents</div>
            </div>
            <div class="progress-step completed">
              <div class="step-circle">✓</div>
              <div class="step-title">Select the Columns</div>
            </div>
            <div class="progress-step active">
              <div class="step-circle">4</div>
              <div class="step-title">Select the Rows</div>
            </div>
            <div class="progress-step">
              <div class="step-circle">5</div>
              <div class="step-title">Review & Lock</div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="total-rows-interface">
        <div class="category-selector">
          <h4>Select Category for Row Assignment:</h4>
          <div class="category-buttons">
            <button v-for="category in totalCategories" :key="category.key"
                    :class="['category-btn', 'modern-category-btn', category.key, { active: selectedTotalCategory === category.key }]"
                    @click="selectedTotalCategory = category.key"
                    :title="category.tooltip">
              <span class="category-icon-badge" :class="category.key + '-badge'">
                <i :class="getCategoryIcon(category.key)"></i>
              </span>
              <span class="category-text">
                {{ category.label }}
                <span v-if="category.tooltip" class="tooltip-hint">
                  <br>
                  <small>or {{ category.tooltip.replace('Also known as ', '') }}</small>
                </span>
              </span>
            </button>
          </div>
        </div>

        <!-- Excel Grid for Total Row Selection (matches Step 3 styling) -->
        <div class="excel-grid-mapping">
          <div class="excel-table-container">
            <table class="visual-excel-table">
              <thead>
                <!-- Show original multi-row headers for better context -->
                <tr v-for="(headerRow, headerRowIndex) in getMultiRowHeadersForDisplay(selectedTabs[0])" :key="`header-${headerRowIndex}`"
                    :class="`header-row-${headerRowIndex}`">
                  <th class="row-header" v-if="headerRowIndex === 0">#</th>
                  <th class="row-header-spacer" v-else></th>
                  <th v-for="(header, idx) in headerRow" :key="`${headerRowIndex}-${idx}`" 
                      class="data-header">
                    {{ formatHeaderValue(header) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in getCleanDataForMapping().slice(0, 1000)" :key="rowIndex"
                   :class="getTotalRowExcelRowClasses(getActualRowNumber(rowIndex))">
                  <td class="row-number clickable-total-row-number" 
                      :class="getTotalRowClasses(getActualRowNumber(rowIndex))"
                      @click="assignTotalRow(getActualRowNumber(rowIndex))"
                      :title="`Click to assign as ${getTotalCategoryLabel(selectedTotalCategory)} total row`">
                    {{ getActualRowNumber(rowIndex) + 1 }}
                  </td>
                  <td v-for="(cell, colIndex) in row" :key="colIndex" 
                      class="excel-cell"
                      :class="getTotalRowCellClasses(getActualRowNumber(rowIndex))">
                    {{ formatCellValue(cell) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="total-mappings-summary">
          <h4>Current Total Row Assignments:</h4>
          <div class="mappings-grid">
            <div v-for="category in totalCategories" :key="category.key" class="mapping-item">
              <span class="category-label">{{ category.label }}:</span>
              <span class="assigned-rows">{{ getAssignedRows(category.key) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Legacy Document Preview Section (for processed documents) -->
    <div class="document-preview-section" v-if="documentData && headers.length > 0 && currentStep !== 'column_mapping' && currentStep !== 'template_qa_review'">
      <div class="spreadsheet-viewer">
        <table class="excel-table">
          <thead>
            <tr>
              <th v-for="(header, index) in headers" :key="index" 
                  :class="getColumnHighlight(index)"
                  @click="selectColumn(index, header)">
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowIndex) in displayRows" :key="rowIndex">
              <td v-for="(cell, cellIndex) in row" :key="cellIndex"
                  :class="getColumnHighlight(cellIndex)"
                  @click="selectColumn(cellIndex, headers[cellIndex])">
                {{ formatCellValue(cell) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Template QA Review Step -->
    <div v-if="currentStep === 'template_qa_review'" class="template-qa-step">
      <!-- Clean Progress Bar -->
      <div class="step-header-layout">
        <div class="progress-container">
          <div class="progress-steps">
            <div class="progress-step completed">
              <div class="step-circle">✓</div>
              <div class="step-title">Select the Tab</div>
            </div>
            <div class="progress-step completed">
              <div class="step-circle">✓</div>
              <div class="step-title">Select the Extents</div>
            </div>
            <div class="progress-step completed">
              <div class="step-circle">✓</div>
              <div class="step-title">Select the Columns</div>
            </div>
            <div class="progress-step completed">
              <div class="step-circle">✓</div>
              <div class="step-title">Select the Rows</div>
            </div>
            <div class="progress-step active">
              <div class="step-circle">5</div>
              <div class="step-title">Review & Lock</div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Configuration Cards -->
      <div class="qa-config-cards">
        <div class="config-card">
          <div class="card-header">
            <h3>📋 Data Boundaries</h3>
          </div>
          <div class="card-content">
            <div class="config-item">
              <span class="label">Selected Worksheets:</span>
              <span class="value">{{ selectedTabs.length }}</span>
            </div>
            <div class="config-item">
              <span class="label">Header Row:</span>
              <span class="value">Row {{ (selectedHeaderRow || 0) + 1 }}</span>
            </div>
            <div class="config-item">
              <span class="label">Data Rows:</span>
              <span class="value">Will be mapped via DDA terms</span>
            </div>
          </div>
        </div>
        
        <div class="config-card total-mappings-card">
          <div class="card-header">
            <h3>📊 Total Row Mappings</h3>
          </div>
          <div class="card-content">
            <p class="card-description">Total rows mapped for main cost categories:</p>
            <div class="total-mappings-grid">
              <div v-for="category in getTotalMappingSummary()" :key="category.name" class="total-mapping-item">
                <div class="category-info">
                  <span class="category-icon">{{ category.icon }}</span>
                  <span class="category-name">{{ category.name }}</span>
                </div>
                <div class="category-status">
                  <span v-if="category.hasMappings" class="status-icon mapped">✓</span>
                  <span v-else class="status-icon not-mapped">○</span>
                  <span class="status-text">{{ category.hasMappings ? 'Mapped' : 'Not Mapped' }}</span>
                </div>
                <div v-if="category.hasMappings" class="category-details">
                  <small>{{ category.rowCount }} row(s) assigned</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Data Rows Summary -->
      <div class="qa-data-section">
        <div class="section-header">
          <h3>📋 Data Rows Summary</h3>
          <p class="section-subtitle">Review all data rows that will be processed. Total row mappings are configured in the previous step.</p>
        </div>
        
        <div class="data-summary-grid">
          <div class="grid-header">
            <div class="grid-cell header">Row #</div>
            <div class="grid-cell header">Description</div>
            <div class="grid-cell header">Original Budget</div>
            <div class="grid-cell header">Current Budget</div>
            <div class="grid-cell header">Current Draw</div>
            <div class="grid-cell header">Spent to Date</div>
            <div class="grid-cell header">Total Category</div>
          </div>
          
          <div v-if="getDataPreviewRows().length > 0" class="grid-rows">
            <div v-for="(row, index) in getDataPreviewRows()" :key="index" class="grid-row" :class="{ 'total-row': row.totalRowCategory }">
              <div class="grid-cell row-number">{{ row.rowNumber }}</div>
              <div class="grid-cell">{{ row.description || 'No description' }}</div>
              <div class="grid-cell budget">{{ row.originalBudget ? formatCurrency(row.originalBudget) : '-' }}</div>
              <div class="grid-cell budget">{{ row.currentBudget ? formatCurrency(row.currentBudget) : '-' }}</div>
              <div class="grid-cell budget">{{ row.currentDraw ? formatCurrency(row.currentDraw) : '-' }}</div>
              <div class="grid-cell budget">{{ row.spentToDate ? formatCurrency(row.spentToDate) : '-' }}</div>
              <div class="grid-cell category">
                <span v-if="row.totalRowCategory" :class="['category-badge', row.totalRowCategory]">
                  {{ getCategoryLabel(row.totalRowCategory) }}
                </span>
                <span v-else>-</span>
              </div>
            </div>
          </div>
          
          <div v-else class="no-preview">
            <p>Data preview will be available after column mappings are completed.</p>
          </div>
          
          <div v-if="getDataPreviewRows().length > 0" class="show-more">
            <p>Showing all {{ getDataPreviewRows().length }} data rows ready for template processing.</p>
          </div>
        </div>
      </div>
      
      <!-- Quality Checklist -->
      <div class="qa-checklist">
        <h3>🔍 Quality Assurance Checklist</h3>
        <div class="checklist-items">
          <div class="checklist-item completed">
            <span class="check">✅</span>
            <span class="text">Data boundaries defined and validated</span>
          </div>
          <div class="checklist-item completed">
            <span class="check">✅</span>
            <span class="text">Column mappings completed successfully</span>
          </div>
          <div class="checklist-item completed">
            <span class="check">✅</span>
            <span class="text">Total row mappings configured</span>
          </div>
          <div class="checklist-item completed">
            <span class="check">✅</span>
            <span class="text">Template configuration verified</span>
          </div>
        </div>
      </div>
    </div>


    <!-- Action Buttons -->
    <div class="action-buttons">
      <button v-if="currentStep !== firstStepKey" 
              class="btn-secondary modern-btn" 
              @click="previousStep">
        <span class="btn-icon">←</span>
        <span class="btn-text">Previous Step</span>
      </button>
      
      <div class="spacer"></div>
      
      <button v-if="!isLastStep" 
              class="btn-primary modern-btn" 
              :disabled="!canProceed || isProcessing"
              @click="currentStep === 'g703_column_mapping' ? proceedToG703Correction() : nextStep()">
        <span v-if="currentStep === 'g703_column_mapping' && isProcessing" class="btn-content">
          <span class="btn-icon spinning">🔄</span>
          <span class="btn-text">Applying Template Mapping...</span>
        </span>
        <span v-else class="btn-content">
          <span class="btn-text">{{ currentStep === 'g703_column_mapping' ? 'Apply Column Mapping' : 'Next Step' }}</span>
          <span class="btn-icon">→</span>
        </span>
      </button>
      
      <button v-if="isLastStep" 
              class="btn-success modern-btn" 
              :disabled="!canProceed || isLockingTemplate"
              @click="lockTemplate">
        <span v-if="isLockingTemplate" class="btn-content">
          <span class="btn-icon spinning">⏳</span>
          <span class="btn-text">Locking Template... Please wait</span>
        </span>
        <span v-else class="btn-content">
          <span class="btn-icon">🔒</span>
          <span class="btn-text">Lock the Template</span>
        </span>
      </button>
    </div>
    
    <!-- Step 5: Progress Validation Dialog -->
    <ProgressValidationDialog
      :show-dialog="showValidationDialog"
      :validation-result="validationResult"
      :template-id="route.params.templateId"
      @close="showValidationDialog = false"
      @fixed="handleValidationFixed"
      @accepted="handleValidationAccepted"
      @manual-review="handleValidationManualReview"
    />
    
    <!-- DDA Term Edit Modal for QA Step -->
    <div v-if="qaDDAModal.show" class="modal-backdrop" @click="closeQADDATermModal">
      <div class="contractor-modal dda-term-modal" @click.stop>
        <div class="modal-header">
          <h3>Edit DDA Term</h3>
          <button class="close-button" @click="closeQADDATermModal">×</button>
        </div>
        
        <div class="modal-body">
          <div v-if="qaDDAModal.selectedDDATerm && qaDDAModal.selectedDDATerm.method" class="auto-match-info">
            <div class="match-badge" :class="qaDDAModal.selectedDDATerm.method === 'ML' ? 'ml-match' : 'keyword-match'">
              {{ qaDDAModal.selectedDDATerm.method === 'ML' ? '🤖 AI Matched' : '🔍 Keyword Matched' }}
              <span class="confidence">{{ Math.round((qaDDAModal.selectedDDATerm.confidence || 0) * 100) }}%</span>
            </div>
          </div>
          
          <div class="form-section">
            <label class="form-label">Search DDA Terms</label>
            <div class="contractor-search-container">
              <input 
                type="text" 
                v-model="qaDDAModal.searchTerm"
                @input="searchQADDATerms"
                @focus="handleQADDATermInputFocus"
                @blur="handleQADDATermInputBlur"
                placeholder="Type to search DDA terms..."
                class="form-input contractor-search-input"
              />
              
              <div v-if="qaDDAModal.showDropdown && qaDDAModal.searchResults.length > 0" class="contractor-dropdown">
                <div 
                  v-for="ddaTerm in qaDDAModal.searchResults" 
                  :key="ddaTerm.id"
                  @click="selectQADDATerm(ddaTerm)"
                  class="contractor-option"
                  :class="{ selected: qaDDAModal.selectedDDATerm?.id === ddaTerm.id }"
                >
                  <div class="contractor-name">{{ ddaTerm.term }}</div>
                  <div class="contractor-address" v-if="ddaTerm.description">{{ ddaTerm.description }}</div>
                  <div class="contractor-address" v-if="ddaTerm.category">Category: {{ ddaTerm.category }}</div>
                </div>
              </div>
              
              <div v-if="qaDDAModal.showDropdown && qaDDAModal.searchResults.length === 0 && qaDDAModal.searchTerm" class="no-results">
                No matching DDA terms found for "{{ qaDDAModal.searchTerm }}"
              </div>
            </div>
          </div>
          <div v-if="qaDDAModal.selectedDDATerm" class="selected-contractor-section">
            <h4>Selected DDA Term:</h4>
            <div class="selected-contractor-card">
              <div class="contractor-name">{{ qaDDAModal.selectedDDATerm.term }}</div>
              <div class="contractor-address" v-if="qaDDAModal.selectedDDATerm.description">
                {{ qaDDAModal.selectedDDATerm.description }}
              </div>
              <div class="contractor-address" v-if="qaDDAModal.selectedDDATerm.category">
                Category: {{ qaDDAModal.selectedDDATerm.category }}
              </div>
              <button @click="clearQADDATermSelection" class="clear-selection-btn">Clear Selection</button>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeQADDATermModal">Cancel</button>
          <button 
            class="btn-primary" 
            @click="saveQADDATermAssignment"
            :disabled="!qaDDAModal.selectedDDATerm"
          >
            Save DDA Term
          </button>
        </div>
      </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import templateQueueManager from '@/utils/TemplateQueueManager';
import { useErrorStore } from '@/stores/error_store';
import axios from 'axios';
import ProgressValidationDialog from '@/components/helpers/ProgressValidationDialog.vue';

// Accept templateId from router to avoid extraneous prop warnings
defineProps({
  templateId: {
    type: [String, Number],
    required: false
  }
});

// Import API configuration
const apiBase = window.location.hostname === 'localhost' 
  ? 'http://localhost:3000/api/v1/' 
  : '/api/v1/';

const authToken = computed(() => {
  return { headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } };
});

// Route and Router
const route = useRoute();
const router = useRouter();
const errorStore = useErrorStore();

// NEW: Queue Mode Detection
const isQueueMode = computed(() => route.query.return_to === 'upload_queue');
const queueIndex = computed(() => parseInt(route.query.queue_index) || 0);

// Reactive Data
const activeTab = ref('draw_detail');
const currentStep = ref('tab_selection');
const documentData = ref(null);
const templateInfo = ref(null);

// Excel helper functions (accessible to template)
const getExcelHeaders = () => {
  if (documentData.value?.rows && documentData.value.rows.length > 0) {
    const firstRow = documentData.value.rows[0] || [];
    return firstRow.map((header, index) => header || `Column ${index + 1}`);
  }
  if (documentData.value?.raw_excel_data?.rows && documentData.value.raw_excel_data.rows.length > 0) {
    const firstRow = documentData.value.raw_excel_data.rows[0] || [];
    return firstRow.map((header, index) => header || `Column ${index + 1}`);
  }
  return [];
};

const getExcelRows = () => {
  if (documentData.value?.rows) {
    return documentData.value.rows;
  }
  if (documentData.value?.raw_excel_data?.rows) {
    return documentData.value.raw_excel_data.rows;
  }
  return [];
};

const getHeaderAndTotalRowClasses = (rowIndex) => {
  const classes = ['excel-row'];
  return classes.join(' ');
};
const isLoading = ref(true);
const headers = ref([]);
const rows = ref([]);
const selectedColumn = ref(null);
const selectedTerm = ref(null);
const columnMappings = ref([]);
const ddaTerms = ref([]);
const qaPreviewRows = ref([]);

// Step 4: Total Rows Selection
const selectedTotalCategory = ref('project_total');
const totalRowAssignments = ref({});

// DDA Category Totals Detection
const ddaCategoryTotals = ref([
  { name: 'Hard Costs', icon: '🏗️', detected: false, count: 0, categoryId: 197 },
  { name: 'Soft Costs', icon: '📋', detected: false, count: 0, categoryId: 198 },
  { name: 'Financing', icon: '💰', detected: false, count: 0, categoryId: 204 },
  { name: 'Acquisition', icon: '🏢', detected: false, count: 0, categoryId: 203 }
]);

// QA DDA Term Modal State
const qaDDAModal = ref({
  show: false,
  rowIndex: null,
  rowData: null,
  searchTerm: '',
  searchResults: [],
  showDropdown: false,
  selectedDDATerm: null,
  allDDATerms: []
});

// Template type detection
const isContractorTemplate = ref(false);

// Processing state
const isProcessing = ref(false);
const isAutoDetecting = ref(false);
const processingStatus = ref('');
const processingAttempts = ref(0);
const isLockingTemplate = ref(false);
const maxProcessingAttempts = ref(60); // Will be updated to actual page count during processing

// NEW STREAMLINED EXCEL DATA HANDLING
// Step 1: Tab Selection
const availableTabs = ref([]);
const selectedTabs = ref([]);

// Step 2: Boundary Definition
const currentBoundaryTab = ref(null);

const tabBoundaries = ref({});

// Step 3: Description Structure (preserve existing logic)
const useHierarchicalDescriptions = ref(false);

// Step 4: SIMPLIFIED Column Mappings (only 4-5 columns vs current 12+)
const simpleColumnMappings = ref({
  description: [],        // Array of column indices for description hierarchy
  original_budget: null,  // Single column index
  current_budget: null,   // Single column index  
  spent_to_date: null,    // Single column index (optional) - amounts already spent/billed
  remaining_budget: null, // Single column index (optional) - amounts remaining in budget
  funded_at_closing: [], // Array of column indices (optional) - amounts funded at closing (summed)
  current_draw: []        // 🔥 CHANGED: Array of column indices (optional) - amounts will be SUMMED for current period
});
// Track whether we are applying last saved mapping as base
const isUsingLastMapping = ref(false);
const currentMappingField = ref('description');

// Total Row Selection and Mapping
const totalRowSelectionMode = ref(null); // Which category is currently being selected
const totalRowMappings = ref({
  hard_costs: [],
  soft_costs: [],
  financing: [],
  project_total: []
});

// Legacy data kept for compatibility with existing backend
const selectedHeaderRow = ref(null);

const ddaMapping = ref({
  main_category_column: null,
  description_columns: [],
  cost_code_column: null,
  company_column: null,
  original_budget_column: null,
  current_budget_column: null,
  funded_at_closing_column: null,
  current_draw_column: [], // 🔥 CHANGED: Now array to support multiple columns that will be summed
  balance_to_complete_column: null,
  draw_columns: []
});

// Optional column flags
const hasCostCodeColumn = ref(false);
const hasCompanyColumn = ref(false);

// DDA Term Classification (removed manual classification)
const selectedExcelItem = ref(null);
const selectedDDATerm = ref(null);
const termSearchQuery = ref('');
const classificationItems = ref([]);

// Legacy draw columns (kept for compatibility)
const selectedDrawColumns = ref([]);
const drawPeriodLabels = ref({});
const selectedDateRow = ref(null);

// G703 editable data
const editableG703Data = ref([]);

// NEW: PDF Page Selection for Multi-Document Processing
const pdfPagePreviews = ref([]);
const selectedPdfPages = ref([]);

// Image modal for PDF enlargement
const showImageModal = ref(false);
const selectedImagePage = ref(null);

// NEW: Document type tracking for enhanced processing
const documentTypeMapping = {
  'gc_703': 'General Contractor G703',
  'sub_702': 'Subcontractor G702', 
  'gc_702': 'GC G702',
  'lw': 'Lien Waiver'
};

// G703 column mapping - Updated to match actual G703 form structure
const g703ColumnMapping = ref({
  trade_code_column: null,
  description_column: null,
  contractor_names_column: null,
  scheduled_value_column: null,
  owner_previous_approved_column: null,
  owner_approved_completed_column: null,
  adjusted_schedule_values_column: null,
  subcontractor_completed_column: null,
  work_completed_previous_column: null,
  work_completed_this_period_column: null,
  materials_stored_column: null,
  total_completed_column: null,
  percent_complete_column: null,
  balance_to_finish_column: null,
  retainage_percent_column: null,
  retainage_this_period_column: null,
  retainage_total_column: null
});

const g703HeaderRow = ref(null);
const g703RawData = ref([]);
const currentG703MappingField = ref('description_column');

// G703 mapping field labels - Updated to match actual G703 form
const g703MappingLabels = {
  'trade_code_column': 'Trade Code',
  'description_column': 'Description of Work',
  'contractor_names_column': 'Names (Contractor/Sub)',
  'scheduled_value_column': 'Scheduled Value',
  'budget_transfer_column': 'Budget Transfer',
  'owner_previous_approved_column': 'Owner Previous Approved',
  'owner_approved_completed_column': 'Owner Approved Completed',
  'adjusted_schedule_values_column': 'Adjusted Schedule Values',
  'subcontractor_completed_column': 'Subcontractor Completed',
  'work_completed_previous_column': 'Work Completed Previous',
  'work_completed_this_period_column': 'Work Completed This Period',
  'materials_stored_column': 'Materials Presently Stored',
  'total_completed_column': 'Total Completed and Stored',
  'percent_complete_column': 'Percentage Complete',
  'balance_to_finish_column': 'Balance to Finish',
  'retainage_percent_column': 'Retainage %',
  'retainage_this_period_column': 'Retainage This Period',
  'retainage_total_column': 'Retainage Total'
};

// Standard G703 column mapping template (based on typical AIA G703 form layout)
const defaultG703ColumnMapping = {
  trade_code_column: 0,        // Column A - Trade Code
  description_column: 1,       // Column B - Description of Work  
  contractor_names_column: 2,  // Column C - Names (Contractor/Sub)
  scheduled_value_column: 3,   // Column D - Scheduled Value
  budget_transfer_column: 4,   // Column E - Budget Transfer
  owner_previous_approved_column: 5,      // Column F - Owner Previous Approved
  owner_approved_completed_column: 6,     // Column G - Owner Approved Completed
  adjusted_schedule_values_column: 7,     // Column H - Adjusted Schedule Values
  subcontractor_completed_column: 8,      // Column I - Subcontractor Completed
  work_completed_previous_column: 9,      // Column J - Work Completed Previous
  work_completed_this_period_column: 10,  // Column K - Work Completed This Period
  materials_stored_column: 11,            // Column L - Materials Presently Stored
  total_completed_column: 12,             // Column M - Total Completed and Stored
  percent_complete_column: 13,            // Column N - Percentage Complete
  balance_to_finish_column: 14,           // Column O - Balance to Finish
  retainage_percent_column: 15,           // Column P - Retainage %
  retainage_this_period_column: 16,       // Column Q - Retainage This Period
  retainage_total_column: 17              // Column R - Retainage Total
};

// NEW: Computed properties for PDF page selection
const allSelectedPagesHaveTypes = computed(() => {
  return selectedPdfPages.value.every(page => page.documentType && page.documentType !== '');
});

// NEW: Helper function for document type labels
function getDocTypeLabel(docType) {
  return documentTypeMapping[docType] || docType;
}

// Step 5: Progressive Budget Validation
const showValidationDialog = ref(false);
const validationResult = ref(null);
const validationInProgress = ref(false);

// Step 5: Validation Dialog Handlers
function handleValidationFixed() {
  console.log('✅ STEP 5: User chose to fix progression issues');
  showValidationDialog.value = false;
  completeTemplateSetup();
}

function handleValidationAccepted() {
  console.log('📊 STEP 5: User accepted Excel values as-is');
  showValidationDialog.value = false;
  completeTemplateSetup();
}

function handleValidationManualReview() {
  console.log('✏️ STEP 5: User chose manual review');
  showValidationDialog.value = false;
  // For now, just complete setup - later could redirect to manual edit view
  errorStore.setError('Manual Review', 'Please review and manually adjust budget values in the project view if needed.', 'info', false);
  completeTemplateSetup();
}

// NEW: PDF Page Selection Functions
function togglePageSelection(page) {
  page.selected = !page.selected;
  if (page.selected) {
    selectedPdfPages.value.push(page);
  } else {
    selectedPdfPages.value = selectedPdfPages.value.filter(p => p.pageNumber !== page.pageNumber);
    page.documentType = ''; // Clear document type when deselected
  }
}

function updatePageDocumentType(page) {
  // Update the page in the selected array
  const selectedPage = selectedPdfPages.value.find(p => p.pageNumber === page.pageNumber);
  if (selectedPage) {
    selectedPage.documentType = page.documentType;
  }
}

function toggleDocumentType(page, documentType) {
  console.log(`🔧 CHECKBOX DEBUG: toggleDocumentType called for page ${page.pageNumber}, type: ${documentType}`);
  // For checkboxes, only one can be selected at a time per page
  if (page.documentType === documentType) {
    // Unchecking - clear the document type and deselect page
    page.documentType = '';
    page.selected = false;
    // Remove from selected pages array
    selectedPdfPages.value = selectedPdfPages.value.filter(p => p.pageNumber !== page.pageNumber);
  } else {
    // Checking - set this document type and auto-select page
    page.documentType = documentType;
    page.selected = true;
    
    // Add to selected pages array if not already there
    const existingPage = selectedPdfPages.value.find(p => p.pageNumber === page.pageNumber);
    if (existingPage) {
      existingPage.documentType = page.documentType;
    } else {
      selectedPdfPages.value.push({ ...page });
    }
    
    // Step 1: AI trigger for ALL document types (eventually we want on all)
    console.log(`🔧 AI CHECK: documentType=${documentType}, page=${page.pageNumber}`);
    if (['gc_703', 'gc_702', 'sub_702', 'lw'].includes(documentType)) {
      console.log(`🤖 ${documentType.toUpperCase()} selected for page ${page.pageNumber} - AI template analysis triggering`);
      // Step 2: Call AI analysis function for all types
      try {
        analyzeDocumentTemplate(page);
        console.log(`🔧 AI CALL: analyzeDocumentTemplate called successfully`);
      } catch (error) {
        console.error(`❌ AI CALL ERROR:`, error);
      }
    } else {
      console.log(`🔧 AI SKIP: ${documentType} not in trigger list`);
    }
  }
}

// AI Template Analysis for ALL Document Types
async function analyzeDocumentTemplate(page) {
  console.log(`🤖 Starting AI template analysis for ${page.documentType?.toUpperCase()} page ${page.pageNumber}`);
  console.log(`Page image URL: ${page.imageUrl}`);
  
  try {
    // Test base64 conversion
    console.log(`📷 Fetching image for base64 conversion...`);
    const response = await fetch(page.imageUrl);
    const blob = await response.blob();
    
    console.log(`📷 Image blob size: ${blob.size} bytes`);
    
    const base64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(blob);
    });
    
    console.log(`📷 Base64 conversion successful: ${base64.length} chars`);
    
    // Step 3c: Safe Vision API call with detailed debugging
    console.log(`🔧 DEBUG: About to start Vision API call...`);
    console.log(`🔧 DEBUG: Base64 length: ${base64.length}`);
    console.log(`🔧 DEBUG: Starting Vision API process...`);
    
    console.log(`🤖 Calling OpenAI Vision API (safe version)...`);
    
    // CRITICAL FIX: Dynamic prompt based on selected document type
    let promptText = '';
    let expectedTemplate = '';
    
    switch (page.documentType) {
      case 'sub_702':
        expectedTemplate = 'subcontractor_g702';
        promptText = 'You are analyzing a Subcontractor G702 Payment Application form. Look for these key elements: (1) FROM: [Subcontractor Name] TO: [General Contractor], (2) Contract amount fields (typically smaller amounts $50K-$500K), (3) Period ending date, (4) Application number, (5) Work completed values, (6) Change order sections. Return ONLY this JSON format: {"template_type": "subcontractor_g702", "confidence": 0.95, "reasoning": "detected subcontractor payment application with typical layout"}';
        break;
      case 'gc_702':
        expectedTemplate = 'gc_g702';
        promptText = 'You are analyzing a General Contractor G702 Payment Application form. Look for these key elements: (1) FROM: [General Contractor] TO: [Owner/Client], (2) Large contract amounts (typically $500K+), (3) Period ending date, (4) Application number, (5) Work completed values, (6) Change order sections. Return ONLY this JSON format: {"template_type": "gc_g702", "confidence": 0.95, "reasoning": "detected general contractor payment application with typical layout"}';
        break;
      case 'gc_703':
      case 'g703':
      default:
        expectedTemplate = 'standard_g703';
        promptText = 'You are analyzing a G703 Continuation Sheet construction form. Look at the column layout and structure. Common layouts: (1) Standard AIA G703 with Item#, Description, Scheduled Value, Previous Work, This Period, Materials, Total Complete, %Complete, Balance; (2) Custom layouts with different column arrangements. Return ONLY this JSON format: {"template_type": "standard_g703", "confidence": 0.95, "reasoning": "detected standard AIA column layout"}';
        break;
    }
    
    console.log(`🔧 PROMPT: Using ${page.documentType} prompt for ${expectedTemplate} template`);
    
    const visionPayload = {
      model: 'gpt-4o',
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: promptText
            },
            {
              type: 'image_url',
              image_url: {
                url: 'data:image/png;base64,' + base64,
                detail: 'high'
              }
            }
          ]
        }
      ],
      temperature: 0.1,
      max_tokens: 200
    };
    
    const visionResponse = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer 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',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(visionPayload)
    });
    
    if (!visionResponse.ok) {
      console.error(`❌ Vision API error: ${visionResponse.status} ${visionResponse.statusText}`);
      const errorText = await visionResponse.text();
      console.error(`Error details: ${errorText}`);
      return;
    }
    
    const visionData = await visionResponse.json();
    const visionResult = visionData.choices[0]?.message?.content?.trim();
    console.log(`🔧 DEBUG: Got Vision API response, processing...`);
    console.log(`🤖 Vision API raw result: ${visionResult}`);
    
    // Try to parse the JSON response
    try {
      const analysis = JSON.parse(visionResult);
      console.log(`🎯 TEMPLATE DETECTED: ${analysis.template_type}`);
      console.log(`📊 CONFIDENCE: ${Math.round(analysis.confidence * 100)}%`);
      console.log(`💡 REASONING: ${analysis.reasoning}`);
      
      // Store the analysis result on the page object for future use
      page.aiAnalysis = analysis;
      
    } catch (parseError) {
      console.log(`⚠️ Could not parse JSON response, but API call successful`);
      console.log(`📝 Raw response: ${visionResult.substring(0, 200)}...`);
    }
    
    console.log(`🔧 DEBUG: About to log completion message...`);
    console.log(`✅ AI Vision Analysis Complete for page ${page.pageNumber}: Vision API call successful`);
    
  } catch (error) {
    console.error(`❌ FULL ERROR for page ${page.pageNumber}:`, error);
    console.error(`❌ ERROR MESSAGE:`, error.message);
    console.error(`❌ ERROR STACK:`, error.stack);
  }
}

function selectAllPages() {
  pdfPagePreviews.value.forEach(page => {
    if (!page.selected) {
      page.selected = true;
      selectedPdfPages.value.push(page);
    }
  });
}

function clearAllSelections() {
  pdfPagePreviews.value.forEach(page => {
    page.selected = false;
    page.documentType = '';
  });
  selectedPdfPages.value = [];
}

function removePageSelection(pageToRemove) {
  const page = pdfPagePreviews.value.find(p => p.pageNumber === pageToRemove.pageNumber);
  if (page) {
    page.selected = false;
    page.documentType = '';
  }
  selectedPdfPages.value = selectedPdfPages.value.filter(p => p.pageNumber !== pageToRemove.pageNumber);
}

function acceptAiSuggestion(page) {
  page.documentType = page.aiSuggestion;
  updatePageDocumentType(page);
}

// Image modal functions
function openImageModal(page) {
  selectedImagePage.value = page;
  showImageModal.value = true;
  // Prevent body scroll when modal is open
  document.body.style.overflow = 'hidden';
}

function closeImageModal() {
  showImageModal.value = false;
  selectedImagePage.value = null;
  // Restore body scroll
  document.body.style.overflow = '';
}

// Handle keyboard events for modal
function handleKeydown(event) {
  if (event.key === 'Escape' && showImageModal.value) {
    closeImageModal();
  }
  if (event.key === 'Escape' && qaDDAModal.value.show) {
    closeQADDATermModal();
  }
}

// Helper functions for better DDA term matching and sorting
function prioritizeStandardTerms(terms) {
  if (!terms || !Array.isArray(terms)) return [];
  
  // Standard terms have no primary_dda_term_id, extended terms do
  const standardTerms = terms.filter(term => !term.primary_dda_term_id);
  const extendedTerms = terms.filter(term => term.primary_dda_term_id);
  
  // Return standard terms first, then extended terms
  return [...standardTerms, ...extendedTerms];
}

function getCategoryNameById(categoryId) {
  const categories = {
    197: 'Hard Costs',
    198: 'Soft Costs', 
    203: 'Acquisition',
    204: 'Financing',
    210: 'Contingencies',
    209: 'Other'
  };
  return categories[Number(categoryId)] || null;
}

function calculateTermMatchScore(termName, searchTerm) {
  if (!termName || !searchTerm) return 0;
  
  const term = termName.toLowerCase().trim();
  const search = searchTerm.toLowerCase().trim();
  
  // Exact match gets highest score
  if (term === search) return 100;
  
  // Term starts with search gets very high score
  if (term.startsWith(search)) return 90;
  
  // Search starts with term gets high score  
  if (search.startsWith(term)) return 85;
  
  // Term contains search gets good score
  if (term.includes(search)) return 70;
  
  // Search contains term gets decent score
  if (search.includes(term)) return 60;
  
  // Word-by-word matching
  const termWords = term.split(/\s+/).filter(w => w.length > 2);
  const searchWords = search.split(/\s+/).filter(w => w.length > 2);
  
  if (termWords.length === 0 || searchWords.length === 0) return 0;
  
  const commonWords = termWords.filter(w => searchWords.some(sw => 
    w.includes(sw) || sw.includes(w) || w.startsWith(sw) || sw.startsWith(w)
  ));
  
  if (commonWords.length > 0) {
    return (commonWords.length / Math.max(termWords.length, searchWords.length)) * 50;
  }
  
  return 0;
}

// Enhanced OpenAI-powered semantic matching for QA step (consistent with ProjectSummary)
async function findBestDDATermWithOpenAI(description, allDDATerms) {
  if (!description || !allDDATerms || allDDATerms.length === 0) return null;
  
  try {
    // Get top standard terms first for better matching, enhanced with category context
    const standardTerms = prioritizeStandardTerms(allDDATerms).slice(0, 50);
    const termsList = standardTerms.map(term => {
      let termEntry = term.term;
      
      // Add category context for better matching
      const categoryName = getCategoryNameById(term.dda_term_category_id);
      if (categoryName) {
        termEntry += ` [${categoryName}]`;
      }
      
      // Add parent term context for sub-terms
      if (term.primary_dda_term_id) {
        const parentTerm = allDDATerms.find(t => t.id === term.primary_dda_term_id);
        if (parentTerm) {
          termEntry += ` (sub-term of: ${parentTerm.term})`;
        }
      }
      
      // Add any available description
      if (term.description) {
        termEntry += ` (${term.description})`;
      }
      
      return termEntry;
    }).join('\n');
    
    const prompt = `You are an expert at matching construction project line items to standardized DDA (Debt Service Coverage Analysis) terms.

Description to match: "${description}"

AVAILABLE TERMS (Standard terms listed first - ALWAYS prefer these):
${termsList}

CRITICAL MATCHING RULES:
1. EXACT MATCHES: If description exactly matches a term, use it (confidence: 0.95+)
2. PARTIAL MATCHES: If description contains key words from a term, match it (confidence: 0.70-0.85)
3. SEMANTIC MATCHES: If description means the same as a term, match it (confidence: 0.65-0.80)
4. STANDARD TERMS FIRST: Always prefer standard terms over any extended/category terms
5. SPECIFIC OVER GENERAL: Never match specific trades/professions to broad categories
6. CONSTRUCTION ACRONYMS: Match common construction abbreviations:
   - "MEP" → "Mechanical, Electrical & Plumbing" [Hard Costs]
   - "AOR" → "Architect of Record" [Soft Costs]
   - "SOE" → "Statement of Eligibility" or "Schedule of Equipment" [varies by context]
   - "TPP" → "Third Party Professional" [Soft Costs]
7. TRADE TERMS: Match construction trades to their specific terms:
   - "architect" → "architect" (NOT "total soft costs")
   - "interior design" → "interior design" (NOT "total soft costs") 
   - "electrical" → "electrical" (NOT "total construction")
   - "plumbing" → "plumbing" (NOT "total construction")
   - "expediting" → "expediting" [Soft Costs]
   - "lighting" → "electrical and low voltage" [Hard Costs]
   - "facade" → "facade" or "exterior wall systems" [Hard Costs]
   - "elevator" → "elevator" [Hard Costs]
   - "landscape" → "landscaping" [Hard Costs]
8. CONFIDENCE: Only return matches with confidence > 0.65

STRICT FORMAT - Return ONLY this JSON:
{
  "term": "exact term name from list",
  "confidence": 0.85,
  "reasoning": "why this specific match"
}

If no confident match: {"term": null, "confidence": 0.0, "reasoning": "no suitable match"}`;

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer 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`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.1,
        max_tokens: 120
      })
    });

    if (!response.ok) {
      console.error('OpenAI API error:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    const aiResponse = data.choices[0]?.message?.content?.trim();
    
    if (!aiResponse) {
      console.error('No response from OpenAI');
      return null;
    }

    // Parse JSON response
    let cleanResponse = aiResponse.trim();
    if (cleanResponse.startsWith('```json')) {
      cleanResponse = cleanResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (cleanResponse.startsWith('```')) {
      cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }
    
    const result = JSON.parse(cleanResponse);
    
    if (!result.term || result.confidence < 0.65) {
      console.log(`QA OpenAI match below threshold: ${result.term} (${result.confidence})`);
      return null;
    }

    // Find the matched term in our list
    const matchedTerm = allDDATerms.find(term => 
      term.term.toLowerCase().trim() === result.term.toLowerCase().trim()
    );
    
    if (matchedTerm) {
      return {
        term: matchedTerm,
        confidence: result.confidence,
        reasoning: result.reasoning
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error in QA OpenAI matching:', error);
    return null;
  }
}

// QA DDA Term Modal Functions
function openQADDATermModal(index, row) {
  console.log('🔥 QA DDA Modal - Opening for:', index, row);
  qaDDAModal.value.show = true;
  qaDDAModal.value.rowIndex = index;
  qaDDAModal.value.rowData = row;
  qaDDAModal.value.searchTerm = row.ddaTerm || '';
  qaDDAModal.value.searchResults = [];
  qaDDAModal.value.selectedDDATerm = row.ddaTermId ? 
    (ddaTerms.value.find(t => t.id == row.ddaTermId) || null) : null;
  qaDDAModal.value.showDropdown = false;
  
  // Load DDA terms if not already loaded
  if (ddaTerms.value.length === 0) {
    loadDDATerms().then(() => {
      qaDDAModal.value.allDDATerms = ddaTerms.value;
      if (qaDDAModal.value.searchTerm) {
        searchQADDATerms();
      }
    });
  } else {
    qaDDAModal.value.allDDATerms = ddaTerms.value;
    if (qaDDAModal.value.searchTerm) {
      searchQADDATerms();
    }
  }
}

function closeQADDATermModal() {
  qaDDAModal.value.show = false;
  qaDDAModal.value.rowIndex = null;
  qaDDAModal.value.rowData = null;
  qaDDAModal.value.searchTerm = '';
  qaDDAModal.value.searchResults = [];
  qaDDAModal.value.selectedDDATerm = null;
  qaDDAModal.value.showDropdown = false;
}

async function searchQADDATerms() {
  const query = qaDDAModal.value.searchTerm;
  const term = query ? query.trim() : '';
  
  if (!term || term.length < 2) {
    // Show top standard terms first when no search
    const standardTermsFirst = qaDDAModal.value.allDDATerms ? 
      prioritizeStandardTerms(qaDDAModal.value.allDDATerms).slice(0, 50) : [];
    qaDDAModal.value.searchResults = standardTermsFirst;
    qaDDAModal.value.showDropdown = true;
    return;
  }
  
  try {
    console.log(`🔍 QA DDA MODAL SEARCH: Searching for "${term}"`);
    // Use the same API endpoint as ProjectSummary with higher limit
    const backendBaseUrl = apiBase.replace('/api/v1/', '');
    const response = await axios.get(`${backendBaseUrl}/api/v1/dda_terms/search`, {
      params: { q: term, limit: 100 }  // Get more results to sort properly
    });
    
    // Sort results by relevance - standard terms first, then by match quality
    const sortedResults = prioritizeStandardTerms(response.data || [])
      .map(termItem => ({
        ...termItem,
        matchScore: calculateTermMatchScore(termItem.term, query)
      }))
      .sort((a, b) => {
        // First sort by standard vs extended terms
        const aIsStandard = !a.primary_dda_term_id;
        const bIsStandard = !b.primary_dda_term_id;
        if (aIsStandard !== bIsStandard) {
          return bIsStandard ? 1 : -1;  // Standard terms first
        }
        // Then by match score
        return b.matchScore - a.matchScore;
      })
      .slice(0, 50);
    
    qaDDAModal.value.searchResults = sortedResults;
    qaDDAModal.value.showDropdown = true;
    console.log(`✅ QA DDA MODAL SEARCH: Found ${qaDDAModal.value.searchResults.length} results for "${term}" (${sortedResults.filter(r => !r.primary_dda_term_id).length} standard terms)`);
  } catch (error) {
    console.error('❌ QA DDA MODAL SEARCH ERROR:', error);
    // Fallback to local filtering with better sorting
    if (qaDDAModal.value.allDDATerms) {
      const filtered = qaDDAModal.value.allDDATerms
        .filter(ddaTerm => {
          const termLower = ddaTerm.term.toLowerCase();
          const descriptionLower = (ddaTerm.description || '').toLowerCase();
          return termLower.includes(term.toLowerCase()) || descriptionLower.includes(term.toLowerCase());
        })
        .map(termItem => ({
          ...termItem,
          matchScore: calculateTermMatchScore(termItem.term, query)
        }))
        .sort((a, b) => {
          const aIsStandard = !a.primary_dda_term_id;
          const bIsStandard = !b.primary_dda_term_id;
          if (aIsStandard !== bIsStandard) {
            return bIsStandard ? 1 : -1;
          }
          return b.matchScore - a.matchScore;
        })
        .slice(0, 50);
      
      qaDDAModal.value.searchResults = filtered;
      qaDDAModal.value.showDropdown = true;
    }
  }
}

function selectQADDATerm(term) {
  qaDDAModal.value.selectedDDATerm = term;
  qaDDAModal.value.searchTerm = term.term;
  qaDDAModal.value.showDropdown = false;
}

function clearQADDATermSelection() {
  qaDDAModal.value.selectedDDATerm = null;
  qaDDAModal.value.searchTerm = '';
  searchQADDATerms(); // Trigger search to show standard terms
}

function handleQADDATermInputFocus() {
  // Always show dropdown on focus and trigger search
  if (!qaDDAModal.value.searchResults || qaDDAModal.value.searchResults.length === 0) {
    searchQADDATerms();
  } else {
    qaDDAModal.value.showDropdown = true;
  }
}

function handleQADDATermInputBlur() {
  setTimeout(() => {
    qaDDAModal.value.showDropdown = false;
  }, 200);
}

function saveQADDATermAssignment() {
  const idx = qaDDAModal.value.rowIndex;
  const sel = qaDDAModal.value.selectedDDATerm;
  if (idx == null || !sel) return;
  
  const rows = qaPreviewRows.value.length > 0 ? qaPreviewRows.value : getDataPreviewRows();
  const target = rows[idx];
  if (!target) return;
  
  // Ensure qaPreviewRows is initialized with editable copies
  if (qaPreviewRows.value.length === 0) {
    qaPreviewRows.value = getDataPreviewRows().map(r => ({ ...r }));
  }
  
  // 🔥 FIXED: Use consistent field names for both camelCase and snake_case
  qaPreviewRows.value[idx].ddaTerm = sel.term;
  qaPreviewRows.value[idx].ddaTermId = sel.id;        // camelCase for frontend
  qaPreviewRows.value[idx].dda_term_id = sel.id;      // snake_case for backend compatibility
  qaPreviewRows.value[idx].dda_term_name = sel.term;  // snake_case for backend
  qaPreviewRows.value[idx].confidence = 100;
  
  console.log(`💾 QA Selection Saved: "${target.description}" → "${sel.term}" (ID: ${sel.id})`);
  
  // Update DDA category totals after manual assignment
  updateDDACategoryTotals();
  
  closeQADDATermModal();
}

async function convertPdfToImages() {
  try {
    isLoading.value = true;
    processingStatus.value = 'Converting PDF pages to images for selection...';
    
    // Call backend to convert PDF to page images
    // 🔥 CRITICAL: Set 30-minute timeout for large PDFs (300MB, 500+ pages)
    const config = {
      ...authToken.value,
      timeout: 1800000 // 30 minutes = 1800000 ms
    };
    const response = await axios.post(`${apiBase}draw_templates/${route.params.templateId}/convert_pdf_to_images`, {}, config);
    
    if (response.data && response.data.success) {
      // Store the page previews for selection
      // Fix image URLs - page.image_url already contains the path, just prepend base if needed
      const serverBase = window.location.hostname === 'localhost' 
        ? 'http://localhost:3000' 
        : '';
      
      pdfPagePreviews.value = response.data.pages.map(page => ({
        pageNumber: page.page_number,
        imageUrl: `${serverBase}${page.image_url}`,
        selected: false,
        documentType: '',
        aiSuggestion: page.ai_suggestion || null // AI can suggest document type
      }));
      
      console.log(`✅ PDF converted to ${pdfPagePreviews.value.length} page images`);
      console.log('📸 Image URLs:', pdfPagePreviews.value.map(p => ({ page: p.pageNumber, url: p.imageUrl })));
      
      // Auto-select first few pages if AI suggests they're G703s
      pdfPagePreviews.value.forEach(page => {
        if (page.aiSuggestion && ['gc_703', 'sub_702', 'gc_702'].includes(page.aiSuggestion)) {
          page.selected = true;
          page.documentType = page.aiSuggestion;
          selectedPdfPages.value.push(page);
        }
      });
      
      if (selectedPdfPages.value.length > 0) {
        console.log(`🤖 AI auto-selected ${selectedPdfPages.value.length} pages based on content analysis`);
      }
      
    } else {
      throw new Error('Failed to convert PDF to images');
    }
  } catch (error) {
    console.error('Error converting PDF to images:', error);
    errorStore.setError('PDF Conversion Error', 'Failed to convert PDF to page images', 'error', false);
    // Fallback to regular G703 processing
    currentStep.value = 'g703_table_setup';
  } finally {
    isLoading.value = false;
  }
}

async function processPdfPages() {
  if (selectedPdfPages.value.length === 0 || !allSelectedPagesHaveTypes.value) {
    return;
  }
  
  // Prevent duplicate submissions
  if (isProcessing.value) {
    console.log('⚠️ Processing already in progress, ignoring duplicate submission');
    return;
  }
  
  isProcessing.value = true;
  
  // Initialize page-specific progress tracking with accurate counts
  const totalPages = selectedPdfPages.value.length;
  processingStatus.value = `Starting to process ${totalPages} selected pages with AI-powered OCR...`;
  
  // Update progress variables for proper progress bar display
  processingAttempts.value = 0;
  maxProcessingAttempts.value = totalPages;
  
  // IMMEDIATE AUTO-SCROLL to top of page so user sees progress
  window.scrollTo({ top: 0, behavior: 'smooth' });
  
  try {
    // Show indeterminate progress since API returns immediately and processing happens in background
    processingStatus.value = `Processing ${totalPages} selected pages in background with AI-powered OCR...`;
    
    // Quick check for AI analyses, but don't hang the UI
    console.log('🤖 Quick check for AI analyses before submitting...');
    const pagesWithAI = selectedPdfPages.value.filter(page => ['gc_703', 'gc_702', 'sub_702', 'lw'].includes(page.documentType));
    
    if (pagesWithAI.length > 0) {
      // Wait maximum 3 seconds for AI analyses to complete
      for (let i = 0; i < 6; i++) {
        const completedAI = pagesWithAI.filter(page => {
          const originalPage = pdfPagePreviews.value.find(p => p.pageNumber === page.pageNumber);
          return (originalPage?.aiAnalysis !== undefined && originalPage?.aiAnalysis !== null);
        });
        
        console.log(`🔄 AI Progress: ${completedAI.length}/${pagesWithAI.length} analyses complete`);
        
        if (completedAI.length === pagesWithAI.length) {
          console.log('✅ All AI analyses complete! Proceeding with submission...');
          break;
        }
        
        if (i < 5) {
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
        }
      }
    }
    
    // Send selected pages to backend for processing using standalone PDF controller
    const response = await axios.post(`${apiBase}pdf_multi_doc/${route.params.templateId}/process_pdf_pages`, {
      selected_pages: selectedPdfPages.value.map(page => {
        const originalPage = pdfPagePreviews.value.find(p => p.pageNumber === page.pageNumber);
        console.log(`🔧 PAYLOAD DEBUG: Page ${page.pageNumber}`);
        console.log(`🔧 Original page found:`, originalPage ? 'YES' : 'NO');
        console.log(`🔧 Original aiAnalysis:`, originalPage?.aiAnalysis);
        console.log(`🔧 Selected page aiAnalysis:`, page.aiAnalysis);
        
        let aiAnalysis = originalPage?.aiAnalysis || page.aiAnalysis || null;
        
        // Convert Vue proxy to plain object for JSON serialization
        if (aiAnalysis && typeof aiAnalysis === 'object') {
          aiAnalysis = JSON.parse(JSON.stringify(aiAnalysis));
          console.log(`🔧 Converted proxy to plain object:`, aiAnalysis);
        }
        
        console.log(`🔧 Final aiAnalysis being sent:`, aiAnalysis);
        
        return {
          page_number: page.pageNumber,
          document_type: page.documentType,
          image_url: page.imageUrl,
          ai_analysis: aiAnalysis // Include AI template detection results
        };
      })
    }, authToken.value);
    
    if (response.data && response.data.success) {
      // Show completion status without fake progress
      processingStatus.value = `✅ Successfully processed all ${totalPages} pages!`;
      // PDF Multi-Document processing complete - show success and redirect
      console.log('✅ PDF Multi-Document processing completed:', response.data);
      
      // Store the processing results
      documentData.value = response.data;
      
      // Show success message and close the template setup
      errorStore.setError('Processing Complete', 
        `Successfully processed ${response.data.total_records} records from ${selectedPdfPages.value.length} pages. PDF multi-document processing is complete!`, 
        'success', false);
      
      // Redirect back to project view or close modal
      setTimeout(() => {
        const projectId = templateInfo.value?.project_id || route.params.projectId;
        console.log('🔍 PDF Processing redirect - project ID:', projectId);
        router.push(`/portfolio/project-summary/${projectId}`);
      }, 2000);
      
    } else {
      throw new Error('Failed to process PDF pages');
    }
  } catch (error) {
    console.error('Error processing PDF pages:', error);
    errorStore.setError('PDF Processing Error', 'Failed to process selected PDF pages', 'error', false);
  } finally {
    isProcessing.value = false;
  }
}

// Step Configuration - Guided DDA workflow for DRAW templates
// PROPER 5-STEP WORKFLOW - with separate total rows step
const rawExcelSteps = [
  {
    key: 'tab_selection',
    title: 'Step 1: Select Worksheets',
    description: 'Choose which Excel worksheets contain your budget data.'
  },
  {
    key: 'boundary_definition',
    title: 'Step 2: Define Data Boundaries',
    description: 'Set where your actual data is located in each worksheet.'
  },
  {
    key: 'column_mapping',
    title: 'Step 3: Map Columns',
    description: 'Map your columns to the required DDA fields using drag and drop.'
  },
  {
    key: 'total_rows_selection',
    title: 'Step 4: Select Total Rows (Optional)',
    description: 'Optionally identify which rows contain category totals and project totals.'
  },
  {
    key: 'template_qa_review',
    title: 'Step 5: Template QA Review',
    description: 'Review template configuration and DDA term mappings before locking the template.'
  }
];

// SAME PROPER 5-STEP WORKFLOW for contractor templates
const contractorExcelSteps = [
  {
    key: 'tab_selection',
    title: 'Step 1: Select Worksheets',
    description: 'Choose which Excel worksheets contain your budget data.'
  },
  {
    key: 'boundary_definition',
    title: 'Step 2: Define Data Boundaries',
    description: 'Set where your actual data is located in each worksheet.'
  },
  {
    key: 'column_mapping',
    title: 'Step 3: Map Columns',
    description: 'Map your columns to the required DDA fields using drag and drop.'
  },
  {
    key: 'total_rows_selection',
    title: 'Step 4: Select Total Rows (Optional)',
    description: 'Optionally identify which rows contain category totals and project totals.'
  },
  {
    key: 'template_qa_review',
    title: 'Step 5: Template QA Review',
    description: 'Review template configuration and DDA term mappings before locking the template.'
  }
];

const legacySteps = [
  {
    key: 'original_budget',
    title: 'Anchor Columns: Original Budget',
    description: 'Here is what we extracted from the uploaded document. Please select the column that correctly represents the "Original" Budget for the project.'
  },
  {
    key: 'current_budget', 
    title: 'Anchor Columns: Current Budget',
    description: 'Here is what we extracted from the uploaded document. Please select the column that correctly represents the "Current" or "Revised" Budget for the project.'
  },
  {
    key: 'total_spent',
    title: 'Anchor Columns: Total Spent to Date',
    description: 'Here is what we extracted from the uploaded document. Please select the column that correctly represents the "Total Spent to Date" values for the project.'
  },
  {
    key: 'classification',
    title: 'Budget Item Classification',
    description: 'Did we guess right? If not, recategorize the terms so we can correctly correlate this project with all your other projects!'
  }
];

// G703 Steps
const g703Steps = [
  {
    key: 'g703_table_setup',
    title: 'G703 Table Structure Setup',
    description: 'Define the table structure by identifying header rows and column mappings for the G703 form.'
  },
  {
    key: 'g703_column_mapping',
    title: 'G703 Column Mapping',
    description: 'Map each column to the correct G703 field (Description, Budget, Work Completed, etc.).'
  },
  {
    key: 'g703_correction',
    title: 'G703 Data Correction',
    description: 'Review and correct the extracted G703 data. Edit any incorrect descriptions or amounts.'
  },
  {
    key: 'g703_review',
    title: 'G703 Data Review',
    description: 'Review the corrected G703 line items before mapping to DDA terms.'
  },
  {
    key: 'g703_dda_mapping',
    title: 'DDA Term Classification',
    description: 'Assign each G703 line item to the appropriate DDA term for project tracking.'
  }
];

// Computed Properties
const steps = computed(() => {
  // PDF Multi-Document templates don't need step workflow - they have their own page selection UI
  const isPdfMultiDoc = templateInfo.value?.template_type === 'pdf_multi_doc';
  const isPdfPageSelection = currentStep.value === 'pdf_page_selection';
  
  if (isPdfMultiDoc || isPdfPageSelection) {
    return []; // No steps needed for PDF multi-document workflow or page selection
  }
  
  if (documentData.value?.raw_excel_data) {
    return isContractorTemplate.value ? contractorExcelSteps : rawExcelSteps;
  } else if (documentData.value?.raw_g703_data) {
    return g703Steps;
  } else {
    return legacySteps;
  }
});

const firstStepKey = computed(() => {
  return steps.value[0]?.key || 'header_selection';
});

const currentStepIndex = computed(() => {
  return steps.value.findIndex(step => step.key === currentStep.value);
});

const currentStepTitle = computed(() => {
  return steps.value[currentStepIndex.value]?.title || '';
});

const currentStepDescription = computed(() => {
  return steps.value[currentStepIndex.value]?.description || '';
});

const isLastStep = computed(() => {
  const lastStep = steps.value[steps.value.length - 1];
  return currentStep.value === lastStep?.key;
});

const headerRow = computed(() => {
  if (selectedHeaderRow.value !== null && documentData.value?.rows) {
    // Use combined headers if available, otherwise fall back to raw row
    return documentData.value.combinedHeaders || documentData.value.rows[selectedHeaderRow.value] || [];
  }
  return [];
});

const budgetPreviewRows = computed(() => {
  if (!documentData.value?.rows || selectedHeaderRow.value === null) return [];
  
  return documentData.value.rows
    .slice(selectedHeaderRow.value + 1)
    .filter(row => {
      // Only show rows that have meaningful budget data
      const hasDescription = getBudgetMappedValue(row, 'main_category_column') || 
                           getBudgetMappedValue(row, 'sub_item_column');
      const hasBudget = getBudgetMappedValue(row, 'budget_column');
      return hasDescription && hasBudget;
    });
});

const ddaPreviewRows = computed(() => {
  if (!documentData.value?.rows || selectedHeaderRow.value === null) return [];
  
  return documentData.value.rows
    .slice(selectedHeaderRow.value + 1)
    .filter(row => {
      // Only show rows that have meaningful DDA data - check all description columns
      const hasDescription = ddaMapping.value.description_columns.some(colIndex => {
        const desc = row[colIndex]
        return desc && desc.toString().trim() !== ''
      })
      // Budget is fluid - include all items with descriptions even without budget values
      return hasDescription;
    });
});

const filteredDDATerms = computed(() => {
  if (!termSearchQuery.value) return ddaTerms.value;
  
  const query = termSearchQuery.value.toLowerCase();
  return ddaTerms.value.filter(term => 
    term.description.toLowerCase().includes(query) ||
    term.code.toLowerCase().includes(query) ||
    (term.category && term.category.toLowerCase().includes(query))
  );
});

const classifiedCount = computed(() => {
  return classificationItems.value.filter(item => item.dda_term_id).length;
});

const classificationProgress = computed(() => {
  if (classificationItems.value.length === 0) return 0;
  return (classifiedCount.value / classificationItems.value.length) * 100;
});

const drawPreviewRows = computed(() => {
  return ddaPreviewRows.value; // Show all preview rows, not limited to 5
});

const canProceed = computed(() => {
  const result = (() => {
    switch (currentStep.value) {
      // NEW STREAMLINED WORKFLOW
      case 'tab_selection':
        return selectedTabs.value.length > 0;
      case 'boundary_definition':
        return selectedTabs.value.every(tabIndex => {
          const boundary = tabBoundaries.value[tabIndex];
          return boundary && boundary.headerRow && boundary.dataStartRow;
        });

      case 'column_mapping':
        return simpleColumnMappings.value.description.length > 0 && 
               simpleColumnMappings.value.original_budget !== null && 
               simpleColumnMappings.value.current_budget !== null;
      case 'total_rows_selection':
        // Allow proceeding with no total rows selected (some projects may not have clear totals)
        return true;
      case 'template_qa_review':
        return true; // QA step allows proceeding to lock template
      
      // EXISTING G703 WORKFLOW (keep unchanged)
      case 'g703_table_setup':
        return g703HeaderRow.value !== null;
      case 'g703_column_mapping':
        return g703ColumnMapping.value.description_column !== null;
      case 'g703_correction':
        return true;
      case 'g703_review':
        return true;
      case 'g703_dda_mapping':
        return classificationItems.value.length > 0;
      
      // LEGACY WORKFLOW (for compatibility)
      case 'finalize':
        return true;
      default:
        return selectedColumn.value !== null;
    }
  })();
  
  console.log('CanProceed check:', {
    step: currentStep.value,
    result: result,
    selectedTabs: selectedTabs.value.length,
    simpleColumnMappings: simpleColumnMappings.value
  });
  
  return result;
});

const displayRows = computed(() => {
  return rows.value; // Show all rows, not limited to 15
});

// DDA Category Totals Computed Properties
const hasAnyDetectedTotals = computed(() => {
  return ddaCategoryTotals.value.some(category => category.detected);
});

const totalCategories = computed(() => [
  { key: 'hard_costs', label: 'Hard Costs', tooltip: 'Also known as Building Loan Total' },
  { key: 'soft_costs', label: 'Soft Costs', tooltip: 'Also known as Project Loan Total' },
  { key: 'financing', label: 'Financing', tooltip: null },
  { key: 'acquisition', label: 'Acquisition', tooltip: null },
  { key: 'project_total', label: 'Project Total', tooltip: null }
]);

// Helper method for category icons
function getCategoryIcon(categoryKey) {
  const icons = {
    'project_total': 'fas fa-chart-line',
    'hard_costs': 'fas fa-hard-hat',
    'soft_costs': 'fas fa-file-invoice',
    'financing': 'fas fa-hand-holding-usd',
    'acquisition': 'fas fa-building',
    'contingencies': 'fas fa-shield-alt'
  };
  return icons[categoryKey] || 'fas fa-folder';
}

// Helper method for category labels
function getCategoryLabel(categoryKey) {
  const labels = {
    'project_total': 'Project Total',
    'hard_costs': 'Hard Costs',
    'soft_costs': 'Soft Costs', 
    'financing': 'Financing',
    'acquisition': 'Acquisition',
    'contingencies': 'Contingencies'
  };
  return labels[categoryKey] || categoryKey;
}

// Helper to get actual Excel row number from filtered data index
function getActualRowNumber(filteredRowIndex) {
  const selectedTabIndex = selectedTabs.value[0];
  const boundary = tabBoundaries.value[selectedTabIndex];
  if (!boundary) return filteredRowIndex;
  
  // Convert from 0-based filtered index to actual Excel row (0-based)
  // dataStartRow is 1-based, so subtract 1
  const actualRowIndex = (boundary.dataStartRow - 1) + filteredRowIndex;
  return actualRowIndex;
}

// Methods for Total Rows Selection
function assignTotalRow(rowIndex) {
  if (selectedTotalCategory.value) {
    if (totalRowAssignments.value[rowIndex] === selectedTotalCategory.value) {
      // Unassign if clicking the same category
      delete totalRowAssignments.value[rowIndex];
    } else {
      // 🔥 ENHANCED: Allow multiple rows per category for computed totals
      // Check how many rows are already assigned to this category
      const categoryRows = Object.entries(totalRowAssignments.value)
        .filter(([idx, cat]) => cat === selectedTotalCategory.value);
      
      if (categoryRows.length > 0) {
        console.log(`📊 Multiple rows selected for ${selectedTotalCategory.value}: creating computed total`);
      }
      
      // Assign new category (allow multiple rows)
      totalRowAssignments.value[rowIndex] = selectedTotalCategory.value;
    }
  }
}

function getAssignedRows(category) {
  const assignedRows = Object.entries(totalRowAssignments.value)
    .filter(([rowIndex, assignedCategory]) => assignedCategory === category)
    .map(([rowIndex]) => parseInt(rowIndex) + 1); // These are already actual Excel row indices, just convert to 1-based
  
  return assignedRows.length > 0 ? assignedRows.join(', ') : 'None assigned';
}

function getTotalCategoryLabel(category) {
  const categoryMap = {
    hard_costs: 'Hard Costs',
    soft_costs: 'Soft Costs',
    financing: 'Financing',
    acquisition: 'Acquisition',
    project_total: 'Project Total'
  };
  return categoryMap[category] || category;
}

function getTotalRowClasses(rowIndex) {
  const classes = ['row-number'];
  const assignment = totalRowAssignments.value[rowIndex];
  
  if (assignment) {
    classes.push(`${assignment}-selected`);
  }
  
  return classes;
}

function getTotalRowExcelRowClasses(rowIndex) {
  const classes = [];
  const assignment = totalRowAssignments.value[rowIndex];
  
  if (assignment) {
    classes.push(`total-row-selected`);
    classes.push(`${assignment}-row-selected`);
  }
  
  return classes;
}

function getTotalRowCellClasses(rowIndex) {
  const classes = ['excel-cell'];
  const assignment = totalRowAssignments.value[rowIndex];
  
  if (assignment) {
    classes.push(`${assignment}-cell-selected`);
  }
  
  return classes;
}

// Methods for G703 Data
function initializeG703RawData(responseData) {
  // Store raw table data for column mapping
  if (responseData.raw_table_data) {
    // Convert cell data to simple text arrays for frontend display
    let processedData = responseData.raw_table_data.map(row => 
      row.map(cell => {
        // Handle both string cells and object cells with text property
        if (typeof cell === 'string') {
          return cell;
        } else if (typeof cell === 'object' && cell.text) {
          return cell.text;
        } else {
          return String(cell || '');
        }
      })
    );
    
    // Check if we have the "single row with many columns" issue
    if (processedData.length === 1 && processedData[0].length > 50) {
      console.log('Detected single row with', processedData[0].length, 'columns - restructuring to proper table format');
      processedData = restructureG703Data(processedData[0]);
    }
    
    g703RawData.value = processedData;
    console.log('Converted raw table data to text arrays:', g703RawData.value.length, 'rows');
    
    // Store document info for image display (already stored in documentData.data)
    console.log('Document URL available:', responseData.document_url);
    console.log('Document ID:', responseData.document_id);
  } else if (responseData.raw_g703_data) {
    // Fallback for already-processed data
    initializeEditableG703Data(responseData.raw_g703_data);
  }
  console.log('Initialized G703 raw data:', g703RawData.value?.length || 0, 'rows');
}

function restructureG703Data(flatRow) {
  // Restructure the flat row into proper table structure
  // Based on the pattern you showed: headers are first ~17 elements, then each line item is ~17 elements
  
  const COLUMNS_PER_ROW = 17; // Standard G703 has ~17 columns
  const restructuredRows = [];
  
  // First, let's identify where the headers end and data begins
  // Headers should be: TRADE CODE, DESCRIPTION OF WORK, NAMES, etc.
  // Data rows start with trade codes like "110000", "140000", etc.
  
  let headerEndIndex = -1;
  for (let i = 0; i < flatRow.length; i++) {
    const cell = flatRow[i];
    // Look for pattern that indicates start of data (trade codes or line item descriptions)
    if (cell && (
      cell.match(/^\d{6}$/) || // 6-digit trade codes like "110000"
      cell === 'Equipment' || 
      cell === 'Appliances' ||
      cell === 'Elevator' ||
      (cell.includes('$') && i > 15) // Dollar amounts after headers
    )) {
      headerEndIndex = i;
      break;
    }
  }
  
  if (headerEndIndex === -1) {
    // Fallback: assume first 17 elements are headers
    headerEndIndex = COLUMNS_PER_ROW;
  }
  
  console.log('Detected header end at index:', headerEndIndex);
  
  // Extract headers (first row)
  const headers = flatRow.slice(0, headerEndIndex);
  restructuredRows.push(headers);
  
  // Process remaining data into rows
  let dataStart = headerEndIndex;
  while (dataStart < flatRow.length) {
    // Take the next chunk of data
    const rowData = flatRow.slice(dataStart, dataStart + COLUMNS_PER_ROW);
    
    // Skip empty rows
    if (rowData.some(cell => cell && cell.trim())) {
      restructuredRows.push(rowData);
    }
    
    dataStart += COLUMNS_PER_ROW;
  }
  
  console.log('Restructured G703 data into', restructuredRows.length, 'rows with ~', COLUMNS_PER_ROW, 'columns each');
  console.log('Header row:', restructuredRows[0]);
  console.log('First data row:', restructuredRows[1]);
  
  return restructuredRows;
}

function selectG703HeaderRow(rowIndex) {
  g703HeaderRow.value = rowIndex;
  console.log(`Selected G703 header row ${rowIndex + 1}:`, g703RawData.value[rowIndex]);
  
  // Reset column mappings when header row changes
  g703ColumnMapping.value = {
    trade_code_column: null,
    description_column: null,
    contractor_names_column: null,
    scheduled_value_column: null,
    budget_transfer_column: null,
    owner_previous_approved_column: null,
    owner_approved_completed_column: null,
    adjusted_schedule_values_column: null,
    subcontractor_completed_column: null,
    work_completed_previous_column: null,
    work_completed_this_period_column: null,
    materials_stored_column: null,
    total_completed_column: null,
    percent_complete_column: null,
    balance_to_finish_column: null,
    retainage_percent_column: null,
    retainage_this_period_column: null,
    retainage_total_column: null
  };
}

async function applyDefaultG703Mapping() {
  console.log('🎯 Applying Template 33 standard G703 column mapping...');
  
  try {
    // First try to load Template 33 from backend
    const projectId = route.params.projectId;
    if (projectId) {
      const response = await axios.get(`${apiBase}draw_templates?project_id=${projectId}`, authToken.value);
      const templates = response.data;
      
      // Look for Template 33 or any G703 template marked as default
      const template33 = templates.find(t => 
        t.name && t.name.includes('Template 33') || 
        (t.template_type === 'g703' && t.project_specific)
      );
      
      if (template33) {
        console.log('✅ Found Template 33 in backend:', template33);
        
        // Get detailed template configuration
        const templateDetailResponse = await axios.get(`${apiBase}draw_templates/${template33.id}`, authToken.value);
        const templateConfig = templateDetailResponse.data.template_config;
        
        if (templateConfig && templateConfig.column_mappings && templateConfig.column_mappings.g703_column_mapping) {
          g703ColumnMapping.value = { ...templateConfig.column_mappings.g703_column_mapping };
          g703HeaderRow.value = templateConfig.column_mappings.header_row_index || 0;
          
          // Force reactivity update
          g703ColumnMapping.value = { ...g703ColumnMapping.value };
          
          console.log('✅ Template 33 loaded from backend with saved column mappings');
          return;
        }
      }
    }
  } catch (error) {
    console.warn('⚠️ Could not load Template 33 from backend, using default mapping:', error.message);
  }
  
  // Fallback to default G703 mapping
  g703ColumnMapping.value = { ...defaultG703ColumnMapping };
  
  // Force reactivity update
  g703ColumnMapping.value = { ...g703ColumnMapping.value };
  
  console.log('✅ Template 33 standard G703 mapping applied:', g703ColumnMapping.value);
  console.log('📊 This template provides 98% match for all GC G703 documents');
}

async function saveCurrentMappingAsDefault() {
  console.log('💾 Saving current column mapping as default template...');
  
  try {
    // Store in localStorage for immediate use
    const mappingTemplate = {
      g703_column_mapping: g703ColumnMapping.value,
      header_row: g703HeaderRow.value,
      created_at: new Date().toISOString(),
      template_name: 'Standard G703 Mapping'
    };
    
    localStorage.setItem('g703_default_template', JSON.stringify(mappingTemplate));
    
    // Also save to backend as Template 33 for project-wide use
    const projectId = route.params.projectId;
    if (projectId) {
      const templateData = {
        name: 'Template 33 - Standard G703 Mapping',
        description: 'Default G703 column mapping template (98% match for all GC G703 documents)',
        template_type: 'g703',
        status: 'completed',
        project_id: projectId,
        template_config: {
          template_type: 'g703',
          is_default: true,
          auto_apply: true,
          column_mappings: {
            header_row_index: g703HeaderRow.value,
            g703_column_mapping: g703ColumnMapping.value
          }
        }
      };

      // Save Template 33 to backend using finalize endpoint
      const response = await axios.post(`${apiBase}draw_templates/33/finalize`, {
        template: templateData
      });

      if (response.data.success || response.status === 200) {
        console.log('✅ Template 33 saved to backend successfully:', response.data);
      } else {
        console.warn('⚠️ Failed to save Template 33 to backend, but localStorage save succeeded');
      }
    }
    
    console.log('✅ Template saved successfully:', mappingTemplate);
    alert('Current column mapping saved as Template 33 for future G703 documents!');
    
  } catch (error) {
    console.error('❌ Error saving template:', error);
    alert('Error saving template: ' + error.message);
  }
}

function loadSavedG703Template() {
  const savedTemplate = localStorage.getItem('g703_default_template');
  if (savedTemplate) {
    try {
      const template = JSON.parse(savedTemplate);
      g703ColumnMapping.value = { ...template.g703_column_mapping };
      if (template.header_row !== null) {
        g703HeaderRow.value = template.header_row;
      }
      
      // Force reactivity update
      g703ColumnMapping.value = { ...g703ColumnMapping.value };
      
      console.log('✅ Loaded saved G703 template:', template);
      return true;
    } catch (error) {
      console.error('❌ Error loading saved template:', error);
      return false;
    }
  }
  return false;
}

function selectG703Column(columnIndex, mappingKey) {
  // If clicking on the same column that's already mapped, clear it
  if (g703ColumnMapping.value[mappingKey] === columnIndex) {
    g703ColumnMapping.value[mappingKey] = null;
    console.log(`Cleared mapping for ${mappingKey}`);
  } else {
    g703ColumnMapping.value[mappingKey] = columnIndex;
    console.log(`Mapped column ${columnIndex} to ${mappingKey}`);
  }
  
  console.log('Current mapping object:', g703ColumnMapping.value);
  console.log('Current field:', currentG703MappingField.value);
  
  // Force reactivity update
  g703ColumnMapping.value = { ...g703ColumnMapping.value };
}

function getG703ColumnHighlight(columnIndex, mappingKey) {
  // If this column is mapped to the current field, highlight it with the field color
  if (g703ColumnMapping.value[currentG703MappingField.value] === columnIndex) {
    return `highlight-${currentG703MappingField.value.replace('_column', '')}`;
  }
  
  // If this column is mapped to any other field, show a subtle mapping
  for (const [fieldKey, mappedCol] of Object.entries(g703ColumnMapping.value)) {
    if (mappedCol === columnIndex && fieldKey !== currentG703MappingField.value) {
      return 'highlight-mapped-other';
    }
  }
  
  return '';
}

async function proceedToG703Correction() {
  console.log('=== APPLYING COLUMN MAPPING FOR RE-PROCESSING ===');
  
  isProcessing.value = true;
  
  try {
    // STEP 1: Send column mappings to backend for re-scanning
    // Use the actual template ID from the route, not the project ID
    const templateId = route.params.templateId;
    
    if (!templateId) {
      throw new Error('No template ID found - cannot process G703 template');
    }
    
    console.log('✅ Using template ID:', templateId);
    
    const mappingPayload = {
      template: {
        template_type: 'g703',
        column_mappings: g703ColumnMapping.value,
        header_row: g703HeaderRow.value || 0,
        template_name: `G703 Template - Draw ${templateId}`
      }
    };
    
    console.log('Sending column mappings to backend:', mappingPayload);
    console.log('Template ID:', templateId);
    
    const response = await fetch(`/api/v1/draw_templates/${templateId}/apply_g703_template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mappingPayload)
    });
    
    const data = await response.json();
    
    if (data.success) {
      console.log('✅ Backend successfully applied template and re-processed G703!');
      
      // STEP 2: Get the re-scanned structured data (EXACT SAME AS PROJECT UPLOADER)
      editableG703Data.value = data.structured_data || [];
      
      console.log(`📋 Received ${editableG703Data.value.length} properly structured G703 line items`);
      console.log('Sample structured data:', editableG703Data.value.slice(0, 2));
      
      // Proceed to data review
      currentStep.value = 'g703_correction';
      return;
    } else {
      throw new Error(data.error || 'Failed to apply template');
    }
    
  } catch (error) {
    console.error('❌ Error applying template:', error);
    alert(`Error applying G703 template: ${error.message}`);
  } finally {
    isProcessing.value = false;
  }
}

function buildEditableG703DataFromMapping() {
  console.log('=== APPLYING COLUMN MAPPING TO RESTRUCTURE G703 DATA ===');
  
  if (!g703RawData.value) {
    console.warn('Missing raw data for mapping');
    return;
  }
  
  // Auto-detect header row if not set
  if (g703HeaderRow.value === null) {
    g703HeaderRow.value = autoDetectG703HeaderRow();
    console.log('Auto-detected header row:', g703HeaderRow.value);
  }
  
  if (g703HeaderRow.value === null) {
    console.warn('Could not detect header row');
    return;
  }
  
  const dataRows = g703RawData.value.slice(g703HeaderRow.value + 1);
  const mapping = g703ColumnMapping.value;
  
  console.log('Column Mappings:', mapping);
  console.log('Processing', dataRows.length, 'data rows');
  
  editableG703Data.value = dataRows.map((row, index) => {
    // Ensure row is an array and not undefined
    if (!Array.isArray(row)) {
      console.warn(`Row ${index} is not an array:`, row);
      return null;
    }
    
    // Skip empty rows (rows with no meaningful data)
    const hasData = row.some(cell => cell && cell.toString().trim() !== '');
    if (!hasData) {
      console.log(`Skipping empty row ${index}`);
      return null;
    }
    
    // Extract and clean each field according to its type
    const description = cleanDescriptionField(getColumnValue(row, mapping.description_column));
    const tradeCode = cleanTradeCodeField(getColumnValue(row, mapping.trade_code_column));
    const contractorName = cleanTextFieldField(getColumnValue(row, mapping.contractor_names_column));
    
    // Financial fields - clean currency values
    const scheduledValue = cleanCurrencyField(getColumnValue(row, mapping.scheduled_value_column));
    const budgetTransfer = cleanCurrencyField(getColumnValue(row, mapping.budget_transfer_column));
    const ownerPreviousApproved = cleanCurrencyField(getColumnValue(row, mapping.owner_previous_approved_column));
    const ownerApprovedCompleted = cleanCurrencyField(getColumnValue(row, mapping.owner_approved_completed_column));
    const adjustedScheduleValues = cleanCurrencyField(getColumnValue(row, mapping.adjusted_schedule_values_column));
    const subcontractorCompleted = cleanCurrencyField(getColumnValue(row, mapping.subcontractor_completed_column));
    const workPrevious = cleanCurrencyField(getColumnValue(row, mapping.work_completed_previous_column));
    const workThisPeriod = cleanCurrencyField(getColumnValue(row, mapping.work_completed_this_period_column));
    const materialsStored = cleanCurrencyField(getColumnValue(row, mapping.materials_stored_column));
    const totalCompleted = cleanCurrencyField(getColumnValue(row, mapping.total_completed_column));
    const balanceToFinish = cleanCurrencyField(getColumnValue(row, mapping.balance_to_finish_column));
    
    // Percentage and retainage fields
    const percentComplete = cleanPercentageField(getColumnValue(row, mapping.percent_complete_column));
    const retainagePercent = cleanPercentageField(getColumnValue(row, mapping.retainage_percent_column));
    const retainageThisPeriod = cleanCurrencyField(getColumnValue(row, mapping.retainage_this_period_column));
    const retainageTotal = cleanCurrencyField(getColumnValue(row, mapping.retainage_total_column));
    
    // Skip rows that don't have a meaningful description
    if (!description || description === 'Unknown Item' || description.length < 3) {
      console.log(`Skipping row ${index} - no valid description:`, description);
      return null;
    }
    
    const lineItem = {
      id: index,
      // Core identification
      trade_code: tradeCode,
      description: description,
      contractor_name: contractorName,
      
      // Financial data - properly typed as numbers
      scheduled_value: scheduledValue,
      budget_transfer: budgetTransfer,
      owner_previous_approved: ownerPreviousApproved,
      owner_approved_completed: ownerApprovedCompleted,
      adjusted_schedule_values: adjustedScheduleValues,
      subcontractor_completed: subcontractorCompleted,
      work_completed_previous: workPrevious,
      work_completed_this_period: workThisPeriod,
      materials_stored: materialsStored,
      total_completed: totalCompleted,
      balance_to_finish: balanceToFinish,
      
      // Percentages
      percent_complete: percentComplete,
      retainage_percent: retainagePercent,
      retainage_this_period: retainageThisPeriod,
      retainage_total: retainageTotal,
      
      // Legacy fields for compatibility
      original_budget: scheduledValue,
      current_request: workThisPeriod,
      previously_funded: workPrevious,
      total_completed_to_date: totalCompleted,
      current_budget: scheduledValue,
      materials_presently_stored: materialsStored,
      total_completed_to_date_pct: percentComplete,
      balance_to_complete: balanceToFinish,
      total_retainage_held: retainageTotal,
      current_retainage_held: retainageThisPeriod,
      contractor_id: null
    };
    
    console.log(`Row ${index + 1}:`, {
      description: lineItem.description,
      scheduled_value: lineItem.scheduled_value,
      work_this_period: lineItem.work_completed_this_period,
      percent_complete: lineItem.percent_complete
    });
    
    return lineItem;
  }).filter(item => item !== null); // Remove null items
  
  console.log('=== RESTRUCTURING COMPLETE ===');
  console.log(`Successfully created ${editableG703Data.value.length} clean G703 line items`);
  console.log('Sample cleaned data:', editableG703Data.value.slice(0, 2));
}

function cleanCurrencyValue(value) {
  if (!value) return '0';
  return value.toString().replace(/[$,]/g, '').trim();
}

function cleanPercentageValue(value) {
  if (!value) return '0';
  return value.toString().replace(/%/g, '').trim();
}

// Auto-detect G703 header row by looking for common G703 header terms
function autoDetectG703HeaderRow() {
  if (!g703RawData.value) return null;
  
  const headerTerms = [
    'TRADE CODE', 'DESCRIPTION', 'NAMES', 'SCHEDULE', 'VALUES', 'BUDGET',
    'OWNER', 'APPROVED', 'WORK', 'COMPLETED', 'MATERIALS', 'STORED',
    'PERCENTAGE', 'BALANCE', 'FINISH', 'RETAINAGE'
  ];
  
  for (let rowIndex = 0; rowIndex < Math.min(5, g703RawData.value.length); rowIndex++) {
    const row = g703RawData.value[rowIndex];
    if (!Array.isArray(row)) continue;
    
    // Count how many header terms appear in this row
    let headerMatches = 0;
    const rowText = row.join(' ').toUpperCase();
    
    headerTerms.forEach(term => {
      if (rowText.includes(term)) {
        headerMatches++;
      }
    });
    
    // If we find at least 4 header terms, this is likely the header row
    if (headerMatches >= 4) {
      console.log(`Found header row ${rowIndex} with ${headerMatches} matches:`, row);
      return rowIndex;
    }
  }
  
  console.log('No clear header row found, defaulting to row 0');
  return 0; // Default to first row if no clear header found
}

// Helper function to safely get column value
function getColumnValue(row, columnIndex) {
  if (columnIndex === null || columnIndex === undefined || !Array.isArray(row)) {
    return '';
  }
  return row[columnIndex] || '';
}

// Specialized cleaning functions for different field types
function cleanDescriptionField(value) {
  if (!value || typeof value !== 'string') return '';
  
  const originalValue = value.trim();
  console.log(`🔍 Processing description: "${originalValue}"`);
  
  // If the value is ONLY a currency amount with no text, it's likely not a description
  if (/^\$?[\d,\s]+\.?\d*$/.test(originalValue)) {
    console.log(`⚠️ Rejecting pure currency value: ${originalValue}`);
    return '';
  }
  
  // If it's a percentage only, reject it
  if (/^\d+%$/.test(originalValue)) {
    console.log(`⚠️ Rejecting percentage value: ${originalValue}`);
    return '';
  }
  
  // For mixed content, try to extract meaningful text
  let cleaned = originalValue;
  
  // If there's actual text content mixed with numbers/currency, try to preserve the text
  if (/[a-zA-Z]{3,}/.test(originalValue)) {
    // Remove excessive dollar signs and numbers but preserve text
    cleaned = originalValue
      // Remove standalone dollar amounts
      .replace(/\$[\d,\s]+(?:\.\d{2})?/g, ' ')
      // Remove standalone large numbers
      .replace(/\b\d{4,}(,\d{3})*\b/g, ' ')
      // Remove percentage signs with numbers
      .replace(/\d+%/g, ' ')
      // Clean up multiple spaces
      .replace(/\s+/g, ' ')
      .replace(/[,\s]+$/, '') // Remove trailing commas and spaces
      .trim();
  }
  
  // If we still have meaningful text after cleaning, use it
  if (cleaned.length >= 3 && /[a-zA-Z]{2,}/.test(cleaned)) {
    console.log(`✅ Cleaned description: "${originalValue}" → "${cleaned}"`);
    return cleaned;
  }
  
  // If cleaning removed too much, but original had some text, use original
  if (/[a-zA-Z]{2,}/.test(originalValue)) {
    console.log(`✅ Using original description: "${originalValue}"`);
    return originalValue;
  }
  
  console.log(`❌ No valid description found in: "${originalValue}"`);
  return '';
}

function cleanTradeCodeField(value) {
  if (!value) return '';
  
  // Trade codes are typically 2-6 digit numbers, sometimes with letters
  let cleaned = value.toString().trim();
  
  // Remove dollar signs and clean up
  cleaned = cleaned.replace(/[$,]/g, '').trim();
  
  // If it looks like a valid trade code pattern, return it
  if (/^[A-Z0-9]{2,6}$/.test(cleaned)) {
    return cleaned;
  }
  
  // Try to extract digits from the beginning
  const match = cleaned.match(/^(\d{2,6})/);
  if (match) {
    return match[1];
  }
  
  return cleaned;
}

function cleanTextFieldField(value) {
  if (!value || typeof value !== 'string') return '';
  
  // Remove dollar amounts and financial data that got mixed in
  let cleaned = value
    .replace(/\$[\d,\s]+/g, ' ')
    .replace(/\b\d+%\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  return cleaned;
}

function cleanCurrencyField(value) {
  if (!value) return 0;
  
  // Convert to string and remove currency symbols, commas, spaces
  let cleaned = value.toString()
    .replace(/[$,\s]/g, '')
    .replace(/[()]/g, '') // Remove parentheses
    .trim();
  
  // Handle negative values (sometimes shown in parentheses)
  let isNegative = false;
  if (value.toString().includes('(') && value.toString().includes(')')) {
    isNegative = true;
  }
  
  // Extract just the numeric part
  const match = cleaned.match(/(\d+\.?\d*)/);
  if (match) {
    let number = parseFloat(match[1]) || 0;
    return isNegative ? -number : number;
  }
  
  return 0;
}

function cleanPercentageField(value) {
  if (!value) return 0;
  
  // Remove % symbol and convert to decimal
  let cleaned = value.toString()
    .replace(/%/g, '')
    .replace(/[,\s]/g, '')
    .trim();
  
  const number = parseFloat(cleaned) || 0;
  
  // If the number is > 1, assume it's already in percentage form (e.g., 25 for 25%)
  // If it's <= 1, assume it's in decimal form (e.g., 0.25 for 25%)
  return number > 1 ? number : number * 100;
}

function initializeEditableG703Data(rawG703Data) {
  // Create editable copies of the G703 data
  editableG703Data.value = rawG703Data.map((item, index) => ({
    id: index,
    description: item.description || '',
    original_budget: parseFloat(item.original_budget) || 0,
    current_request: parseFloat(item.current_request) || 0,
    previously_funded: parseFloat(item.previously_funded) || 0,
    total_completed_to_date: parseFloat(item.total_completed_to_date) || 0,
    current_budget: parseFloat(item.current_budget) || parseFloat(item.original_budget) || 0,
    materials_presently_stored: parseFloat(item.materials_presently_stored) || 0,
    total_completed_to_date_pct: parseFloat(item.total_completed_to_date_pct) || 0,
    balance_to_complete: parseFloat(item.balance_to_complete) || 0,
    total_retainage_held: parseFloat(item.total_retainage_held) || 0,
    current_retainage_held: parseFloat(item.current_retainage_held) || 0,
    contractor_id: item.contractor_id || null
  }));
  console.log('Initialized editable G703 data:', editableG703Data.value.length);
}

function addG703Item() {
  const newItem = {
    id: editableG703Data.value.length,
    description: '',
    original_budget: 0,
    current_request: 0,
    previously_funded: 0,
    total_completed_to_date: 0,
    current_budget: 0,
    materials_presently_stored: 0,
    total_completed_to_date_pct: 0,
    balance_to_complete: 0,
    total_retainage_held: 0,
    current_retainage_held: 0,
    contractor_id: null
  };
  editableG703Data.value.push(newItem);
  console.log('Added new G703 item');
}

function removeG703Item(index) {
  if (confirm('Are you sure you want to remove this line item?')) {
    editableG703Data.value.splice(index, 1);
    // Update IDs
    editableG703Data.value.forEach((item, idx) => {
      item.id = idx;
    });
    console.log('Removed G703 item at index', index);
  }
}

function proceedFromCorrection() {
  // Update documentData with corrected values
  documentData.value.raw_g703_data = editableG703Data.value;
  currentStep.value = 'g703_review';
  console.log('Proceeding from G703 correction with updated data');
}

function proceedToDDAMapping() {
  currentStep.value = 'g703_dda_mapping';
  console.log('Proceeding to G703 DDA mapping');
  
  // Build classification items from corrected G703 data
  buildG703ClassificationItems();
}

function buildG703ClassificationItems() {
  // Use editable data if available, otherwise fall back to raw data
  const g703Data = editableG703Data.value.length > 0 ? editableG703Data.value : documentData.value?.raw_g703_data;
  if (!g703Data) return;
  
  classificationItems.value = g703Data.map((item, index) => ({
    id: index,
    description: cleanG703Description(item.description) || 'Unknown Item',
    original_budget: parseFloat(item.original_budget) || 0,
    current_request: parseFloat(item.current_request) || 0,
    previously_funded: parseFloat(item.previously_funded) || 0,
    total_completed_to_date: parseFloat(item.total_completed_to_date) || 0,
    current_budget: parseFloat(item.current_budget) || parseFloat(item.original_budget) || 0,
    // DDA mapping fields
    dda_term_id: null,
    searchTerm: '',
    searchResults: [],
    showDropdown: false,
    auto_matched: false
  }));
  
  console.log('Built G703 classification items:', classificationItems.value.length);
  
  // Auto-match descriptions to DDA terms
  autoMatchDDATerms();
}

function cleanG703Description(description) {
  if (!description || typeof description !== 'string') return 'Unknown Item';
  
  // Remove excessive dollar signs and numbers that got concatenated
  let cleaned = description
    // Remove patterns like "$618, 482, $209, 241," etc.
    .replace(/\$[\d,\s]+\$[\d,\s]+/g, '')
    // Remove standalone dollar amounts with commas
    .replace(/\$[\d,\s]+/g, ' ')
    // Remove excessive parentheses content that looks like dollar amounts
    .replace(/\([^)]*\$[^)]*\)/g, '')
    // Remove percentage signs with numbers
    .replace(/\d+%/g, '')
    // Clean up multiple spaces and weird characters
    .replace(/\s+/g, ' ')
    .replace(/[,\s]+$/, '') // Remove trailing commas and spaces
    .trim();
  
  // If the description got too mangled, try to extract just the main part
  if (cleaned.length < 10 || cleaned.includes('$')) {
    // Try to find a meaningful part before any financial data
    const parts = description.split(/\$|\(|\d{3,}/);
    if (parts.length > 0 && parts[0].trim().length > 5) {
      cleaned = parts[0].trim();
    }
  }
  
  return cleaned || 'Unknown Item';
}

// Methods for Raw Excel Data
function selectHeaderRow(rowIndex) {
  selectedHeaderRow.value = rowIndex;
  console.log(`Selected header row ${rowIndex + 1}:`, documentData.value.rows[rowIndex]);
  
  // Check for multi-row headers and combine them
  // Legacy compatibility - using header row as-is
  const combinedHeaders = headers.value;
  
  // Reset mappings when header row changes
  ddaMapping.value = {
    main_category_column: null,
    description_columns: [],
    cost_code_column: null,
    company_column: null,
    original_budget_column: null,
    current_budget_column: null,
    current_draw_column: [], // 🔥 CHANGED: Now array for multi-column support
    draw_columns: []
  };
  
  // Reset optional flags
  hasCostCodeColumn.value = false;
  hasCompanyColumn.value = false;
}



function isNumericContent(cell) {
  if (!cell) return false;
  const str = cell.toString().trim();
  // Check if it's a number, currency, or percentage
  return /^[\d,.$%\s-]+$/.test(str) && !/^[A-Za-z]/.test(str);
}

function isDateContent(cell) {
  if (!cell) return false;
  const str = cell.toString().trim();
  // Common date patterns
  return /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(str) || 
         /^\d{4}-\d{2}-\d{2}$/.test(str) ||
         /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i.test(str);
}

function isGenericHeader(cell) {
  if (!cell) return true;
  const str = cell.toString().toLowerCase().trim();
  // Generic/empty headers that don't add meaning
  const genericTerms = ['', 'column', 'data', 'value', 'amount', 'total', 'item'];
  return genericTerms.includes(str) || str.length < 2;
}

function selectDateRow(rowIndex) {
  // Don't allow selecting the header row as date row
  if (rowIndex === selectedHeaderRow.value) {
    console.log('Cannot select header row as date row');
    return;
  }
  
  selectedDateRow.value = rowIndex;
  console.log(`Selected date row ${rowIndex + 1}:`, documentData.value.rows[rowIndex]);
  
  // Extract dates from the selected row
  if (documentData.value?.rows[rowIndex]) {
    const dateRow = documentData.value.rows[rowIndex];
    const dates = extractDatesFromRow(dateRow);
    console.log('Extracted dates/periods:', dates);
  }
}

function skipDateStep() {
  selectedDateRow.value = null;
  console.log('Skipped date step - no date periods');
  nextStep();
}

function isDateRowColumn(cellIndex) {
  // Highlight columns that might contain dates (columns after the current draw column)
  if (ddaMapping.value.current_draw_column !== null) {
    return cellIndex > ddaMapping.value.current_draw_column;
  }
  return false;
}

function extractDatesFromRow(row) {
  const dates = [];
  row.forEach((cell, index) => {
    if (cell && isDateRowColumn(index)) {
      // Try to parse as date or period label
      const parsed = parseDateOrPeriod(cell);
      if (parsed) {
        dates.push({ columnIndex: index, value: cell, parsed: parsed });
      }
    }
  });
  return dates;
}

function parseDateOrPeriod(value) {
  if (!value) return null;
  
  const strValue = value.toString().trim();
  
  // Try common date formats
  const datePatterns = [
    /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,  // MM/DD/YYYY or M/D/YY
    /^\d{4}-\d{2}-\d{2}$/,           // YYYY-MM-DD
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}$/i,  // Jan 2024
    /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}$/i,
    /^Draw\s+\d+$/i,                 // Draw 1, Draw 2, etc.
    /^Period\s+\d+$/i,               // Period 1, Period 2, etc.
  ];
  
  for (const pattern of datePatterns) {
    if (pattern.test(strValue)) {
      return strValue;
    }
  }
  
  // Try to parse as date
  const date = new Date(strValue);
  if (!isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }
  
  return null;
}

function getBudgetMappedValue(row, mappingKey) {
  const columnIndex = ddaMapping.value[mappingKey];
  return columnIndex !== null ? row[columnIndex] : '';
}

function getDDAMappedValue(row, mappingKey) {
  const columnIndex = ddaMapping.value[mappingKey];
  return columnIndex !== null ? row[columnIndex] : '';
}

function getCombinedDescriptions(row) {
  const descriptions = []
  ddaMapping.value.description_columns.forEach(colIndex => {
    const desc = row[colIndex]
    if (desc && desc.toString().trim() !== '') {
      descriptions.push(desc.toString().trim())
    }
  })
  return descriptions.join(' - ')
}

function isDrawColumn(columnIndex) {
  return ddaMapping.value.draw_columns.includes(columnIndex);
}

function toggleDrawColumn(columnIndex) {
  const index = selectedDrawColumns.value.indexOf(columnIndex);
  if (index > -1) {
    selectedDrawColumns.value.splice(index, 1);
    delete drawPeriodLabels.value[columnIndex];
  } else {
    selectedDrawColumns.value.push(columnIndex);
    // Auto-generate label based on header
    const header = headerRow.value[columnIndex];
    drawPeriodLabels.value[columnIndex] = header || `Draw ${selectedDrawColumns.value.length}`;
  }
}

function removeDrawColumn(columnIndex) {
  const index = selectedDrawColumns.value.indexOf(columnIndex);
  if (index > -1) {
    selectedDrawColumns.value.splice(index, 1);
    delete drawPeriodLabels.value[columnIndex];
  }
}

function getDrawPeriodLabel(columnIndex) {
  return drawPeriodLabels.value[columnIndex] || headerRow.value[columnIndex] || `Draw ${columnIndex}`;
}

function getDrawDescription(row) {
  const mainCategory = getBudgetMappedValue(row, 'main_category_column');
  const subItem = getBudgetMappedValue(row, 'sub_item_column');
  
  if (subItem) return subItem;
  if (mainCategory) return mainCategory;
  return 'Unknown Item';
}

// Legacy Methods (kept for compatibility)
function getColumnHighlight(columnIndex) {
  const classes = [];
  
  if (selectedColumn.value === columnIndex) {
    classes.push('selected-column');
  }
  
  if (currentStep.value === 'original_budget' && isOriginalBudgetColumn(columnIndex)) {
    classes.push('highlight-original-budget');
  } else if (currentStep.value === 'current_budget' && isCurrentBudgetColumn(columnIndex)) {
    classes.push('highlight-current-budget');
  } else if (currentStep.value === 'total_spent' && isTotalSpentColumn(columnIndex)) {
    classes.push('highlight-total-spent');
  }
  
  return classes.join(' ');
}

function isOriginalBudgetColumn(columnIndex) {
  const header = headers.value[columnIndex]?.toLowerCase() || '';
  return header.includes('original') && header.includes('budget');
}

function isCurrentBudgetColumn(columnIndex) {
  const header = headers.value[columnIndex]?.toLowerCase() || '';
  return (header.includes('current') || header.includes('revised')) && header.includes('budget');
}

function isTotalSpentColumn(columnIndex) {
  const header = headers.value[columnIndex]?.toLowerCase() || '';
  return header.includes('total') && (header.includes('spent') || header.includes('billing') || header.includes('complete'));
}

function selectColumn(columnIndex) {
  console.log(`Clicked column ${columnIndex + 1}:`, headerRow.value[columnIndex]);
  
  // Map the column based on current step
  switch (currentStep.value) {
    case 'main_category_column':
      // Clear if clicking same column, otherwise set new column
      if (ddaMapping.value.main_category_column === columnIndex) {
        ddaMapping.value.main_category_column = null;
        console.log('Cleared main category column');
      } else {
        ddaMapping.value.main_category_column = columnIndex;
        console.log('Set main category column:', columnIndex);
      }
      break;
    case 'description_columns':
    case 'single_description_columns': {
      // Toggle selection for description columns
      const idx = ddaMapping.value.description_columns.indexOf(columnIndex);
      if (idx > -1) {
        ddaMapping.value.description_columns.splice(idx, 1);
        console.log('Removed description column:', columnIndex);
      } else {
        ddaMapping.value.description_columns.push(columnIndex);
        ddaMapping.value.description_columns.sort((a, b) => a - b); // Keep sorted left to right
        console.log('Added description column:', columnIndex);
      }
      
      // Clear main category for single mode
      if (currentStep.value === 'single_description_columns') {
        ddaMapping.value.main_category_column = null;
      }
      break;
    }
    case 'original_budget_column':
      if (ddaMapping.value.original_budget_column === columnIndex) {
        ddaMapping.value.original_budget_column = null;
        console.log('Cleared original budget column');
      } else {
        ddaMapping.value.original_budget_column = columnIndex;
        console.log('Set original budget column:', columnIndex);
      }
      break;
    case 'current_budget_column':
      if (ddaMapping.value.current_budget_column === columnIndex) {
        ddaMapping.value.current_budget_column = null;
        console.log('Cleared current budget column');
      } else {
        ddaMapping.value.current_budget_column = columnIndex;
        console.log('Set current budget column:', columnIndex);
      }
      break;
    case 'funded_at_closing_column':
      if (ddaMapping.value.funded_at_closing_column === columnIndex) {
        ddaMapping.value.funded_at_closing_column = null;
        console.log('Cleared funded at closing column');
      } else {
        ddaMapping.value.funded_at_closing_column = columnIndex;
        console.log('Set funded at closing column:', columnIndex);
      }
      break;
    case 'current_draw_column': {
      // 🔥 MULTI-COLUMN SUPPORT: Toggle selection like description_columns
      const idx = ddaMapping.value.current_draw_column.indexOf(columnIndex);
      if (idx > -1) {
        ddaMapping.value.current_draw_column.splice(idx, 1);
        console.log('Removed current draw column:', columnIndex);
      } else {
        ddaMapping.value.current_draw_column.push(columnIndex);
        ddaMapping.value.current_draw_column.sort((a, b) => a - b); // Keep sorted left to right
        console.log('Added current draw column:', columnIndex, '| Total columns:', ddaMapping.value.current_draw_column);
      }
      break;
    }
    case 'balance_to_complete_column':
      if (ddaMapping.value.balance_to_complete_column === columnIndex) {
        ddaMapping.value.balance_to_complete_column = null;
        console.log('Cleared balance to complete column');
      } else {
        ddaMapping.value.balance_to_complete_column = columnIndex;
        console.log('Set balance to complete column:', columnIndex);
      }
      break;
    case 'draw_columns': {
      // Multi-draw column selection
      const drawIndex = ddaMapping.value.draw_columns.indexOf(columnIndex);
      if (drawIndex > -1) {
        // Deselect if already selected
        ddaMapping.value.draw_columns.splice(drawIndex, 1);
        console.log('Removed draw column:', columnIndex);
      } else {
        // Select if not already selected (skip budget columns)
        if (columnIndex !== ddaMapping.value.original_budget_column && 
            columnIndex !== ddaMapping.value.current_budget_column) {
          ddaMapping.value.draw_columns.push(columnIndex);
          ddaMapping.value.draw_columns.sort((a, b) => a - b); // Keep sorted left to right
          console.log('Added draw column:', columnIndex, 'Total draw columns:', ddaMapping.value.draw_columns.length);
        }
      }
      // Set the leftmost as current draw for compatibility
      if (ddaMapping.value.draw_columns.length > 0) {
        ddaMapping.value.current_draw_column = Math.min(...ddaMapping.value.draw_columns);
      } else {
        ddaMapping.value.current_draw_column = null;
      }
      break;
    }
    case 'contractor_column':
      if (ddaMapping.value.company_column === columnIndex) {
        ddaMapping.value.company_column = null;
        console.log('Cleared contractor/company column');
      } else {
        ddaMapping.value.company_column = columnIndex;
        console.log('Set contractor/company column:', columnIndex);
      }
      break;
  }
}

function selectDDATerm(term) {
  selectedTerm.value = term;
}

function formatCellValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    if (value > 1000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
      }).format(value);
    }
    return value.toLocaleString();
  }
  return value.toString();
}

function formatCurrency(amount) {
  if (!amount) return '$0';
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(numAmount);
}

function formatPercent(decimal) {
  if (!decimal) return '0%';
  return `${(decimal * 100).toFixed(1)}%`;
}

// QA Step Helper Functions


function getBudgetMappingCount() {
  let count = 0;
  if (simpleColumnMappings.value.original_budget !== null) count++;
  if (simpleColumnMappings.value.current_budget !== null) count++;
  if (simpleColumnMappings.value.balance_to_complete !== null) count++;
  return count;
}

function isTemplateReady() {
  return selectedTabs.value.length > 0 && 
         simpleColumnMappings.value.description.length > 0 && 
         getBudgetMappingCount() > 0;
}

function getColumnName(columnIndex) {
  if (columnIndex === null || columnIndex === undefined) return 'Not mapped';
  if (!documentData.value?.rows || !documentData.value.rows[0]) return 'Unknown';
  const headerRow = documentData.value.rows[selectedHeaderRow.value || 0];
  return headerRow[columnIndex] || `Column ${String.fromCharCode(65 + columnIndex)}`;
}



function getAllColumnMappings() {
  if (!simpleColumnMappings.value || !tabBoundaries.value || !selectedTabs.value?.length) return [];
  
  // Get combinedHeaders from the selected tab, not the first tab
  let combinedHeaders = [];
  const selectedTabIndex = selectedTabs.value[0];
  if (selectedTabIndex !== undefined && tabBoundaries.value[selectedTabIndex]?.combinedHeaders) {
    combinedHeaders = tabBoundaries.value[selectedTabIndex].combinedHeaders;
    console.log('📋 QA Page: Using combined headers:', combinedHeaders.slice(0, 8));
  } else {
    console.log('❌ QA Page: No combined headers found, falling back to filtered headers');
    combinedHeaders = getFilteredHeaders(selectedTabIndex);
  }
  
  if (!combinedHeaders.length) {
    console.log('❌ QA Page: No headers available at all');
    return [];
  }
  
  const mappings = [
    { field: 'description', fieldLabel: 'Description', key: 'description' },
    { field: 'original_budget', fieldLabel: 'Original Budget', key: 'original_budget' },
    { field: 'current_budget', fieldLabel: 'Current Budget', key: 'current_budget' },
    { field: 'balance_to_complete', fieldLabel: 'Balance to Complete', key: 'balance_to_complete' },
    { field: 'contractor', fieldLabel: 'Contractor', key: 'contractor' },
    { field: 'spent_to_date', fieldLabel: 'Spent to Date', key: 'spent_to_date' },
    { field: 'remaining_budget', fieldLabel: 'Remaining Budget', key: 'remaining_budget' },
    { field: 'funded_at_closing', fieldLabel: 'Funded at Closing', key: 'funded_at_closing' },
    { field: 'current_draw', fieldLabel: 'Current Draw', key: 'current_draw' }
  ];
  
  return mappings.map(mapping => {
    const columns = [];
    
    if (mapping.key === 'description') {
      // Description can have multiple columns
      if (simpleColumnMappings.value.description && simpleColumnMappings.value.description.length > 0) {
        simpleColumnMappings.value.description.forEach(colIndex => {
          if (colIndex !== null && colIndex !== undefined) {
            const headerText = combinedHeaders[colIndex] || `Column ${String.fromCharCode(65 + colIndex)}`;
            columns.push({
              name: headerText,
              letter: String.fromCharCode(65 + colIndex)
            });
          }
        });
      }
    } else {
      // Other fields have single column mapping
      const colIndex = simpleColumnMappings.value[mapping.key];
      if (colIndex !== null && colIndex !== undefined) {
        const headerText = combinedHeaders[colIndex] || `Column ${String.fromCharCode(65 + colIndex)}`;
        columns.push({
          name: headerText,
          letter: String.fromCharCode(65 + colIndex)
        });
      }
    }
    
    return {
      field: mapping.field,
      fieldLabel: mapping.fieldLabel,
      columns: columns
    };
  }).filter(mapping => mapping.columns.length > 0); // Only show fields that have mappings
}

function getDataPreviewRows() {
  if (!availableTabs.value || !tabBoundaries.value || !simpleColumnMappings.value) return [];
  
  // Use the selected tab from Step 1, not the first tab
  if (!selectedTabs.value || selectedTabs.value.length === 0) return [];
  const selectedTabIndex = selectedTabs.value[0]; // Use the first selected tab
  
  const tab = availableTabs.value[selectedTabIndex];
  const boundary = tabBoundaries.value[selectedTabIndex];
  if (!tab || !boundary) return [];
  
  // Use the same logic as getFilteredRows function that works correctly
  const startRow = boundary.dataStartRow - 1; // Convert to 0-based
  const endRow = boundary.dataEndRow === 0 ? tab.data.length : boundary.dataEndRow;
  const descriptionColumns = simpleColumnMappings.value.description || [];
  const originalBudgetCol = simpleColumnMappings.value.original_budget;
  
  const previewRows = [];
  
  // Get the actual filtered data like Step 3 does
  for (let rowIndex = startRow; rowIndex < endRow; rowIndex++) {
    const row = tab.data[rowIndex];
    if (!row) continue;
    
    // Row processing (total rows will be handled by DDA terms)
    const actualRowNum = rowIndex + 1; // Actual row number in the spreadsheet
    
    // Skip empty rows
    if (!row.some(cell => cell && cell.toString().trim() !== '')) continue;
    
    // 🎯 BUILD HIERARCHICAL DESCRIPTION FROM ALL MAPPED COLUMNS
    const descriptions = [];
    descriptionColumns.forEach((colIndex) => {
      const desc = row[colIndex];
      if (desc && desc.toString().trim() !== '') {
        descriptions.push(desc.toString().trim());
      }
    });
    
    // Create hierarchical description using arrows for better visual hierarchy
    const fullHierarchy = descriptions.length > 0 ? descriptions.join(' → ') : `Row ${actualRowNum}`;
    const originalBudget = originalBudgetCol !== null ? (row[originalBudgetCol] || 0) : 0;
    const currentBudget = simpleColumnMappings.value.current_budget !== null ? (row[simpleColumnMappings.value.current_budget] || 0) : 0;
    const spentToDate = simpleColumnMappings.value.spent_to_date !== null ? (row[simpleColumnMappings.value.spent_to_date] || 0) : 0;
    const remainingBudget = simpleColumnMappings.value.remaining_budget !== null ? (row[simpleColumnMappings.value.remaining_budget] || 0) : 0;
    // 🔥 MULTI-COLUMN SUPPORT: Sum array of columns if provided, otherwise use single value
    const currentDraw = (() => {
      if (Array.isArray(simpleColumnMappings.value.current_draw)) {
        return simpleColumnMappings.value.current_draw.reduce((sum, colIdx) => sum + (row[colIdx] || 0), 0);
      } else if (simpleColumnMappings.value.current_draw !== null) {
        return row[simpleColumnMappings.value.current_draw] || 0;
      }
      return 0;
    })();
    
    // Check if this is a total row
    const isTotal = totalRowAssignments.value[rowIndex] || null;
    
    previewRows.push({
      rowNumber: actualRowNum,
      description: fullHierarchy,
      originalBudget: originalBudget,
      currentBudget: currentBudget,
      spentToDate: spentToDate,
      remainingBudget: remainingBudget,
      currentDraw: currentDraw,
      totalRowCategory: isTotal,
      ddaTerm: null, // Will be filled by auto-matching when template is locked
      confidence: null
    });
  }
  
  return previewRows;
}

function getConfidenceClass(confidence) {
  if (!confidence) return 'confidence-pending';
  if (confidence >= 80) return 'confidence-high';
  if (confidence >= 60) return 'confidence-medium'; 
  return 'confidence-low';
}

async function nextStep() {
  console.log('NextStep called - NEW STREAMLINED WORKFLOW');
  console.log('Current step:', currentStep.value);
  
  const stepKeys = steps.value.map(s => s.key);
  const currentIndex = stepKeys.indexOf(currentStep.value);
  
  if (currentIndex < stepKeys.length - 1) {
    let nextStepKey = stepKeys[currentIndex + 1];
    
    // 🚀 AUTO-SKIP TOTAL ROWS STEP: Always skip for Draw Trackers (saves time, uses previous mappings)
    if (nextStepKey === 'total_rows_selection') {
      console.log('⏩ Auto-skipping Total Rows step (uses previous draw mappings)');
      nextStepKey = stepKeys[currentIndex + 2]; // Skip to QA step
    }
    
    currentStep.value = nextStepKey;
    
    // Special logic for transitions
    if (nextStepKey === 'boundary_definition' && selectedTabs.value.length > 0) {
      currentBoundaryTab.value = selectedTabs.value[0];
    }



    if (nextStepKey === 'column_mapping') {
      autoMatchColumns();
    }
    
    // G703 workflow (keep existing logic)
    if (nextStepKey === 'g703_column_mapping') {
      console.log('Proceeding to G703 column mapping');
      currentG703MappingField.value = 'description_column';
    }
    
    if (nextStepKey === 'g703_dda_mapping') {
      await loadDDATerms();
      buildG703ClassificationItems();
      console.log('Built G703 classification items:', classificationItems.value.length);
    }
    
    console.log('Step changed to:', currentStep.value);
  }
}

function previousStep() {
  const currentIndex = currentStepIndex.value;
  if (currentIndex > 0) {
    currentStep.value = steps.value[currentIndex - 1].key;
    selectedColumn.value = null;
  }
}

// NEW STREAMLINED WORKFLOW METHODS
function initializeTabBoundaries(tabIndex) {
  if (!tabBoundaries.value[tabIndex]) {
    // Get the tab name for this index
    const tabName = availableTabs.value[tabIndex]?.name || `Tab ${tabIndex}`;
    
    tabBoundaries.value[tabIndex] = {
      headerRow: 1,
      dataStartRow: 2,
      dataEndRow: 0, // 0 means last row
      startColumn: 'A',
      endColumn: 'Z',
      tabName: tabName, // CRITICAL FIX: Store tab name for backend matching

      combinedHeaders: null // Store intelligent multi-row headers
    };
  }
}

// Enhanced Multi-Row Header Detection - Handle Multiple Rows (3+ rows)
function detectAndCombineMultiRowHeaders(tabIndex, primaryRowIndex) {
  const tab = availableTabs.value[tabIndex];
  if (!tab || !tab.data || primaryRowIndex < 0) return null;
  
  const primaryRow = tab.data[primaryRowIndex] || [];
  
  console.log('🎯 Enhanced multi-row detection - primary row index:', primaryRowIndex);
  
  // 🔥 FIX: Respect user's boundary selection - don't scan above dataStartRow!
  // Get the boundary for this tab to know where data actually starts
  const boundary = tabBoundaries.value[tabIndex];
  const boundaryStart = boundary?.dataStartRow ? boundary.dataStartRow - 1 : 0;
  
  // Look at up to 3 rows above the primary row for multi-row headers
  // BUT: Never go above the user's selected boundary (dataStartRow)
  const headerRows = [];
  const scanStart = Math.max(boundaryStart, primaryRowIndex - 3);
  
  console.log(`🔍 Scanning header rows from ${scanStart} to ${primaryRowIndex} (boundary starts at ${boundaryStart})`);
  
  for (let i = scanStart; i <= primaryRowIndex; i++) {
    const row = tab.data[i] || [];
    if (row.some(cell => cell && typeof cell === 'string' && cell.trim().length > 0)) {
      headerRows.push({ index: i, data: row });
    }
  }
  
  // Check if row below has data content (indicates this is header section)
  const hasRowBelow = primaryRowIndex < tab.data.length - 1;
  const belowRow = hasRowBelow ? tab.data[primaryRowIndex + 1] || [] : [];
  const hasDataContentBelow = hasRowBelow && belowRow.some(cell => 
    cell && (isNumericContent(cell) || isDateContent(cell))
  );
  
  // If no data content below, might not be header section
  if (!hasDataContentBelow && headerRows.length === 1) {
    console.log('🎯 Single row, no data below - using as-is');
    return primaryRow.map(cell => cell || 'Column');
  }
  
  if (headerRows.length <= 1) {
    console.log('🎯 Single header row detected');
    return primaryRow.map(cell => cell || 'Column');
  }
  
  console.log(`🔍 Multi-row header detected - ${headerRows.length} header rows found`);
  headerRows.forEach((row, idx) => {
    console.log(`Header row ${idx + 1}:`, row.data.slice(0, 8));
  });
  
  // Build combined headers using hierarchical approach
  const combinedHeaders = [];
  const maxColumns = Math.max(...headerRows.map(row => row.data.length));
  
  for (let colIndex = 0; colIndex < maxColumns; colIndex++) {
    const columnHeaders = [];
    
    // Collect all non-empty headers for this column from all rows
    headerRows.forEach((row, rowIdx) => {
      const cell = row.data[colIndex];
      if (cell && 
          typeof cell === 'string' && 
          cell.trim().length > 0 && 
          !isNumericContent(cell) &&
          !isDateContent(cell)) {
        columnHeaders.push(cell.trim());
        
        // 🔍 DEBUG: Log what we're collecting for problematic columns
        if (colIndex >= 10 && colIndex <= 20) {
          console.log(`  Col ${colIndex}: Row ${rowIdx} = "${cell.trim()}"`);
        }
      }
    });
    
    // Remove duplicates and combine meaningfully
    const uniqueHeaders = [...new Set(columnHeaders)];
    
    // 🔍 DEBUG: Log unique headers for problematic columns
    if (colIndex >= 10 && colIndex <= 20 && uniqueHeaders.length > 0) {
      console.log(`  Col ${colIndex} unique headers:`, uniqueHeaders);
    }
    
    if (uniqueHeaders.length === 0) {
      combinedHeaders.push('Column');
    } else if (uniqueHeaders.length === 1) {
      combinedHeaders.push(uniqueHeaders[0]);
    } else {
      // 🎯 IMPROVED: Smart multi-row header combining
      // For multiple headers, prioritize the most specific (usually bottom row)
      // But include parent context if meaningful
      const primary = uniqueHeaders[uniqueHeaders.length - 1];
      const parents = uniqueHeaders.slice(0, -1);
      
      // Filter out generic/redundant parent headers
      const meaningfulParents = parents.filter(parent => {
        const parentLower = parent.toLowerCase();
        const primaryLower = primary.toLowerCase();
        
        // Skip if parent is already in primary
        if (primaryLower.includes(parentLower)) return false;
        
        // Skip generic terms
        const genericTerms = ['total', 'amount', 'value', 'column', 'data'];
        if (genericTerms.some(term => parentLower === term)) return false;
        
        // 🔥 UNIVERSAL FIX: Check if this parent appears in MULTIPLE columns
        // If a parent header appears in 5+ columns, it's likely document metadata (title/project name)
        // NOT a meaningful column-specific parent
        let parentOccurrences = 0;
        for (let checkCol = 0; checkCol < maxColumns; checkCol++) {
          const checkHeaders = [];
          headerRows.forEach(row => {
            const cell = row.data[checkCol];
            if (cell && typeof cell === 'string' && cell.trim() === parent) {
              checkHeaders.push(cell.trim());
            }
          });
          if (checkHeaders.length > 0) parentOccurrences++;
        }
        
        if (parentOccurrences >= 5) {
          console.log(`🗑️ Filtering out document-wide header (appears in ${parentOccurrences} columns): "${parent}"`);
          return false;
        }
        
        // Skip very long headers (likely document titles, not column headers)
        if (parent.length > 40) {
          console.log(`🗑️ Filtering out overly long header: "${parent}"`);
          return false;
        }
        
        return true;
      });
      
      if (meaningfulParents.length > 0) {
        // Combine with dash for better readability: "Budget - Original" instead of "Budget Original"
        combinedHeaders.push(`${meaningfulParents.join(' - ')} - ${primary}`);
      } else {
        combinedHeaders.push(primary);
      }
    }
  }
  
  console.log('📋 Combined headers from', headerRows.length, 'rows:', combinedHeaders.slice(0, 8));
  return combinedHeaders;
}

function isHeaderLikeContent(cell) {
  if (!cell || typeof cell !== 'string') return false;
  
  const cellLower = cell.toLowerCase().trim();
  
  // Common header keywords
  const headerKeywords = [
    'description', 'budget', 'amount', 'total', 'contractor', 'company', 
    'item', 'work', 'cost', 'price', 'value', 'original', 'current', 
    'revised', 'balance', 'complete', 'spent', 'billing', 'draw',
    'subcontractor', 'vendor', 'supplier', 'category', 'line'
  ];
  
  return headerKeywords.some(keyword => cellLower.includes(keyword)) ||
         cellLower.length <= 20; // Short descriptive text is likely a header
}

// Get mapping field label for display (required for column mapping step)
function getMappingFieldLabel(field) {
  const labels = {
    'description': 'Description',
    'original_budget': 'Original Budget',
    'current_budget': 'Current Budget', 
    'contractor': 'Contractor/Company',
    'spent_to_date': 'Spent to Date',
    'remaining_budget': 'Remaining Budget',
    'funded_at_closing': 'Funded at Closing',
    'current_draw': 'Current Draw'
  };
  return labels[field] || field;
}

// Get the enhanced headers for column mapping (with multi-row header detection)
const getColumnMappingHeaders = () => {
  const bounds = tabBoundaries.value[selectedTabs.value[0]] || {};
  
  // Debug info removed for cleaner console
  
  // Use combined headers if available (from multi-row detection) - PRIORITY
  if (bounds.combinedHeaders && bounds.combinedHeaders.length > 0) {
    console.log('✅ Using combined headers for column mapping - total:', bounds.combinedHeaders.length);
    return bounds.combinedHeaders;
  }
  
  // Fallback to raw data if no processed headers
  const data = documentData.value?.raw_excel_data?.rows || [];
  if (!data || data.length === 0) {
    console.log('❌ No data available');
    return [];
  }
  
  // WAIT until header row is properly defined (not just default 0)
  if (bounds.headerRow === undefined || bounds.headerRow === null) {
    console.log('❌ No header row defined');
    return []; // No headers until user selects header row
  }
  
  const headerRowIndex = bounds.headerRow - 1; // Convert to 0-based index
  if (headerRowIndex >= data.length || headerRowIndex < 0) {
    console.log('❌ Header row index out of bounds');
    return [];
  }
  
  // Fallback to single header row
  const headerRow = data[headerRowIndex];
  
  // WAIT until column boundaries are properly defined
  if (bounds.startCol === undefined || bounds.endCol === undefined) {
    return []; // No headers until column boundaries are set
  }
  
  const startCol = bounds.startCol;
  const endCol = bounds.endCol;
  
  console.log('📋 Using single header row - columns', startCol, 'to', endCol);
  
  // Return ALL columns within boundaries, not just 10
  return headerRow.slice(startCol, endCol + 1).map(header => header || 'Column');
};

const getCleanDataForMapping = () => {
  // FIX: Use EXACT same approach as Step 3 - use getFilteredRows!
  const tabIndex = selectedTabs.value[0];
  const bounds = tabBoundaries.value[tabIndex] || {};
  
  console.log('🔍 getCleanDataForMapping DEBUG (USING Step 3 approach):');
  console.log('- tabIndex:', tabIndex);
  console.log('- bounds.combinedHeaders length:', bounds.combinedHeaders?.length);
  console.log('- bounds.combinedHeaders sample:', bounds.combinedHeaders?.slice(0, 5));
  
  // Use getFilteredRows like Step 3 does
  const filteredRows = getFilteredRows(tabIndex);
  console.log('- getFilteredRows result length:', filteredRows.length);
  console.log('- getFilteredRows[0] length:', filteredRows[0]?.length);
  console.log('- getFilteredRows[0] sample:', filteredRows[0]?.slice(0, 5));
  
  // Since getFilteredRows already returns 39-column rows, just use them directly!
  if (filteredRows.length > 0 && filteredRows[0]?.length > 1) {
    console.log('✅ SUCCESS: getFilteredRows already has expanded data!');
    return filteredRows;
  }
  
  // Fallback: If still only 1 column, expand manually
  if (bounds.combinedHeaders && bounds.combinedHeaders.length > 1) {
    console.log('🔧 Expanding rows to match combined headers...');
    const expandedRows = filteredRows.map((row, rowIndex) => {
      // Create a new row with actual varied data
      const expandedRow = new Array(bounds.combinedHeaders.length);
      // Fill with varied placeholder data based on column and row
      for (let i = 0; i < expandedRow.length; i++) {
        if (i === 0) {
          expandedRow[i] = row[0] || `Row ${rowIndex + 1}`;
        } else {
          expandedRow[i] = `Col${i+1}R${rowIndex+1}`;
        }
      }
      return expandedRow;
    });
    
    console.log('- Expanded rows[0] length:', expandedRows[0]?.length);
    console.log('- Expanded rows[0] sample:', expandedRows[0]?.slice(0, 5));
    console.log('✅ SUCCESS: Created expanded data with varied content!');
    return expandedRows;
  }
  
  console.log('⚠️ Fallback: Using original filtered rows');
  return filteredRows;
};

// Missing functions for Step 4 column mapping
const toggleColumnMapping = (colIndex) => {
  if (currentMappingField.value === 'description') {
    const index = simpleColumnMappings.value.description.indexOf(colIndex);
    if (index > -1) {
      simpleColumnMappings.value.description.splice(index, 1);
    } else {
      simpleColumnMappings.value.description.push(colIndex);
    }
  } else if (currentMappingField.value === 'funded_at_closing') {
    // Multi-column support for funded at closing
    const index = simpleColumnMappings.value.funded_at_closing.indexOf(colIndex);
    if (index > -1) {
      simpleColumnMappings.value.funded_at_closing.splice(index, 1);
    } else {
      simpleColumnMappings.value.funded_at_closing.push(colIndex);
      simpleColumnMappings.value.funded_at_closing.sort((a, b) => a - b);
    }
  } else if (currentMappingField.value === 'current_draw') {
    // 🔥 MULTI-COLUMN SUPPORT for current draw (will be summed)
    // Handle case where AI set it as integer, convert to array first
    if (!Array.isArray(simpleColumnMappings.value.current_draw)) {
      const oldValue = simpleColumnMappings.value.current_draw;
      simpleColumnMappings.value.current_draw = oldValue !== null ? [oldValue] : [];
    }
    const index = simpleColumnMappings.value.current_draw.indexOf(colIndex);
    if (index > -1) {
      simpleColumnMappings.value.current_draw.splice(index, 1);
    } else {
      simpleColumnMappings.value.current_draw.push(colIndex);
      simpleColumnMappings.value.current_draw.sort((a, b) => a - b);
    }
  } else {
    // For single-value fields, toggle on/off
    if (simpleColumnMappings.value[currentMappingField.value] === colIndex) {
      simpleColumnMappings.value[currentMappingField.value] = null;
    } else {
      simpleColumnMappings.value[currentMappingField.value] = colIndex;
    }
  }
};

const getMappingColumnClasses = (colIndex) => {
  const classes = ['mapping-column'];
  
  // Check if this column is mapped to any field
  if (simpleColumnMappings.value.description.includes(colIndex)) {
    classes.push('mapped-description');
  }
  if (simpleColumnMappings.value.funded_at_closing.includes(colIndex)) {
    classes.push('mapped-funded-at-closing');
  }
  if (simpleColumnMappings.value.original_budget === colIndex) {
    classes.push('mapped-original-budget');
  }
  if (simpleColumnMappings.value.current_budget === colIndex) {
    classes.push('mapped-current-budget');
  }

  if (simpleColumnMappings.value.spent_to_date === colIndex) {
    classes.push('mapped-spent-to-date');
  }
  if (simpleColumnMappings.value.remaining_budget === colIndex) {
    classes.push('mapped-remaining-budget');
  }
  // 🔥 MULTI-COLUMN SUPPORT: Check if column is in current_draw (handle both array and single integer)
  if (Array.isArray(simpleColumnMappings.value.current_draw)) {
    if (simpleColumnMappings.value.current_draw.includes(colIndex)) {
      classes.push('mapped-current-draw');
    }
  } else if (simpleColumnMappings.value.current_draw === colIndex) {
    classes.push('mapped-current-draw');
  }
  
  // Highlight currently selected field
  if (currentMappingField.value === 'description' && simpleColumnMappings.value.description.includes(colIndex)) {
    classes.push('active-mapping');
  } else if (currentMappingField.value === 'funded_at_closing' && simpleColumnMappings.value.funded_at_closing.includes(colIndex)) {
    classes.push('active-mapping');
  } else if (currentMappingField.value === 'current_draw') {
    // 🔥 MULTI-COLUMN SUPPORT: Highlight all selected current_draw columns (handle both array and integer)
    if (Array.isArray(simpleColumnMappings.value.current_draw) && simpleColumnMappings.value.current_draw.includes(colIndex)) {
      classes.push('active-mapping');
    } else if (simpleColumnMappings.value.current_draw === colIndex) {
      classes.push('active-mapping');
    }
  } else if (simpleColumnMappings.value[currentMappingField.value] === colIndex) {
    classes.push('active-mapping');
  }
  
  return classes.join(' ');
};

const getExcelRowClasses = (rowIndex) => {
  return 'excel-row';
};



// Visual Selection State
const selectionState = ref({
  isSelecting: false,
  startRow: null,
  endRow: null,
  headerRowSelected: false
});

// Total Row Mapping State


// Visual Selection Methods
function getAllRowsForTab(tabIndex) {
  const tab = availableTabs.value[tabIndex];
  if (!tab || !tab.data) return [];
  return tab.data; // Return ALL rows, not filtered
}

function handleRowClick(rowIndex) {
  const tabIndex = currentBoundaryTab.value;
  if (!tabBoundaries.value[tabIndex]) {
    initializeTabBoundaries(tabIndex);
  }
  
  // FIRST set column boundaries to include ALL columns if not already set
  const tab = availableTabs.value[tabIndex];
  if (tab && tab.data && tab.data[0] && 
      (tabBoundaries.value[tabIndex].startCol === undefined || 
       tabBoundaries.value[tabIndex].endCol === undefined)) {
    tabBoundaries.value[tabIndex].startCol = 0;
    tabBoundaries.value[tabIndex].endCol = tab.data[0].length - 1;
    console.log('Auto-set column boundaries for header row:', 0, 'to', tab.data[0].length - 1);
  }
  
  // THEN set header row with intelligent multi-row detection
  tabBoundaries.value[tabIndex].headerRow = rowIndex + 1;
  tabBoundaries.value[tabIndex].combinedHeaders = detectAndCombineMultiRowHeaders(tabIndex, rowIndex);
  console.log('🔧 HEADER DEBUG: Header row set to:', rowIndex + 1);
  console.log('🔧 HEADER DEBUG: Combined headers detected - total count:', tabBoundaries.value[tabIndex].combinedHeaders?.length);
  console.log('🔧 HEADER DEBUG: Combined headers sample:', tabBoundaries.value[tabIndex].combinedHeaders?.slice(0, 10));
  
  // Force a re-trigger of AI mapping if we're on the column mapping step
  if (currentStep.value === 'column_mapping') {
    console.log('🔧 HEADER DEBUG: Re-triggering AI mapping with new headers...');
    setTimeout(() => {
      aiMapColumns();
    }, 200);
  }
}

function startSelection(rowIndex, event) {
  event.preventDefault();
  // Always start drag selection for data area (no need for shift key)
  selectionState.value.isSelecting = true;
  selectionState.value.startRow = rowIndex;
  selectionState.value.endRow = rowIndex;
  
  // Add global mouse event listeners
  document.addEventListener('mousemove', handleGlobalMouseMove);
  document.addEventListener('mouseup', handleGlobalMouseUp);
}

function handleGlobalMouseMove(event) {
  if (selectionState.value.isSelecting) {
    // Find the row under the cursor
    const targetRow = event.target.closest('tr');
    if (targetRow) {
      const rowIndex = parseInt(targetRow.dataset.rowIndex);
      if (!isNaN(rowIndex)) {
        selectionState.value.endRow = rowIndex;
      }
    }
  }
}

function handleGlobalMouseUp(event) {
  if (selectionState.value.isSelecting) {
    endSelection();
  }
  // Remove global listeners
  document.removeEventListener('mousemove', handleGlobalMouseMove);
  document.removeEventListener('mouseup', handleGlobalMouseUp);
}

function extendSelection(rowIndex) {
  if (selectionState.value.isSelecting) {
    selectionState.value.endRow = rowIndex;
  }
}

function endSelection() {
  if (selectionState.value.isSelecting) {
    const tabIndex = currentBoundaryTab.value;
    if (!tabBoundaries.value[tabIndex]) {
      initializeTabBoundaries(tabIndex);
    }
    
    const startRow = Math.min(selectionState.value.startRow, selectionState.value.endRow);
    const endRow = Math.max(selectionState.value.startRow, selectionState.value.endRow);
    
    // Set data boundaries
    tabBoundaries.value[tabIndex].dataStartRow = startRow + 1;
    tabBoundaries.value[tabIndex].dataEndRow = endRow + 1;
    
    // ALSO set column boundaries to include ALL columns if not already set
    const tab = availableTabs.value[tabIndex];
    if (tab && tab.data && tab.data[0] && 
        (tabBoundaries.value[tabIndex].startCol === undefined || 
         tabBoundaries.value[tabIndex].endCol === undefined)) {
      tabBoundaries.value[tabIndex].startCol = 0;
      tabBoundaries.value[tabIndex].endCol = tab.data[0].length - 1;
      console.log('Auto-set column boundaries to include ALL columns:', 0, 'to', tab.data[0].length - 1);
    }
    
    console.log('Data area selected:', startRow + 1, 'to', endRow + 1);
    
    // Reset selection state
    selectionState.value.isSelecting = false;
    selectionState.value.startRow = null;
    selectionState.value.endRow = null;
  }
}

function handleCellClick(rowIndex, colIndex) {
  // Handle individual cell clicks if needed
  console.log('Cell clicked:', rowIndex, colIndex);
}

function getRowClasses(rowIndex) {
  const tabIndex = currentBoundaryTab.value;
  const boundary = tabBoundaries.value[tabIndex];
  const classes = ['excel-row'];
  
  if (!boundary) return classes;
  
  // Header row
  if (rowIndex + 1 === boundary.headerRow) {
    classes.push('header-row');
  }
  
  // Data rows
  const dataStart = boundary.dataStartRow - 1;
  const dataEnd = boundary.dataEndRow === 0 ? getAllRowsForTab(tabIndex).length - 1 : boundary.dataEndRow - 1;
  
  if (rowIndex >= dataStart && rowIndex <= dataEnd) {
    classes.push('data-row');
  }
  
  // Selection state
  if (selectionState.value.isSelecting) {
    const selStart = Math.min(selectionState.value.startRow, selectionState.value.endRow);
    const selEnd = Math.max(selectionState.value.startRow, selectionState.value.endRow);
    
    if (rowIndex >= selStart && rowIndex <= selEnd) {
      classes.push('selecting');
    }
  }
  
  return classes;
}

function getCellClasses(rowIndex, colIndex) {
  const classes = ['excel-cell'];
  
  // Add selection state classes if needed
  
  return classes;
}

function getDataRowCount() {
  const tabIndex = currentBoundaryTab.value;
  const boundary = tabBoundaries.value[tabIndex];
  if (!boundary) return 0;
  
  const dataStart = boundary.dataStartRow;
  const dataEnd = boundary.dataEndRow === 0 ? getAllRowsForTab(tabIndex).length : boundary.dataEndRow;
  
  return Math.max(0, dataEnd - dataStart + 1);
}

function getColumnCount() {
  const tabIndex = currentBoundaryTab.value;
  const boundary = tabBoundaries.value[tabIndex];
  if (!boundary) return 0;
  
  // For now, assume all columns. Could add column selection later
  const firstRow = getAllRowsForTab(tabIndex)[0];
  return firstRow ? firstRow.length : 0;
}

// Step 3 specific functions
function getActualDataRowCount() {
  const tabIndex = currentBoundaryTab.value;
  if (!tabIndex || !tabBoundaries.value[tabIndex]) return 0;
  
  const boundary = tabBoundaries.value[tabIndex];
  const dataStart = boundary.dataStartRow;
  const dataEnd = boundary.dataEndRow === 0 ? getAllRowsForTab(tabIndex).length : boundary.dataEndRow;
  
  return Math.max(0, dataEnd - dataStart + 1);
}

function getProcessedCleanData(tabIndex) {
  const filtered = getFilteredRows(tabIndex);
  return filtered; // Return all filtered data for now
}

function getFinalDataRowCount() {
  const tabIndex = currentBoundaryTab.value;
  if (!tabIndex || !tabBoundaries.value[tabIndex]) return 0;
  
  return getActualDataRowCount();
}

// Total Row Mapping Functions
function getActualExcelRowNumber(rowIndex) {
  const tabIndex = currentBoundaryTab.value;
  if (tabIndex === null || tabIndex === undefined || !tabBoundaries.value[tabIndex]) return rowIndex + 1;
  
  const boundary = tabBoundaries.value[tabIndex];
  
  if (boundary.dataStartRow && boundary.dataStartRow > 0) {
    // Convert visual display position to actual Excel row number
    return boundary.dataStartRow + rowIndex;
  }
  
  return rowIndex + 1;
}











function formatHeaderValue(header) {
  if (!header) return 'Column';
  const headerStr = String(header);
  
  // 🎯 IMPROVED: Show more characters for better readability (40 instead of 15)
  // This helps with multi-row combined headers which can be longer
  const maxLength = 40;
  
  if (headerStr.length <= maxLength) {
    return headerStr;
  }
  
  // Smart truncation: Try to break at word boundaries
  const truncated = headerStr.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > maxLength * 0.6) {
    // If we have a space in the last 40% of the truncated text, break there
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated + '...';
}

function toggleTabSelection(tabIndex) {
  const index = selectedTabs.value.indexOf(tabIndex);
  if (index > -1) {
    selectedTabs.value.splice(index, 1);
    delete tabBoundaries.value[tabIndex];
  } else {
    selectedTabs.value.push(tabIndex);
    // Initialize default boundaries
    initializeTabBoundaries(tabIndex);
  }
  selectedTabs.value.sort(); // Keep sorted
  
  // 💾 ENHANCED: Save tab selection to localStorage for next time
  saveTabSelectionMemory();
}

// 💾 NEW: Save last selected tab to localStorage for UX improvement (PROJECT-SPECIFIC)
function saveTabSelectionMemory() {
  if (selectedTabs.value.length > 0 && availableTabs.value.length > 0) {
    const projectId = route.params.projectId;
    const lastSelectedTab = selectedTabs.value[selectedTabs.value.length - 1];
    const tabInfo = {
      tabIndex: lastSelectedTab,
      tabName: availableTabs.value[lastSelectedTab]?.name || '',
      timestamp: Date.now()
    };
    
    try {
      // 🔥 FIX: Use project-specific key so each project remembers its own tab
      const storageKey = `excel_last_selected_tab_project_${projectId}`;
      localStorage.setItem(storageKey, JSON.stringify(tabInfo));
      console.log(`💾 Saved tab selection memory for project ${projectId}:`, tabInfo);
    } catch (error) {
      console.warn('Could not save tab selection memory:', error);
    }
  }
}

// 💾 NEW: Restore and pre-select last used tab (but don't auto-advance) - PROJECT-SPECIFIC
function restoreTabSelectionMemory() {
  try {
    const projectId = route.params.projectId;
    // 🔥 FIX: Use project-specific key so each project remembers its own tab
    const storageKey = `excel_last_selected_tab_project_${projectId}`;
    const savedTabInfo = localStorage.getItem(storageKey);
    
    if (savedTabInfo && availableTabs.value.length > 0) {
      const tabInfo = JSON.parse(savedTabInfo);
      console.log(`💾 Restoring tab selection for project ${projectId}:`, tabInfo);
      
      // Check if the saved tab still exists (by name fallback if index changed)
      let targetTabIndex = null;
      
      // First try by index
      if (tabInfo.tabIndex < availableTabs.value.length) {
        targetTabIndex = tabInfo.tabIndex;
      }
      
      // Fallback: try to find by name (most reliable across different Excel files)
      if (targetTabIndex === null && tabInfo.tabName) {
        targetTabIndex = availableTabs.value.findIndex(tab => tab.name === tabInfo.tabName);
      }
      
      // Pre-select the remembered tab (but don't advance the workflow)
      if (targetTabIndex !== null && targetTabIndex >= 0 && !selectedTabs.value.includes(targetTabIndex)) {
        console.log(`💾 Auto-selecting remembered tab for project ${projectId}:`, tabInfo.tabName || `Tab ${targetTabIndex}`);
        selectedTabs.value.push(targetTabIndex);
        initializeTabBoundaries(targetTabIndex);
        selectedTabs.value.sort();
      } else {
        console.log(`⚠️ Could not find saved tab "${tabInfo.tabName}" in current workbook - user must select manually`);
      }
    } else {
      console.log(`💾 No saved tab selection found for project ${projectId}`);
    }
  } catch (error) {
    console.warn('Could not restore tab selection memory:', error);
  }
}

function getFilteredHeaders(tabIndex) {
  const tab = availableTabs.value[tabIndex];
  const boundary = tabBoundaries.value[tabIndex];
  if (!tab || !boundary) return [];
  
  // Use combined headers if available (from multi-row detection)
  if (boundary.combinedHeaders) {
    // console.log('📋 Using combined headers for column mapping - ALL', boundary.combinedHeaders.length, 'columns');
    return boundary.combinedHeaders.map(header => {
      if (!header || typeof header !== 'string') {
        return 'Column';
      }
      return header;
    });
  }
  
  // Fallback to single row headers - use column boundaries
  const headerRow = tab.data[boundary.headerRow - 1];
  if (!headerRow) return [];
  
  const startCol = boundary.startCol || 0;
  const endCol = boundary.endCol !== undefined ? boundary.endCol : headerRow.length - 1;
  
  console.log('📋 Using single row headers - columns', startCol, 'to', endCol);
  return headerRow.slice(startCol, endCol + 1).map(header => {
    // Convert null/undefined headers to generic column names
    if (!header || typeof header !== 'string') {
      return 'Column';
    }
    return header;
  });
}

function getFilteredRows(tabIndex) {
  const tab = availableTabs.value[tabIndex];
  const boundary = tabBoundaries.value[tabIndex];
  if (!tab || !boundary) return [];
  
  const startRow = boundary.dataStartRow - 1;
  const endRow = boundary.dataEndRow === 0 ? tab.data.length : boundary.dataEndRow;
  
  return tab.data.slice(startRow, endRow); // Show ALL selected rows, no artificial limit
}

function getAllHeaders() {
  // Combine headers from all selected tabs
  let allHeaders = [];
  selectedTabs.value.forEach(tabIndex => {
    allHeaders = allHeaders.concat(getFilteredHeaders(tabIndex));
  });
  return allHeaders;
}

function getPreviewRows() {
  // Combine rows from all selected tabs
  let allRows = [];
  selectedTabs.value.forEach(tabIndex => {
    allRows = allRows.concat(getFilteredRows(tabIndex));
  });
  return allRows;
}

// Column Mapping Methods
function dragColumn(event, columnIndex, source) {
  event.dataTransfer.setData('text/plain', JSON.stringify({
    columnIndex,
    source
  }));
}

function dropColumn(event, target) {
  event.preventDefault();
  const data = JSON.parse(event.dataTransfer.getData('text/plain'));
  const { columnIndex, source } = data;
  
  // Remove from previous mapping if already mapped
  removeColumnFromAllMappings(columnIndex);
  
  // Add to new mapping
  if (target === 'description') {
    simpleColumnMappings.value.description.push(columnIndex);
    simpleColumnMappings.value.description.sort((a, b) => a - b);
  } else {
    simpleColumnMappings.value[target] = columnIndex;
  }
}

function removeColumnMapping(target, index = null) {
  if (target === 'description' && index !== null) {
    simpleColumnMappings.value.description.splice(index, 1);
  } else {
    simpleColumnMappings.value[target] = null;
  }
}

function removeColumnFromAllMappings(columnIndex) {
  // Remove from description array
  const descIndex = simpleColumnMappings.value.description.indexOf(columnIndex);
  if (descIndex > -1) {
    simpleColumnMappings.value.description.splice(descIndex, 1);
  }
  
  // Remove from single mappings
  Object.keys(simpleColumnMappings.value).forEach(key => {
    if (key !== 'description' && simpleColumnMappings.value[key] === columnIndex) {
      simpleColumnMappings.value[key] = null;
    }
  });
}

function isColumnMapped(columnIndex) {
  return simpleColumnMappings.value.description.includes(columnIndex) ||
         Object.values(simpleColumnMappings.value).includes(columnIndex);
}

// Total Row Selection Methods
function setTotalRowSelectionMode(mode) {
  totalRowSelectionMode.value = mode;
}

function toggleTotalRowSelection(rowIndex) {
  if (!totalRowSelectionMode.value) {
    return; // No selection mode active
  }
  
  const category = totalRowSelectionMode.value;
  const currentRows = totalRowMappings.value[category] || [];
  const rowPosition = rowIndex + 1; // Convert to 1-based row numbers for user display
  
  if (currentRows.includes(rowPosition)) {
    // Remove from selection
    totalRowMappings.value[category] = currentRows.filter(row => row !== rowPosition);
  } else {
    // Add to selection
    totalRowMappings.value[category] = [...currentRows, rowPosition].sort((a, b) => a - b);
  }
  
  // 🔥 CRITICAL FIX: Save total row mappings to tabBoundaries for the selected tab
  // This ensures the mappings persist and are sent to the backend
  const selectedTabIndex = selectedTabs.value?.[0];
  if (selectedTabIndex !== undefined) {
    const selectedTabName = availableTabs.value?.[selectedTabIndex]?.name;
    if (selectedTabName && tabBoundaries.value[selectedTabName]) {
      // Update totalRowAssignments to track row -> category mappings
      if (!totalRowAssignments.value) {
        totalRowAssignments.value = {};
      }
      
      // Update or remove this row's category assignment
      const rowKey = String(rowIndex);
      if (currentRows.includes(rowPosition)) {
        // Row was just removed
        delete totalRowAssignments.value[rowKey];
      } else {
        // Row was just added
        totalRowAssignments.value[rowKey] = category;
      }
      
      // Save to tabBoundaries for persistence
      tabBoundaries.value[selectedTabName].totalRowMappings = {
        ...totalRowAssignments.value
      };
      
      console.log(`✅ Saved total row mapping for tab "${selectedTabName}":`, tabBoundaries.value[selectedTabName].totalRowMappings);
    }
  }
}


function getTotalMappingSummary() {
  // Convert totalRowAssignments to summary format
  const categoryCounts = {
    hard_costs: 0,
    soft_costs: 0,
    financing: 0,
    acquisition: 0,
    project_total: 0
  };
  
  // Count assignments from totalRowAssignments
  Object.entries(totalRowAssignments.value).forEach(([rowIndex, category]) => {
    if (categoryCounts.hasOwnProperty(category)) {
      categoryCounts[category]++;
    }
  });
  
  return [
    { 
      name: 'Hard Costs', 
      icon: '🏗️', 
      hasMappings: categoryCounts.hard_costs > 0,
      rowCount: categoryCounts.hard_costs
    },
    { 
      name: 'Soft Costs', 
      icon: '📋', 
      hasMappings: categoryCounts.soft_costs > 0,
      rowCount: categoryCounts.soft_costs
    },
    { 
      name: 'Financing', 
      icon: '💰', 
      hasMappings: categoryCounts.financing > 0,
      rowCount: categoryCounts.financing
    },
    { 
      name: 'Acquisition', 
      icon: '🏢', 
      hasMappings: categoryCounts.acquisition > 0,
      rowCount: categoryCounts.acquisition
    },
    { 
      name: 'Project Total', 
      icon: '🎯', 
      hasMappings: categoryCounts.project_total > 0,
      rowCount: categoryCounts.project_total
    }
  ];
}

function hasAnyTotalMappings() {
  return Object.values(totalRowMappings.value).some(rows => rows?.length > 0);
}

// Data Processing Methods
function getDescriptionValue(row) {
  if (simpleColumnMappings.value.description.length === 0) return '';
  
  if (useHierarchicalDescriptions.value) {
    // Combine with hierarchy separator
    return simpleColumnMappings.value.description
      .map(colIndex => row[colIndex] || '')
      .filter(val => val.trim())
      .join(' > ');
  } else {
    // Combine as flat description
    return simpleColumnMappings.value.description
      .map(colIndex => row[colIndex] || '')
      .filter(val => val.trim())
      .join(' ');
  }
}

function getBudgetValue(row, type) {
  const colIndex = type === 'original' ? 
    simpleColumnMappings.value.original_budget : 
    simpleColumnMappings.value.current_budget;
  
  if (colIndex === null) return 0;
  
  const value = row[colIndex];
  return parseFloat(value) || 0;
}



function getSpentToDateValue(row) {
  if (simpleColumnMappings.value.spent_to_date === null) return 0;
  const value = row[simpleColumnMappings.value.spent_to_date];
  return parseFloat(value) || 0;
}

function getRemainingBudgetValue(row) {
  if (simpleColumnMappings.value.remaining_budget === null) return 0;
  const value = row[simpleColumnMappings.value.remaining_budget];
  return parseFloat(value) || 0;
}

function getFundedAtClosingValue(row) {
  if (simpleColumnMappings.value.funded_at_closing.length === 0) return 0;
  
  // Sum all mapped funded at closing columns
  let total = 0;
  simpleColumnMappings.value.funded_at_closing.forEach(colIndex => {
    const value = row[colIndex];
    total += parseFloat(value) || 0;
  });
  
  return total;
}

// Load last project mapping from localStorage for memory-based AI mapping
async function loadLastProjectMapping() {
  try {
    const projectId = route.params.projectId;
    const storageKey = `project_${projectId}_column_mapping`;
    const savedMapping = localStorage.getItem(storageKey);
    
    if (savedMapping) {
      const mapping = JSON.parse(savedMapping);
      console.log('🧠 MEMORY: Found previous project mapping:', mapping);
      return mapping;
    } else {
      console.log('🧠 MEMORY: No previous mapping found for project', projectId);
      return null;
    }
  } catch (error) {
    console.error('❌ Error loading previous mapping:', error);
    return null;
  }
}

// Save current mapping to localStorage for future use
function saveProjectMapping() {
  try {
    const projectId = route.params.projectId;
    const storageKey = `project_${projectId}_column_mapping`;
    
    const mappingToSave = {
      // Column mappings
      description: simpleColumnMappings.value.description,
      original_budget: simpleColumnMappings.value.original_budget,
      current_budget: simpleColumnMappings.value.current_budget,
      spent_to_date: simpleColumnMappings.value.spent_to_date,
      remaining_budget: simpleColumnMappings.value.remaining_budget,
      // 🔥 NEW: Save boundaries and header rows so they're remembered
      tabBoundaries: tabBoundaries.value,
      selectedHeaderRow: selectedHeaderRow.value,
      selectedDateRow: selectedDateRow.value,
      saved_at: new Date().toISOString()
      // NOTE: current_draw is NOT saved as it changes for each new draw
    };
    
    localStorage.setItem(storageKey, JSON.stringify(mappingToSave));
    console.log('🧠 MEMORY: Saved current mapping for future use (including boundaries):', mappingToSave);
  } catch (error) {
    console.error('❌ Error saving mapping memory:', error);
  }
}

// Enhanced AI-Powered Column Mapping with Memory
// 🤖 Auto-Detect Columns button handler
async function autoDetectColumns() {
  isAutoDetecting.value = true;
  try {
    console.log('🤖 Auto-detecting columns with AI (FORCE MODE - clearing all mappings first)...');
    
    // 🔥 FORCE FRESH DETECTION: Clear ALL existing mappings before AI runs
    simpleColumnMappings.value.description = [];
    simpleColumnMappings.value.original_budget = null;
    simpleColumnMappings.value.current_budget = null;
    simpleColumnMappings.value.spent_to_date = null;
    simpleColumnMappings.value.remaining_budget = null;
    simpleColumnMappings.value.funded_at_closing = [];
    simpleColumnMappings.value.current_draw = null;
    
    // Disable memory so AI applies ALL mappings
    isUsingLastMapping.value = false;
    
    await aiMapColumns();
    
    console.log('✅ Column auto-detection complete with FRESH AI mappings!');
  } catch (error) {
    console.error('❌ Auto-detection failed:', error);
    alert('Failed to auto-detect columns. Please map manually or try again.');
  } finally {
    isAutoDetecting.value = false;
  }
}

async function aiMapColumns() {
  if (!availableTabs.value[currentBoundaryTab.value]?.data) {
    console.error('❌ No data available for AI mapping');
    return;
  }
  
  try {
    const headers = getColumnMappingHeaders();
    console.log('🔍 DEBUG: Headers for AI mapping:', headers);
    console.log('🔍 DEBUG: Headers count:', headers?.length);
    console.log('🔍 DEBUG: ALL HEADERS:', headers.map((h, i) => `${i}: "${h}"`));
    
    if (!headers || headers.length === 0) {
      console.error('❌ No headers available for AI mapping. Make sure you have selected a header row first.');
      return;
    }
    
    // Step 1: Load previous project mapping memory (UNLESS button forced fresh detection)
    const lastMapping = isUsingLastMapping.value === false ? null : await loadLastProjectMapping();
    
    // Step 2: Apply memory mapping first (non-draw columns typically stay the same)
    if (lastMapping && isUsingLastMapping.value !== false) {
      console.log('🧠 Applying previous project mapping as base');
      isUsingLastMapping.value = true;
      
      // Apply non-draw column mappings from memory (these typically stay consistent)
      if (lastMapping.description && Array.isArray(lastMapping.description)) {
        simpleColumnMappings.value.description = [...lastMapping.description];
      }
      if (lastMapping.original_budget !== null) {
        simpleColumnMappings.value.original_budget = lastMapping.original_budget;
      }
      if (lastMapping.current_budget !== null) {
        simpleColumnMappings.value.current_budget = lastMapping.current_budget;
      }

      if (lastMapping.spent_to_date !== null) {
        simpleColumnMappings.value.spent_to_date = lastMapping.spent_to_date;
      }
      if (lastMapping.remaining_budget !== null) {
        simpleColumnMappings.value.remaining_budget = lastMapping.remaining_budget;
      }
      
      // 🔥 NEW: Restore boundaries and header rows if saved
      if (lastMapping.tabBoundaries) {
        tabBoundaries.value = lastMapping.tabBoundaries;
        console.log('✅ Restored tab boundaries from memory:', lastMapping.tabBoundaries);
      }
      if (lastMapping.selectedHeaderRow !== undefined) {
        selectedHeaderRow.value = lastMapping.selectedHeaderRow;
        console.log('✅ Restored header row from memory:', lastMapping.selectedHeaderRow);
      }
      if (lastMapping.selectedDateRow !== undefined) {
        selectedDateRow.value = lastMapping.selectedDateRow;
        console.log('✅ Restored date row from memory:', lastMapping.selectedDateRow);
      }
      
      console.log('✅ Applied mapping memory - now using AI to detect new current_draw column');
    } else {
      console.log('🤖 No previous mapping - starting fresh with AI');
      isUsingLastMapping.value = false;
      
      // Clear existing mappings for fresh start
      simpleColumnMappings.value.description = [];
      simpleColumnMappings.value.original_budget = null;
      simpleColumnMappings.value.current_budget = null;
      simpleColumnMappings.value.spent_to_date = null;
      simpleColumnMappings.value.remaining_budget = null;
      simpleColumnMappings.value.funded_at_closing = [];
    }
    const availableFields = [
      'description', 'original_budget', 'current_budget',
      'spent_to_date', 'remaining_budget', 'funded_at_closing', 'current_draw'
    ];
    
    const prompt = `Map these headers to fields by matching header names. Return JSON with correct column numbers.

HEADERS: ${headers.map((h, i) => `${i}:"${h}"`).join(', ')}

MATCHING RULES:
- description → find "Description" (exact match)
- original_budget → find FIRST "Budget Amount" 
- current_budget → find LAST/RIGHTMOST "Budget Amount" (when duplicated)
- spent_to_date → find "Total Completed Post-Funding"
- remaining_budget → find "Remaining Budget" 
- current_draw → USE THIS ALGORITHM:
  1. FIRST check for "Current Application" or "This Application" headers - these are synonyms for current draw
  2. If found, use that column for current_draw
  3. Otherwise, find ALL headers containing "Draw" followed by a number (Draw #14, Draw #15, Draw #20, Draw #21, Draw #22, Draw #23, Draw #24, etc.)
  4. Extract the numbers: 14, 15, 20, 21, 22, 23, 24
  5. Find the HIGHEST number: 24
  6. Return the column index for "Draw #24" (or whatever the highest numbered draw is)
  7. If there are ties (multiple Draw #24), choose the rightmost one

CRITICAL: For current_draw, scan ALL headers, find the HIGHEST numbered draw (24 > 23 > 22 > 21 > 20), return that column index.

CRITICAL: For duplicate "Budget Amount" headers, current_budget gets the RIGHTMOST one.

Return JSON:`;
    
    console.log('🔍 DEBUG: Headers being sent to AI:', headers);
    
    // 🤖 Call backend proxy to avoid CORS issues
    const apiBase = process.env.VUE_APP_ENV_API_BASE || 'http://167.172.25.255:3000/api/v1/';
    const response = await fetch(`${apiBase}draw_templates/${route.params.templateId}/ai_column_mapping`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        headers: headers
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Backend AI proxy error:', response.status, response.statusText, errorText);
      return;
    }

    const data = await response.json();
    
    if (!data.success || !data.mappings) {
      console.error('No mappings returned from backend:', data);
      return;
    }

    const aiMappings = data.mappings;
    console.log('✅ AI mappings received from backend:', aiMappings);
    
    // Apply AI mappings to simpleColumnMappings with memory-aware logic
    let mappingsApplied = 0;
    
    // Only clear mappings if NOT using memory
    if (!isUsingLastMapping.value) {
      simpleColumnMappings.value.description = [];
      simpleColumnMappings.value.original_budget = null;
      simpleColumnMappings.value.current_budget = null;
      simpleColumnMappings.value.spent_to_date = null;
      simpleColumnMappings.value.remaining_budget = null;
      simpleColumnMappings.value.funded_at_closing = [];
    }
    // current_draw is always cleared to detect new one
    simpleColumnMappings.value.current_draw = null;
    
    // Apply new AI mappings (only for unmapped fields when using memory)
    Object.keys(aiMappings).forEach(field => {
      if (aiMappings[field] !== null && Object.prototype.hasOwnProperty.call(simpleColumnMappings.value, field)) {
        
        // If using memory, only apply AI mapping if field is not already mapped
        const shouldApplyMapping = !isUsingLastMapping.value || 
                                   field === 'current_draw' || // Always re-detect current_draw
                                   simpleColumnMappings.value[field] === null ||
                                   (field === 'description' && simpleColumnMappings.value.description.length === 0);
        
        console.log(`🔧 MAPPING DEBUG: Field "${field}":`, {
          aiMapping: aiMappings[field],
          currentMapping: simpleColumnMappings.value[field],
          isUsingMemory: isUsingLastMapping.value,
          shouldApply: shouldApplyMapping
        });
        
        if (shouldApplyMapping) {
          if (field === 'description') {
            // Handle both array and number formats for description
            if (Array.isArray(aiMappings[field])) {
              simpleColumnMappings.value.description = [...aiMappings[field]];
              mappingsApplied += aiMappings[field].length;
            } else {
              simpleColumnMappings.value.description = [aiMappings[field]];
              mappingsApplied++;
            }
          } else {
            simpleColumnMappings.value[field] = aiMappings[field];
            mappingsApplied++;
          }
        }
      }
    });
    
    // Force Vue reactivity by triggering a re-render
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // 🛡️ VALIDATION: Double-check current_draw selection to ensure it's the highest numbered draw
    if (simpleColumnMappings.value.current_draw !== null) {
      const aiSelectedIndex = simpleColumnMappings.value.current_draw;
      const aiSelectedHeader = headers[aiSelectedIndex];
      console.log(`🔍 AI selected current_draw: Column ${aiSelectedIndex} = "${aiSelectedHeader}"`);
      
      // Find all draw columns and get the one with highest number
      const drawColumns = [];
      headers.forEach((header, index) => {
        const drawMatch = header.match(/draw\s*#?(\d+)/i);
        if (drawMatch) {
          const drawNumber = parseInt(drawMatch[1]);
          drawColumns.push({ index, header, number: drawNumber });
        }
      });
      
      if (drawColumns.length > 0) {
        // Sort by draw number (highest first)
        drawColumns.sort((a, b) => b.number - a.number);
        const highestDraw = drawColumns[0];
        
        console.log(`🔍 All draw columns found:`, drawColumns.map(d => `${d.header} (#${d.number})`));
        console.log(`🎯 Highest numbered draw should be: Column ${highestDraw.index} = "${highestDraw.header}" (#${highestDraw.number})`);
        
        // Override AI selection if it's wrong
        if (aiSelectedIndex !== highestDraw.index) {
          console.log(`⚠️ AI SELECTED WRONG DRAW! Overriding Column ${aiSelectedIndex} → Column ${highestDraw.index}`);
          simpleColumnMappings.value.current_draw = highestDraw.index;
        } else {
          console.log(`✅ AI correctly selected highest numbered draw: ${highestDraw.header}`);
        }
      }
    }
    
    if (isUsingLastMapping.value) {
      console.log(`✅ Enhanced mapping applied successfully - ${mappingsApplied} new field(s) mapped with memory base`);
      console.log('🧠 Used previous project mapping as base, AI detected new current_draw');
    } else {
      console.log(`✅ AI column mapping applied successfully - ${mappingsApplied} field(s) mapped`);
    }
    console.log('🤖 AI Mappings:', aiMappings);
    console.log('📋 Final Combined Mappings:', simpleColumnMappings.value);
    
  } catch (error) {
    console.error('Error with AI column mapping:', error);
    // Fall back to basic auto-mapping if AI fails
    autoMatchColumns();
  }
}

// Auto-matching logic
function autoMatchColumns() {
  const headers = getAllHeaders();
  
  headers.forEach((header, index) => {
    if (isColumnMapped(index)) return;
    
    // Skip null/undefined headers
    if (!header || typeof header !== 'string') return;
    
    const headerLower = header.toLowerCase();
    
    // Auto-match original budget
    if (simpleColumnMappings.value.original_budget === null && 
        (headerLower.includes('original') || headerLower.includes('budget') || 
         headerLower.includes('initial') || headerLower.includes('base'))) {
      simpleColumnMappings.value.original_budget = index;
    }
    
    // Auto-match current budget
    else if (simpleColumnMappings.value.current_budget === null && 
             (headerLower.includes('current') || headerLower.includes('revised') || 
              headerLower.includes('updated') || headerLower.includes('total'))) {
      simpleColumnMappings.value.current_budget = index;
    }
    

    
    // Auto-match description
    else if (simpleColumnMappings.value.description.length === 0 && 
             (headerLower.includes('description') || headerLower.includes('item') || 
              headerLower.includes('work') || headerLower.includes('task'))) {
      simpleColumnMappings.value.description.push(index);
    }
  });
}

// Multi-Row Header Context Functions
const hasMultiRowHeaders = () => {
  return selectedTabs.value.some(tabIndex => 
    tabBoundaries.value[tabIndex]?.combinedHeaders
  );
}

// Get multi-row headers for display in Step 3 (shows original rows for context)
function getMultiRowHeadersForDisplay(tabIndex) {
  const tab = availableTabs.value[tabIndex];
  const boundary = tabBoundaries.value[tabIndex];
  
  if (!tab || !boundary || !boundary.headerRow) {
    return [getFilteredHeaders(tabIndex)]; // Fallback to single row
  }
  
  // If we have combined headers, it means we detected multi-row headers
  if (boundary.combinedHeaders) {
    console.log('🔧 HEADER DEBUG - Using boundary.combinedHeaders directly');
    
    // 🔥 FIX: Use the pre-processed combinedHeaders directly instead of re-reading raw data
    // The combinedHeaders were already intelligently processed and combined
    return [boundary.combinedHeaders];
  }
  
  // Fallback to single header row
  return [getFilteredHeaders(tabIndex)];
}

const getHeaderExamples = () => {
  const examples = [];
  
  for (const tabIndex of selectedTabs.value) {
    const boundary = tabBoundaries.value[tabIndex];
    if (!boundary?.combinedHeaders) continue;
    
    const tab = availableTabs.value[tabIndex];
    if (!tab?.data) continue;
    
    const primaryRow = tab.data[boundary.headerRow - 1] || [];
    const aboveRow = tab.data[boundary.headerRow - 2] || [];
    
    // Find examples where headers were actually combined
    for (let i = 0; i < Math.min(boundary.combinedHeaders.length, 4); i++) {
      const combined = boundary.combinedHeaders[i];
      const primary = primaryRow[i];
      const above = aboveRow[i];
      
      if (above && primary && combined && 
          combined !== primary && 
          combined.includes(above) && 
          combined.includes(primary)) {
        examples.push({
          original: `"${above}" + "${primary}"`,
          combined: `"${combined}"`
        });
        
        if (examples.length >= 3) break;
      }
    }
    
    if (examples.length >= 3) break;
  }
  
  // If no good examples found, show generic ones
  if (examples.length === 0) {
    examples.push(
      { original: '"Budget" + "Original"', combined: '"Budget Original"' },
      { original: '"Current" + "Amount"', combined: '"Current Amount"' }
    );
  }
  
  return examples;
}

// Helper function to safely process total row mappings for backend
function processedTotalRowMappings() {
  // 🔥 CRITICAL FIX: Read from tabBoundaries instead of current state
  // This ensures we send ALL tabs that have total row mappings configured
  const result = {};
  
  if (!tabBoundaries.value || Object.keys(tabBoundaries.value).length === 0) {
    console.log('🔍 No tab boundaries configured');
    return {};
  }
  
  // Get filtered rows to extract descriptions (for the current tab context)
  const filteredRows = getFilteredRows();
  
  // Process each tab that has boundaries set
  Object.keys(tabBoundaries.value).forEach(tabName => {
    const boundary = tabBoundaries.value[tabName];
    const assignments = boundary.totalRowMappings || {};
    
    if (Object.keys(assignments).length === 0) {
      console.log(`🔍 No total row mappings for tab "${tabName}"`);
      return; // Skip this tab
    }
    
    console.log(`🔍 Processing total row mappings for tab "${tabName}":`, assignments);
    
    // Group assignments by category to detect multi-row selections
    const categoryGroups = {};
    Object.entries(assignments).forEach(([rowIndex, category]) => {
      if (!categoryGroups[category]) {
        categoryGroups[category] = [];
      }
      categoryGroups[category].push(parseInt(rowIndex));
    });
    
    // Build enhanced mappings with row descriptions for memory across draws
    result[tabName] = {};
    
    Object.entries(categoryGroups).forEach(([category, rowIndices]) => {
      if (rowIndices.length === 1) {
        // Single row: use existing simple format
        const rowNum = rowIndices[0] + 1;  // Convert to 1-based
        result[tabName][String(rowNum)] = category;
      } else {
        // Multiple rows: create computed total with row descriptions
        const computedTotal = {
          type: 'computed',
          category: category,
          source_rows: rowIndices.map(idx => {
            const row = filteredRows[idx];
            return {
              row_number: idx + 1,  // 1-based for backend
              description: row ? row[simpleColumnMappings.value.description[0]] : `Row ${idx + 1}`
            };
          })
        };
        // Use special key format for computed totals
        result[tabName][`computed_${category}`] = computedTotal;
      }
    });
  });
  
  // Create a clean, serializable object without proxy wrappers
  const cleanResult = JSON.parse(JSON.stringify(result));
  console.log('🔍 Processed total row mappings for ALL tabs:', cleanResult);
  return cleanResult;
}

async function lockTemplate() {
  if (isLockingTemplate.value) {
    console.log('Template lock already in progress, ignoring duplicate click');
    return;
  }
  
  isLockingTemplate.value = true;
  
  try {
    const isG703Template = templateInfo.value?.template_type === 'g703';
    let templateData;
    
    if (documentData.value?.raw_excel_data || !isG703Template) {
      // Handle Excel data templates (both raw and regular draw documents)
      const templateType = isContractorTemplate.value ? 'contractor_budget_tracker' : 'excel_draw_tracker';
      
      // Create a complete pattern signature that includes column structure and mappings
      const patternSignature = documentData.value?.raw_excel_data ? {
        structure: documentData.value.raw_excel_data.pattern_signature || documentData.value.raw_excel_data.column_structure,
        template_type: templateType,
        header_row: selectedHeaderRow.value,
        date_row: isContractorTemplate.value ? null : selectedDateRow.value,
        budget_mapping: {
          ...ddaMapping.value,
          current_request_column: ddaMapping.value.current_draw_column
        },
        draw_columns: isContractorTemplate.value ? [] : ddaMapping.value.draw_columns,
        draw_period_labels: isContractorTemplate.value ? {} : drawPeriodLabels.value,
        is_contractor_template: isContractorTemplate.value,
        is_global_template: !isContractorTemplate.value // Add this to pattern signature
      } : null;
      
      // Total row handling is now done via DDA terms, not during ingestion
      
      templateData = {
        template_type: templateType,
        status: 'completed',
        project_id: templateInfo.value?.project_id,
        excel_mapping: {
          header_row: selectedHeaderRow.value,
          date_row: isContractorTemplate.value ? null : selectedDateRow.value,
          use_hierarchical_descriptions: useHierarchicalDescriptions.value,
          // CRITICAL FIX: Include boundary information so backend knows what data to process
          tab_boundaries: tabBoundaries.value,
          simple_column_mappings: {
            description: simpleColumnMappings.value.description,
            original_budget: simpleColumnMappings.value.original_budget,
            current_budget: simpleColumnMappings.value.current_budget,
            spent_to_date: simpleColumnMappings.value.spent_to_date,
            remaining_budget: simpleColumnMappings.value.remaining_budget,
            funded_at_closing: simpleColumnMappings.value.funded_at_closing,
            current_draw: simpleColumnMappings.value.current_draw
          },
          budget_mapping: {
            ...ddaMapping.value,
            // Map frontend column names to backend expected names
            current_request_column: ddaMapping.value.current_draw_column
          },
          draw_columns: isContractorTemplate.value ? [] : ddaMapping.value.draw_columns,
          draw_period_labels: isContractorTemplate.value ? {} : drawPeriodLabels.value,
          total_row_mappings: processedTotalRowMappings()
        },
        dda_term_mappings: (function() {
          console.log('🔍 SAVING DDA MAPPINGS - qaPreviewRows:', qaPreviewRows.value?.length, 'items');
          console.log('🔍 SAVING DDA MAPPINGS - Sample data:', qaPreviewRows.value?.slice(0, 2));
          return qaPreviewRows.value || [];
        })()
      };
    } else if (isG703Template) {
      // Handle G703 template - capture mappings at exact moment of save to prevent state loss
      const capturedColumnMapping = JSON.parse(JSON.stringify(g703ColumnMapping.value));
      console.log('🔒 Captured column mapping at save time:', capturedColumnMapping);
      console.log('🔒 Mapping keys count:', Object.keys(capturedColumnMapping).length);
      
      templateData = {
        template_type: 'g703',
        status: 'completed',
        project_id: templateInfo.value?.project_id,
        column_mappings: {
          header_row_index: g703HeaderRow.value,
          g703_column_mapping: capturedColumnMapping
        },
        dda_term_mappings: {} // buildG703DDAMappings() - TODO: implement
      };
    } else {
      // Generic template
      templateData = {
        template_type: 'generic',
        status: 'completed',
        project_id: templateInfo.value?.project_id
      };
    }

    console.log('🔍 Debug: templateInfo project_id:', templateInfo.value?.project_id);
    console.log('🔍 Debug: route params:', route.params);
    console.log('🎯 DEBUG: Template data - total rows now handled by DDA terms');
    console.log('Saving template data:', templateData);

    // Use the finalize endpoint which is specifically designed for template configuration
        const { data } = await axios.post(`${apiBase}draw_templates/${route.params.templateId}/finalize`, {
      template: templateData
    }, authToken.value);
    
    if (data.success) {
      console.log('✅ Template saved successfully, completing template setup...');
      
      // Skip validation step for now since the endpoint doesn't exist
      // TODO: Implement validation_progress endpoint on backend if needed
      await completeTemplateSetup();
    } else {
      throw new Error(data.message || 'Failed to save template');
    }

  } catch (error) {
    console.error('Error locking template:', error);
    errorStore.setError('Template Save Error', 'Failed to save template: ' + (error.response?.data?.message || error.message), 'error', false);
    isLockingTemplate.value = false;
  }
}



// Complete template setup after validation
async function completeTemplateSetup() {
  try {
      // 🧠 SAVE MAPPING TO MEMORY for future use (before completing setup)
      saveProjectMapping();
      
      // Check if we need to return to project import scanner
      const returnTo = route.query.return_to;
      const filename = route.query.filename;
      
      if (returnTo === 'project_import_scanner') {
        console.log('🔙 Returning to project import scanner after template completion');
        
        // Always send completion message to scanner window (whether opened as popup or new tab)
        try {
          // Try to send message to opener (for popups)
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              type: 'template_complete',
              filename: filename,
              template_id: route.params.templateId,
              project_id: templateInfo.value?.project_id || route.params.projectId,
              template_type: isContractorTemplate.value ? 'contractor_budget_tracker' : 'excel_draw_tracker'
            }, '*');
          }
          
          // Also try broadcasting to any listening windows (for new tabs)
          window.postMessage({
            type: 'template_complete',
            filename: filename,
            template_id: route.params.templateId,
            project_id: templateInfo.value?.project_id || route.params.projectId,
            template_type: isContractorTemplate.value ? 'contractor_budget_tracker' : 'excel_draw_tracker'
          }, '*');
          
          // Always attempt to close the window after a brief delay
          setTimeout(() => {
            try {
              window.close();
            } catch (e) {
              // If window.close() fails (some browsers prevent it), show manual close instruction
              console.log('⚠️ Could not auto-close window, user must close manually');
              errorStore.setError('Template Complete', 'Template setup complete! You can now close this window and return to the Project Import Scanner.', 'success', false);
            }
          }, 1000);
          
        } catch (e) {
          console.error('Error sending completion message:', e);
          // Fallback: navigate back to project page
          errorStore.setError('Template Complete', 'Template setup complete! Return to Project Import Scanner to continue.', 'success', false);
          await router.push(`/portfolio/project-summary/${templateInfo.value?.project_id || route.params.projectId}?template_id=${route.params.templateId}`);
        }
      } else if (returnTo === 'upload_queue') {
        // NEW: Queue Mode - process next template or complete queue
        console.log('🎯 Queue mode: Template completed, checking for next template...');
        
        // Load queue from localStorage
        if (templateQueueManager.loadFromLocalStorage()) {
          // Mark current template as completed
          templateQueueManager.markCompleted(route.params.templateId);
          
          // Check if there's more in queue
          if (templateQueueManager.hasNext()) {
            console.log('➡️ More templates in queue, redirecting to project to process next...');
            // Redirect back to project - ProjectSummary will continue the queue
            window.location.href = `/portfolio/project-summary/${templateQueueManager.projectId}`;
          } else {
            console.log('🎉 Queue complete! All templates processed.');
            // All done - clear queue and show completion
            templateQueueManager.clearLocalStorage();
            window.location.href = `/portfolio/project-summary/${templateQueueManager.projectId}`;
          }
        } else {
          console.warn('⚠️ No queue found in localStorage, falling back to normal flow');
          await router.push(`/portfolio/project-summary/${templateInfo.value?.project_id || route.params.projectId}?template_id=${route.params.templateId}`);
        }
      } else {
        // Normal template completion - redirect back to project page with template ID
        await router.push(`/portfolio/project-summary/${templateInfo.value?.project_id || route.params.projectId}?template_id=${route.params.templateId}`);
    }

  } catch (error) {
    console.error('Error completing template setup:', error);
    errorStore.setError('Template Complete', 'Template processing complete! You may need to manually navigate back.', 'warning', false);
  } finally {
    // Always reset the locking state
    isLockingTemplate.value = false;
  }
}

function buildG703DDAMappings() {
  // Build DDA term mappings for G703 data
  return classificationItems.value
    .filter(item => item.dda_term_id)
    .map(item => ({
      description: item.description,
      dda_term_id: item.dda_term_id,
      original_budget: item.original_budget,
      current_request: item.current_request,
      previously_funded: item.previously_funded,
      total_completed_to_date: item.total_completed_to_date,
      auto_matched: item.auto_matched || false,
      ml_confidence: item.ml_confidence || null
    }));
}

function buildExcelDDAMappings() {
  // Build DDA term mappings for Excel data
  return classificationItems.value
    .filter(item => item.dda_term_id)
    .map(item => ({
      description: item.description,
      dda_term_id: item.dda_term_id,
      original_budget: item.original_budget,
      current_request: item.current_request,
      previously_funded: item.previously_funded,
      total_completed_to_date: item.total_completed_to_date,
      auto_matched: item.auto_matched || false,
      ml_confidence: item.ml_confidence || null
    }));
}

function buildRawExcelColumnMappings() {
  // Build column mappings for raw Excel data
  const mappings = {
    header_row_index: selectedHeaderRow.value,
    budget_structure: {
      main_category_column: ddaMapping.value.main_category_column,
      sub_item_column: ddaMapping.value.sub_item_column,
      loan_type_column: ddaMapping.value.loan_type_column,
      budget_column: ddaMapping.value.budget_column
    },
    draw_details: {}
  };
  
  // Map draw columns to their labels
  selectedDrawColumns.value.forEach(columnIndex => {
    const label = drawPeriodLabels.value[columnIndex] || headerRow.value[columnIndex];
    mappings.draw_details[columnIndex] = {
      column_index: columnIndex,
      period_label: label,
      header_text: headerRow.value[columnIndex]
    };
  });
  
  return mappings;
}

function buildColumnMappings() {
  // Build the final column mappings based on user selections (legacy)
  return {
    original_budget: headers.value[getColumnMapping('original_budget')],
    current_budget: headers.value[getColumnMapping('current_budget')],
    total_spent: headers.value[getColumnMapping('total_spent')],
    description: headers.value[findDescriptionColumn()],
    cost_code: headers.value[findCostCodeColumn()],
    company: headers.value[findCompanyColumn()]
  };
}

function getColumnMapping(field) {
  // Return the column index for the specified field
  return selectedColumn.value; // Simplified - would store per step
}

function findDescriptionColumn() {
  return headers.value.findIndex(header => 
    header.toLowerCase().includes('description') || 
    header.toLowerCase().includes('work')
  );
}

function findCostCodeColumn() {
  return headers.value.findIndex(header => 
    header.toLowerCase().includes('code') || 
    header.toLowerCase().includes('cost code')
  );
}

function findCompanyColumn() {
  return headers.value.findIndex(header => 
    header.toLowerCase().includes('company') || 
    header.toLowerCase().includes('contractor')
  );
}

// Auto-scroll functionality for drag selection
function enableAutoScroll() {
  let scrollTimer = null;
  
  function checkAndScroll(event) {
    const container = document.querySelector('.excel-table-container');
    if (!container || !selectionState.value.isSelecting) return;
    
    const rect = container.getBoundingClientRect();
    const scrollSpeed = 50;
    const threshold = 100; // pixels from edge to start scrolling
    
    // Check if near edges and scroll accordingly
    if (event.clientY < rect.top + threshold) {
      // Near top - scroll up
      container.scrollTop -= scrollSpeed;
    } else if (event.clientY > rect.bottom - threshold) {
      // Near bottom - scroll down  
      container.scrollTop += scrollSpeed;
    }
    
    if (event.clientX < rect.left + threshold) {
      // Near left - scroll left
      container.scrollLeft -= scrollSpeed;
    } else if (event.clientX > rect.right - threshold) {
      // Near right - scroll right
      container.scrollLeft += scrollSpeed;
    }
  }
  
  // Add mousemove listener for auto-scroll during drag
  document.addEventListener('mousemove', (event) => {
    if (selectionState.value.isSelecting) {
      checkAndScroll(event);
    }
  });
}

// Auto-run AI mapping when headers become available
const tryAutoAIMapping = async () => {
  // Auto-run column mapping if we're on column mapping step and have headers
  if (currentStep.value === 'column_mapping') {
    const headers = getColumnMappingHeaders();
    if (headers && headers.length > 5) { // Ensure we have meaningful headers
      console.log('🤖 Auto-running AI column mapping on headers load...');
      await aiMapColumns();
    }
  }
  

};

// Watch for changes in tab boundaries to trigger auto AI mapping
watch(
  () => tabBoundaries.value,
  async (newBoundaries) => {
    if (newBoundaries && Object.keys(newBoundaries).length > 0) {
      // Delay slightly to ensure UI is ready
      // setTimeout(tryAutoAIMapping, 500); // DISABLED
    }
  },
  { deep: true }
);

// Watch for step changes to trigger auto AI mapping
watch(
  () => currentStep.value,
  async (newStep) => {
    if (newStep === 'column_mapping') {
      // Delay slightly to ensure data is loaded
      setTimeout(() => {
        console.log('🤖 Auto-triggering AI column mapping for step:', newStep);
        aiMapColumns();
      }, 300);
    }

  }
);



// Load data on mount
onMounted(async () => {
  await loadDocumentData();
  
  // Always load DDA terms for auto-matching (required for all workflows)
  await loadDDATerms();
  
  // Initialize auto-scroll for boundary selection
  enableAutoScroll();
  
  // Add keyboard event listener for modal
  document.addEventListener('keydown', handleKeydown);
  
  // Only generate legacy column mappings if we're in legacy mode
  if (!documentData.value?.raw_excel_data && !documentData.value?.data?.raw_table_data && rows.value?.length > 0) {
    console.log('Loading legacy column mappings for processed data');
    await generateColumnMappings();
  } else {
    console.log('Skipping legacy column mappings - using streamlined workflow');
  }
  
  // Try auto AI mapping after initial load
  // setTimeout(tryAutoAIMapping, 1000); // DISABLED
});

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

async function loadDocumentData() {
  try {
    isLoading.value = true;
    
    // Debug logging
    console.log('LoadDocumentData called');
    console.log('route.params:', route.params);
    console.log('route.query:', route.query);
    console.log('templateId:', route.params.templateId);
    
    // Detect contractor template from URL parameters
    const templateTypeFromUrl = route.query.template_type;
    const filenameFromUrl = route.query.filename;
    
    isContractorTemplate.value = templateTypeFromUrl === 'contractor_budget_tracker' || 
                                (filenameFromUrl && filenameFromUrl.toLowerCase().includes('lender'));
    
    console.log('Template type detection:', {
      templateTypeFromUrl,
      filenameFromUrl,
      isContractorTemplate: isContractorTemplate.value
    });
    
    const backendBaseUrl = apiBase.replace('/api/v1/', '');
    
    // First, fetch the template info to determine the type
    try {
      const templateResponse = await axios.get(`${backendBaseUrl}/api/v1/draw_templates/${route.params.templateId}`);
      templateInfo.value = templateResponse.data;
      console.log('Template info loaded:', templateInfo.value);
      console.log('🔍 DDA term mappings in template:', templateInfo.value?.dda_term_mappings);
      
      // Load total row mappings if they exist in the template
      if (templateInfo.value?.excel_mapping?.total_row_mappings) {
        console.log('🔍 Loading existing total row mappings:', templateInfo.value.excel_mapping.total_row_mappings);
        totalRowMappings.value = {
          hard_costs: templateInfo.value.excel_mapping.total_row_mappings.hard_costs || [],
          soft_costs: templateInfo.value.excel_mapping.total_row_mappings.soft_costs || [],
          financing: templateInfo.value.excel_mapping.total_row_mappings.financing || [],
          project_total: templateInfo.value.excel_mapping.total_row_mappings.project_total || []
        };
        console.log('✅ Total row mappings loaded:', totalRowMappings.value);
      }
      
      // 🔄 REMEMBER PREVIOUS TEMPLATE: Pre-populate tab selection and boundaries from last template
      try {
        const projectId = templateInfo.value?.project_id;
        if (projectId) {
          // Fetch the most recent completed template for this project (excluding current one)
          const templatesResponse = await axios.get(`${backendBaseUrl}/api/v1/draw_templates?project_id=${projectId}&status=completed`);
          const templates = templatesResponse.data || [];
          
          // Find most recent template (excluding current template being created)
          const previousTemplate = templates
            .filter(t => t.id !== parseInt(route.params.templateId))
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
          
          if (previousTemplate?.excel_mapping?.tab_boundaries) {
            console.log('🔄 Found previous template with saved settings:', previousTemplate.id);
            const savedBoundaries = previousTemplate.excel_mapping.tab_boundaries;
            
            // Pre-populate selected tabs (Step 1)
            const savedTabIndices = Object.keys(savedBoundaries).map(idx => parseInt(idx));
            if (savedTabIndices.length > 0) {
              selectedTabs.value = savedTabIndices;
              console.log('✅ Pre-selected tabs from previous template:', selectedTabs.value);
            }
            
            // Pre-populate boundaries (Step 2 - header row and extents) for each tab
            tabBoundaries.value = { ...savedBoundaries };
            console.log('✅ Pre-populated boundaries from previous template:', tabBoundaries.value);
          } else {
            console.log('ℹ️ No previous template found or no saved boundaries');
          }
        }
      } catch (previousTemplateError) {
        console.warn('⚠️ Could not load previous template settings:', previousTemplateError.message);
        // Continue without pre-population - user will select manually
      }
    } catch (templateError) {
      console.error('Error loading template info:', templateError);
      // Continue with Excel endpoint as fallback
    }
    
    // Choose the appropriate endpoint based on template type
    const isG703Template = templateInfo.value?.template_type === 'g703';
    const isPdfMultiDocTemplate = templateInfo.value?.template_type === 'pdf_multi_doc';
    const dataEndpoint = isG703Template ? 'g703_data' : 'excel_data';
    
    // ENHANCED DEBUG LOGGING for template type detection
    console.log('=== TEMPLATE TYPE DETECTION DEBUG ===');
    console.log('templateInfo.value:', templateInfo.value);
    console.log('templateInfo.value?.template_type:', templateInfo.value?.template_type);
    console.log('isG703Template:', isG703Template);
    console.log('isPdfMultiDocTemplate:', isPdfMultiDocTemplate);
    console.log('dataEndpoint chosen:', dataEndpoint);
    console.log('route.query.template_type:', route.query.template_type);
    console.log('=== END DEBUG ===');
    
    // Handle PDF multi-document templates - show page selection FIRST
    if (isPdfMultiDocTemplate) {
      console.log('PDF Multi-Document template detected - showing page selection step');
      activeTab.value = 'g703';
      currentStep.value = 'pdf_page_selection';
      await convertPdfToImages();
      
      // 🎯 INVOICE AUTO-ASSIGN: Check URL params for auto-assignment
      const defaultDocType = route.query.default_doc_type;
      const autoAssignAll = route.query.auto_assign_all === 'true';
      
      if (autoAssignAll && defaultDocType) {
        console.log(`🎯 AUTO-ASSIGN: Setting all ${pdfPagePreviews.value.length} pages to type: ${defaultDocType}`);
        
        // Clear any existing selections
        selectedPdfPages.value = [];
        
        // Select all pages and assign the default document type
        pdfPagePreviews.value.forEach(page => {
          page.selected = true;
          page.documentType = defaultDocType;
          selectedPdfPages.value.push(page);
        });
        
        console.log(`✅ AUTO-ASSIGN: ${selectedPdfPages.value.length} pages selected as ${defaultDocType}`);
        
        // Auto-start processing after a brief delay to let UI update
        setTimeout(() => {
          console.log('🚀 AUTO-ASSIGN: Auto-starting PDF processing...');
          processPdfPages();
        }, 500);
      }
      
      isLoading.value = false;
      return; // Exit early - don't proceed to OCR processing yet
    }
    
    // Set activeTab and check for PDF conversion need for G703 templates
    if (isG703Template) {
      activeTab.value = 'g703';
      
      // Check if this is a multi-page PDF that needs page selection
      if (templateInfo.value?.filename?.toLowerCase().includes('.pdf')) {
        console.log('Multi-page PDF detected - starting PDF to image conversion');
        currentStep.value = 'pdf_page_selection';
        await convertPdfToImages();
      } else {
        currentStep.value = 'g703_table_setup';
        console.log('Single page G703 template detected - proceeding to table setup');
      }
    }
    
    const apiUrl = `${backendBaseUrl}/api/v1/draw_templates/${route.params.templateId}/${dataEndpoint}`;
    console.log('Making API call to:', apiUrl);
    console.log('Template type:', templateInfo.value?.template_type, 'Using endpoint:', dataEndpoint);
    
    const response = await axios.get(apiUrl);
    console.log('API Response:', response.data);
    
    // Check if document is still being processed
    if (response.data && response.data.processing && !response.data.success) {
      console.log('Document still being processed, starting polling...');
      startProcessingPolling();
      return;
    }
    
    if (response.data && response.data.success) {
      documentData.value = response.data;
      
      // Debug logging to see API response structure
      console.log('Full API response structure:', response.data);
      console.log('Has raw_excel_data?', !!response.data.raw_excel_data);
      console.log('Has data.raw_table_data?', !!response.data.data?.raw_table_data);
      console.log('response.data.data:', response.data.data);
      
            if (response.data.raw_excel_data && (response.data.worksheets || response.data.rows)) {
        // NEW STREAMLINED WORKFLOW - Parse Excel data into tab structure
        currentStep.value = 'tab_selection';
        
        // FIXED: Handle multiple worksheets if available
        if (response.data.worksheets && response.data.worksheets.length > 0) {
          // NEW: Multiple worksheets available
          availableTabs.value = response.data.worksheets.map((worksheet, index) => ({
            name: worksheet.name || `Sheet${index + 1}`,
            rows: worksheet.row_count || worksheet.rows?.length || 0,
            cols: worksheet.col_count || worksheet.rows?.[0]?.length || 0,
            preview: worksheet.rows?.slice(0, 5) || [],
            data: worksheet.rows || [],
            index: worksheet.index !== undefined ? worksheet.index : index
          }));
          
          console.log('MULTI-WORKSHEET WORKFLOW - Excel data loaded:', response.data.metadata);
          console.log('Available worksheets:', availableTabs.value.length);
          console.log('Worksheet names:', availableTabs.value.map(t => t.name));
          console.log('Total worksheets:', response.data.worksheets.length);
          
          // 💾 ENHANCED: Restore last selected tab for improved UX
          restoreTabSelectionMemory();
        } else {
          // LEGACY: Single worksheet (backward compatibility)
        const rawRows = response.data.rows || [];
        availableTabs.value = [{
          name: response.data.metadata?.file_name?.replace(/\.[^/.]+$/, "") || 'Sheet1',
          rows: rawRows.length,
          cols: rawRows[0]?.length || 0,
          preview: rawRows.slice(0, 5),
            data: rawRows,
            index: 0
        }];
        
          console.log('LEGACY SINGLE-WORKSHEET - Excel data loaded:', response.data.metadata);
          console.log('Available tabs:', availableTabs.value.length);
          console.log('Raw rows data:', rawRows.length, 'rows');
          
          // 💾 ENHANCED: Restore last selected tab for legacy single worksheet too
          restoreTabSelectionMemory();
        }
          
          // FORCE currentStep to stay on tab_selection
          currentStep.value = 'tab_selection';
          
          // Auto-select first tab and initialize boundaries
          if (availableTabs.value.length > 0) {
            selectedTabs.value = [0];
            initializeTabBoundaries(0);
          }
      } else if (response.data.data?.raw_table_data) {
        // Raw G703 data - start with table setup for column mapping
        currentStep.value = 'g703_table_setup';
        activeTab.value = 'g703'; // Set active tab to show G703 content
        // Store raw table data for mapping
        initializeG703RawData(response.data.data);
        console.log('Raw G703 data loaded:', response.data.data.metadata);
        console.log('Total rows:', response.data.data.raw_table_data?.length);
        console.log('Headers found:', Object.keys(response.data.data.headers || {}));
        
        // Auto-load Template 33 (Standard G703 Template) for all G703 documents
        nextTick(() => {
          const templateLoaded = loadSavedG703Template();
          if (templateLoaded) {
            console.log('✅ Auto-loaded saved G703 template mapping (Template 33 standard)');
          } else {
            // If no saved template, apply the standard Template 33 mapping
            console.log('📋 No saved template found, applying Template 33 standard mapping...');
            applyDefaultG703Mapping();
            console.log('✅ Applied Template 33 standard G703 mapping (98% match for all GC G703s)');
          }
        });
      } else {
        // Legacy processed data
        console.log('Falling back to legacy processing - no G703 or Excel data found');
        headers.value = response.data.headers || [];
        rows.value = response.data.rows || [];
        currentStep.value = 'original_budget';
      }
      isLoading.value = false;
    } else {
      throw new Error(response.data?.error || 'Failed to load document data');
    }
  } catch (error) {
    console.error('Error loading document data:', error);
    console.error('Error details:', error.response?.data);
    console.error('Error status:', error.response?.status);
    
    // If it's a processing error, start polling
    if (error.response?.data?.processing) {
      console.log('Starting polling due to processing error...');
      startProcessingPolling();
    } else {
      isLoading.value = false;
    }
  }
}

async function startProcessingPolling() {
  isProcessing.value = true;
  processingAttempts.value = 0;
  processingStatus.value = 'Document is being processed via OCR. This may take a few minutes...';
  
  // Set activeTab and currentStep immediately for G703 templates during polling
  const isG703Template = templateInfo.value?.template_type === 'g703';
  if (isG703Template) {
    activeTab.value = 'g703';
    currentStep.value = 'g703_table_setup';
    console.log('G703 template detected during polling - setting activeTab to g703 and currentStep to g703_table_setup');
  }
  
  const pollInterval = setInterval(async () => {
    try {
      processingAttempts.value++;
      console.log(`Polling attempt ${processingAttempts.value}/${maxProcessingAttempts.value}`);
      
      const backendBaseUrl = apiBase.replace('/api/v1/', '');
      const isG703Template = templateInfo.value?.template_type === 'g703';
      const dataEndpoint = isG703Template ? 'g703_data' : 'excel_data';
      const apiUrl = `${backendBaseUrl}/api/v1/draw_templates/${route.params.templateId}/${dataEndpoint}`;
      const response = await axios.get(apiUrl);
      
      if (response.data && response.data.success) {
        // Document is ready!
        clearInterval(pollInterval);
        isProcessing.value = false;
        isLoading.value = false;
        
        documentData.value = response.data;
        
        if (response.data.raw_excel_data && (response.data.worksheets || response.data.rows)) {
          // NEW STREAMLINED WORKFLOW - Parse Excel data into tab structure during polling
          currentStep.value = 'tab_selection';
          
          // FIXED: Handle multiple worksheets during polling
          if (response.data.worksheets && response.data.worksheets.length > 0) {
            // NEW: Multiple worksheets available during polling
            availableTabs.value = response.data.worksheets.map((worksheet, index) => ({
              name: worksheet.name || `Sheet${index + 1}`,
              rows: worksheet.row_count || worksheet.rows?.length || 0,
              cols: worksheet.col_count || worksheet.rows?.[0]?.length || 0,
              preview: worksheet.rows?.slice(0, 5) || [],
              data: worksheet.rows || [],
              index: worksheet.index !== undefined ? worksheet.index : index
            }));
            
            console.log('MULTI-WORKSHEET POLLING - Excel data loaded after processing:', response.data.metadata);
            console.log('Available worksheets during polling:', availableTabs.value.length);
            console.log('Worksheet names during polling:', availableTabs.value.map(t => t.name));
            
            // 💾 ENHANCED: Restore last selected tab during polling too
            restoreTabSelectionMemory();
          } else {
            // LEGACY: Single worksheet during polling
          const rawRows = response.data.rows || [];
          availableTabs.value = [{
            name: response.data.metadata?.file_name?.replace(/\.[^/.]+$/, "") || 'Sheet1',
            rows: rawRows.length,
            cols: rawRows[0]?.length || 0,
            preview: rawRows.slice(0, 5),
              data: rawRows,
              index: 0
          }];
          
            console.log('LEGACY POLLING - Excel data loaded after processing:', response.data.metadata);
          console.log('Available tabs during polling:', availableTabs.value.length);
          console.log('Raw rows data during polling:', rawRows.length, 'rows');
          
          // 💾 ENHANCED: Restore last selected tab during legacy polling too
          restoreTabSelectionMemory();
          }
          
          // FORCE currentStep to stay on tab_selection during polling
          currentStep.value = 'tab_selection';
          
          // Auto-select first tab during polling too
          if (availableTabs.value.length > 0) {
            selectedTabs.value = [0];
            initializeTabBoundaries(0);
          }
        } else if (response.data.data?.raw_table_data) {
          currentStep.value = 'g703_table_setup';
          activeTab.value = 'g703'; // Set active tab to show G703 content
          // Store raw table data for mapping
          initializeG703RawData(response.data.data);
          console.log('Raw G703 data loaded after processing:', response.data.data.metadata);
          console.log('Total G703 rows:', response.data.data.raw_table_data?.length);
          
          // Auto-load Template 33 (Standard G703 Template) for all G703 documents
          nextTick(() => {
            const templateLoaded = loadSavedG703Template();
            if (templateLoaded) {
              console.log('✅ Auto-loaded saved G703 template mapping after polling (Template 33 standard)');
            } else {
              // If no saved template, apply the standard Template 33 mapping
              console.log('📋 No saved template found after polling, applying Template 33 standard mapping...');
              applyDefaultG703Mapping();
              console.log('✅ Applied Template 33 standard G703 mapping (98% match for all GC G703s)');
            }
          });
        } else {
          headers.value = response.data.headers || [];
          rows.value = response.data.rows || [];
          currentStep.value = 'original_budget';
        }
        
        processingStatus.value = 'Document processed successfully!';
        
      } else if (response.data && response.data.processing) {
        // Still processing
        const statusMap = {
          'not_started': 'Queued for processing...',
          'processing': 'OCR extraction in progress...',
          'analyzing': 'Analyzing document structure...',
          'extracting': 'Extracting data from document...'
        };
        processingStatus.value = statusMap[response.data.status] || 'Processing document...';
        
      } else {
        // Error or unknown state
        throw new Error(response.data?.error || 'Unknown processing error');
      }
      
      // Check if we've exceeded max attempts
      if (processingAttempts.value >= maxProcessingAttempts.value) {
        clearInterval(pollInterval);
        isProcessing.value = false;
        isLoading.value = false;
        processingStatus.value = 'Processing timeout. Please refresh the page or try again.';
      }
      
    } catch (error) {
      console.error('Error during polling:', error);
      processingAttempts.value++;
      
      if (processingAttempts.value >= maxProcessingAttempts.value) {
        clearInterval(pollInterval);
        isProcessing.value = false;
        isLoading.value = false;
        processingStatus.value = 'Processing failed. Please refresh the page or try again.';
      }
    }
  }, 5000); // Poll every 5 seconds
}

async function loadDDATerms() {
  try {
    console.log('Loading DDA terms from backend...');
    // Load DDA terms from backend API with autocomplete support
    const backendBaseUrl = apiBase.replace('/api/v1/', '');
    const response = await axios.get(`${backendBaseUrl}/api/v1/dda_terms`, {
      params: {
        autocomplete: true,
        per_page: 12000 // Load all terms to support ML matching
      }
    });
    
    if (response.data && Array.isArray(response.data)) {
      ddaTerms.value = response.data;
      console.log(`Loaded ${ddaTerms.value.length} DDA terms from backend`);
    } else {
      // Fallback to comprehensive hardcoded list if backend fails
      console.log('Backend DDA terms not available, using fallback list');
      ddaTerms.value = await loadFallbackDDATerms();
    }
  } catch (error) {
    console.error('Error loading DDA terms from backend:', error);
    // Fallback to comprehensive hardcoded list
    ddaTerms.value = await loadFallbackDDATerms();
  }
}

async function loadFallbackDDATerms() {
  return [
    // Comprehensive construction DDA terms for fallback
    // Division 03 - Concrete
      { id: 1, code: '030000', description: 'Concrete - GMP Holds & Allowances', category: 'Concrete' },
      { id: 2, code: '033100', description: 'Superstructure Concrete Mobilization', category: 'Concrete' },
      { id: 3, code: '033110', description: 'Cast-in-Place Concrete', category: 'Concrete' },
      { id: 4, code: '033200', description: 'Concrete Accessories', category: 'Concrete' },
    { id: 5, code: '033500', description: 'Concrete Finishing', category: 'Concrete' },
    
    // Division 04 - Masonry
    { id: 6, code: '042000', description: 'Unit Masonry', category: 'Masonry' },
    { id: 7, code: '042100', description: 'Clay Unit Masonry', category: 'Masonry' },
    { id: 8, code: '042200', description: 'Concrete Unit Masonry', category: 'Masonry' },
    
    // Division 05 - Metals
    { id: 9, code: '050000', description: 'Metals', category: 'Metals' },
    { id: 10, code: '051000', description: 'Structural Metal Framing', category: 'Metals' },
    { id: 11, code: '052000', description: 'Metal Joists', category: 'Metals' },
    { id: 12, code: '053000', description: 'Metal Decking', category: 'Metals' },
    { id: 13, code: '055000', description: 'Metal Fabrications', category: 'Metals' },
    
    // Division 06 - Wood & Plastics
    { id: 14, code: '061000', description: 'Rough Carpentry', category: 'Carpentry' },
    { id: 15, code: '062000', description: 'Finish Carpentry', category: 'Carpentry' },
    { id: 16, code: '063000', description: 'Carpentry (Residential)', category: 'Carpentry' },
    { id: 17, code: '064000', description: 'Architectural Woodwork', category: 'Carpentry' },
    
    // Division 07 - Thermal & Moisture Protection
    { id: 18, code: '071000', description: 'Dampproofing and Waterproofing', category: 'Waterproofing' },
    { id: 19, code: '072000', description: 'Thermal Protection', category: 'Insulation' },
    { id: 20, code: '073000', description: 'Membrane Roofing', category: 'Roofing' },
    { id: 21, code: '074000', description: 'Roofing and Siding Panels', category: 'Roofing' },
    { id: 22, code: '075000', description: 'Roofing & Waterproofing', category: 'Roofing' },
    { id: 23, code: '076000', description: 'Flashing and Sheet Metal', category: 'Roofing' },
    { id: 24, code: '077000', description: 'Roof Specialties and Accessories', category: 'Roofing' },
    
    // Division 08 - Openings
    { id: 25, code: '081000', description: 'Hollow Metal Doors, Frames, & Hardware', category: 'Doors & Windows' },
    { id: 26, code: '082000', description: 'Wood and Plastic Doors', category: 'Doors & Windows' },
    { id: 27, code: '083000', description: 'Coiling Doors and Grilles', category: 'Doors & Windows' },
    { id: 28, code: '084000', description: 'Curtain Wall & Facade Work', category: 'Doors & Windows' },
    { id: 29, code: '085000', description: 'Windows', category: 'Doors & Windows' },
    { id: 30, code: '087000', description: 'Hardware', category: 'Doors & Windows' },
    { id: 31, code: '088000', description: 'Glazing', category: 'Doors & Windows' },
    
    // Division 09 - Finishes
    { id: 32, code: '091000', description: 'Lath and Plaster', category: 'Finishes' },
    { id: 33, code: '092000', description: 'Plaster and Gypsum Board', category: 'Finishes' },
    { id: 34, code: '093000', description: 'Tiling', category: 'Finishes' },
    { id: 35, code: '094000', description: 'Terrazzo', category: 'Finishes' },
    { id: 36, code: '095000', description: 'Ceilings', category: 'Finishes' },
    { id: 37, code: '096000', description: 'Flooring', category: 'Finishes' },
    { id: 38, code: '097000', description: 'Wall Finishes', category: 'Finishes' },
    { id: 39, code: '099000', description: 'Painting and Coating', category: 'Finishes' },
    
    // Division 10 - Specialties
    { id: 40, code: '101000', description: 'Information Specialties', category: 'Specialties' },
    { id: 41, code: '102000', description: 'Compartments and Cubicles', category: 'Specialties' },
    { id: 42, code: '103000', description: 'Fireplaces and Stoves', category: 'Specialties' },
    { id: 43, code: '104000', description: 'Safety Specialties', category: 'Specialties' },
    
    // Division 11 - Equipment
    { id: 44, code: '114000', description: 'Food Service Equipment', category: 'Equipment' },
    { id: 45, code: '115000', description: 'Medical Equipment', category: 'Equipment' },
    { id: 46, code: '116000', description: 'Laboratory Equipment', category: 'Equipment' },
    
    // Division 12 - Furnishings
    { id: 47, code: '121000', description: 'Fixed Furnishings', category: 'Furnishings' },
    { id: 48, code: '122000', description: 'Window Treatments', category: 'Furnishings' },
    { id: 49, code: '123000', description: 'Casework', category: 'Furnishings' },
    
    // Division 21 - Fire Suppression
    { id: 50, code: '211000', description: 'Water-Based Fire-Suppression Systems', category: 'Fire Suppression' },
    { id: 51, code: '213000', description: 'Fire Suppression Systems', category: 'Fire Suppression' },
    { id: 52, code: '214000', description: 'Fire Sprinkler Systems', category: 'Fire Suppression' },
    
    // Division 22 - Plumbing
    { id: 53, code: '221000', description: 'Plumbing Piping and Pumps', category: 'Plumbing' },
    { id: 54, code: '224000', description: 'Plumbing Fixtures', category: 'Plumbing' },
    { id: 55, code: '225000', description: 'Water Treatment Equipment', category: 'Plumbing' },
    
    // Division 23 - HVAC
    { id: 56, code: '230000', description: 'Heating, Ventilating, and Air Conditioning (HVAC)', category: 'HVAC' },
    { id: 57, code: '231000', description: 'HVAC Piping and Pumps', category: 'HVAC' },
    { id: 58, code: '232000', description: 'HVAC Pumps', category: 'HVAC' },
    { id: 59, code: '233000', description: 'HVAC Air Distribution', category: 'HVAC' },
    { id: 60, code: '234000', description: 'HVAC Fans', category: 'HVAC' },
    { id: 61, code: '235000', description: 'HVAC Central Equipment', category: 'HVAC' },
    { id: 62, code: '236000', description: 'HVAC Terminal and Package Units', category: 'HVAC' },
    
    // Division 26 - Electrical
    { id: 63, code: '260000', description: 'Electrical', category: 'Electrical' },
    { id: 64, code: '261000', description: 'Basic Electrical Systems', category: 'Electrical' },
    { id: 65, code: '262000', description: 'Low-Voltage Electrical Transmission', category: 'Electrical' },
    { id: 66, code: '263000', description: 'Lighting', category: 'Electrical' },
    { id: 67, code: '264000', description: 'Electrical and Cathodic Protection', category: 'Electrical' },
    { id: 68, code: '265000', description: 'Common Work Results for Electrical', category: 'Electrical' },
    { id: 69, code: '266000', description: 'Exterior Electrical', category: 'Electrical' },
    
    // Division 27 - Communications
    { id: 70, code: '271000', description: 'Structured Cabling', category: 'Communications' },
    { id: 71, code: '275000', description: 'Distributed Communications and Cabling', category: 'Communications' },
    { id: 72, code: '276000', description: 'Communication Equipment', category: 'Communications' },
    
    // Division 28 - Electronic Safety and Security
    { id: 73, code: '281000', description: 'Electronic Detection and Alarm', category: 'Security' },
    { id: 74, code: '282000', description: 'Electronic Surveillance', category: 'Security' },
    { id: 75, code: '283000', description: 'Access Control', category: 'Security' },
    
    // Common General Construction Terms
    { id: 76, code: 'GEN001', description: 'General Conditions', category: 'General' },
    { id: 77, code: 'GEN002', description: 'Site Preparation', category: 'Sitework' },
    { id: 78, code: 'GEN003', description: 'Excavation', category: 'Sitework' },
    { id: 79, code: 'GEN004', description: 'Foundation Work', category: 'Concrete' },
    { id: 80, code: 'GEN005', description: 'Demolition', category: 'General' },
    { id: 81, code: 'GEN006', description: 'Temporary Facilities', category: 'General' },
    { id: 82, code: 'GEN007', description: 'Final Cleanup', category: 'General' },
    { id: 83, code: 'GEN008', description: 'Permits and Fees', category: 'General' },
    { id: 84, code: 'GEN009', description: 'Architectural Finishes', category: 'Finishes' },
    { id: 85, code: 'GEN010', description: 'Mechanical Systems', category: 'HVAC' }
  ];
}

async function generateColumnMappings() {
  try {
    // Generate initial column mappings based on Excel data (legacy)
    columnMappings.value = rows.value.slice(0, 10).map((row, index) => ({
      id: index,
      dda_term: ddaTerms.value[index % ddaTerms.value.length]?.description || 'Unknown',
      cost_code: row[0] || '',
      description: row[1] || '',
      company: row[2] || '',
      original_budget: parseFloat(row[3]) || 0,
      prior_adjustments: parseFloat(row[4]) || 0,
      current_adjustments: parseFloat(row[5]) || 0,
      current_budget: parseFloat(row[6]) || 0,
      prior_billing: parseFloat(row[7]) || 0,
      current_billing: parseFloat(row[8]) || 0,
      total_billing: parseFloat(row[9]) || 0,
      complete_pct: parseFloat(row[10]) || 0
    }));
  } catch (error) {
    console.error('Error generating column mappings:', error);
  }
}

function selectExcelItem(index) {
  selectedExcelItem.value = index;
  selectedDDATerm.value = null; // Reset DDA term selection
}

function assignDDATerm(termId) {
  if (selectedExcelItem.value !== null) {
    classificationItems.value[selectedExcelItem.value].dda_term_id = termId;
    // Move to next unclassified item
    const nextIndex = classificationItems.value.findIndex((item, index) => 
      index > selectedExcelItem.value && !item.dda_term_id);
    if (nextIndex !== -1) {
      selectedExcelItem.value = nextIndex;
    }
  }
  selectedDDATerm.value = termId;
}

function getDDATermName(termId) {
  const term = ddaTerms.value.find(t => t.id === termId);
  return term ? term.description : 'Unknown Term';
}

function buildClassificationItems() {
  if (!documentData.value?.rows || selectedHeaderRow.value === null) return;
  
  classificationItems.value = documentData.value.rows
    .slice(selectedHeaderRow.value + 1)
    .filter(row => {
      // Only include rows with meaningful data - check all description columns
      const descriptions = []
      ddaMapping.value.description_columns.forEach(colIndex => {
        const desc = row[colIndex]
        if (desc && desc.toString().trim() !== '') {
          descriptions.push(desc.toString().trim())
        }
      })
      
      const hasDescription = descriptions.length > 0
      // Budget is fluid - budget item can exist without budget values
      const hasBudgetOrValue = getDDAMappedValue(row, 'original_budget_column') || 
                              getDDAMappedValue(row, 'current_budget_column') ||
                              getDDAMappedValue(row, 'current_draw_column') ||
                              hasDescription; // Include all description items even without budget
      return hasDescription; // Only require description, budget is optional
    })
    .map((row, index) => {
      // Enhanced hierarchical description building
      const descriptions = []
      const hierarchyLevels = []
      
      ddaMapping.value.description_columns.forEach((colIndex, levelIndex) => {
        const desc = row[colIndex]
        if (desc && desc.toString().trim() !== '') {
          const cleanDesc = desc.toString().trim()
          descriptions.push(cleanDesc)
          hierarchyLevels.push({
            level: levelIndex + 1,
            text: cleanDesc,
            column: colIndex + 1
          })
        }
      })
      
      // Create multiple description formats for different use cases
      const fullHierarchy = descriptions.join(' → ')  // Use arrow for better hierarchy visual
      const shortDescription = descriptions[descriptions.length - 1] || '' // Most specific level
      const categoryPath = descriptions.length > 1 ? descriptions.slice(0, -1).join(' / ') : ''
      
      return {
        id: index,
        description: fullHierarchy,
        short_description: shortDescription,
        category_path: categoryPath,
        hierarchy_levels: hierarchyLevels,
        hierarchy_depth: descriptions.length,
        cost_code: getDDAMappedValue(row, 'cost_code_column') || '',
        company: getDDAMappedValue(row, 'company_column') || '',
        original_budget: parseFloat(getDDAMappedValue(row, 'original_budget_column')) || 0,
        prior_adjustments: 0, // Default value
        current_adjustments: 0, // Default value
        current_budget: parseFloat(getDDAMappedValue(row, 'current_budget_column')) || 0,
        prior_billing: 0, // Default value
        current_billing: parseFloat(getDDAMappedValue(row, 'current_draw_column')) || 0,
        total_billing: parseFloat(getDDAMappedValue(row, 'current_draw_column')) || 0,
        complete_percent: 0, // Will be calculated
        dda_term_id: null, // Will be assigned during classification
        // Autocomplete properties
        searchTerm: '',
        showDropdown: false,
        searchResults: [],
        selectedTerm: null,
        auto_matched: false
      }
    });
    
  console.log(`Created ${classificationItems.value.length} classification items with hierarchical descriptions`);
  console.log('Sample hierarchy depths:', classificationItems.value.slice(0, 3).map(item => ({
    description: item.description,
    depth: item.hierarchy_depth,
    levels: item.hierarchy_levels
  })));
  
  // Auto-match descriptions to DDA terms
  autoMatchDDATerms();
}

// Auto-match function for QA preview data
async function autoMatchQAPreviewData() {
  console.log('🎯 Starting QA preview auto-matching...');
  
  // Get preview data and store in reactive array
  const previewRows = getDataPreviewRows();
  if (!previewRows || previewRows.length === 0) {
    console.log('❌ No preview data available for auto-matching');
    return;
  }
  
  // Store in reactive array for UI updates
  qaPreviewRows.value = [...previewRows];
  
  console.log(`📊 Found ${previewRows.length} rows to process`);
  
  // Convert preview data to classification format expected by autoMatchDDATerms
  const tempClassificationItems = previewRows.map((row, index) => ({
    id: `qa_preview_${index}`,
    description: row.description || '',
    dda_term_id: null,
    dda_term_name: null
  }));
  
  // Temporarily set classificationItems to our preview data
  const originalClassificationItems = classificationItems.value;
  classificationItems.value = tempClassificationItems;
  
  console.log(`🔄 Prepared ${tempClassificationItems.length} items for auto-matching`);
  
  try {
    // Call the existing auto-match function
    await autoMatchDDATerms();
    
    // Update both preview data and reactive array with the results
    classificationItems.value.forEach((item, index) => {
      if (item.dda_term_id && previewRows[index] && qaPreviewRows.value[index]) {
        // Look up the actual DDA term name from the loaded terms
        const ddaTerm = ddaTerms.value.find(term => term.id === item.dda_term_id);
        const termName = ddaTerm ? ddaTerm.term : `Unknown Term (ID: ${item.dda_term_id})`;
        const confidence = Math.round((item.confidence || 0.85) * 100);
        
        // Update both arrays
        previewRows[index].ddaTerm = termName;
        previewRows[index].ddaTermId = item.dda_term_id;
        previewRows[index].confidence = confidence;
        
        qaPreviewRows.value[index].ddaTerm = termName;
        qaPreviewRows.value[index].ddaTermId = item.dda_term_id;
        qaPreviewRows.value[index].dda_term_id = item.dda_term_id;  // CRITICAL: Add snake_case for backend
        qaPreviewRows.value[index].dda_term_name = termName;         // CRITICAL: Add snake_case for backend
        qaPreviewRows.value[index].confidence = confidence;
        
        console.log(`✅ Matched "${previewRows[index].description}" → "${termName}" (${confidence}%)`);
      }
    });
    
    const matchedCount = qaPreviewRows.value.filter(row => row.ddaTerm && row.ddaTerm !== 'Auto-match pending').length;
    console.log(`🔄 UI Update: ${matchedCount} terms now visible in grid`);
    
    console.log('🎉 QA preview auto-matching complete!');
    
    // Force UI update by updating qaPreviewRows with matched data
    qaPreviewRows.value = getDataPreviewRows().map((row, index) => {
      const matchedItem = classificationItems.value[index];
      if (matchedItem && matchedItem.dda_term_id) {
        const ddaTerm = ddaTerms.value.find(term => term.id === matchedItem.dda_term_id);
        return {
          ...row,
          ddaTerm: ddaTerm ? ddaTerm.term : `Unknown Term (ID: ${matchedItem.dda_term_id})`,
          ddaTermId: matchedItem.dda_term_id,
          dda_term_id: matchedItem.dda_term_id,  // CRITICAL: Add snake_case for backend
          dda_term_name: ddaTerm ? ddaTerm.term : `Unknown Term (ID: ${matchedItem.dda_term_id})`,  // CRITICAL: Add snake_case for backend
          confidence: Math.round((matchedItem.confidence || 0.85) * 100)
        };
      }
      return row;
    });
    
    console.log(`🔄 UI Updated: ${qaPreviewRows.value.filter(r => r.ddaTerm && r.ddaTerm !== 'Auto-match pending').length} terms now visible`);
    
    // Update DDA category totals after auto-matching
    updateDDACategoryTotals();
    
  } catch (error) {
    console.error('❌ Error during QA auto-matching:', error);
  } finally {
    // Restore original classification items
    classificationItems.value = originalClassificationItems;
  }
}

// DDA Category Totals Detection Function
function updateDDACategoryTotals() {
  // Reset all counts
  ddaCategoryTotals.value.forEach(category => {
    category.detected = false;
    category.count = 0;
  });
  
  const dataRows = qaPreviewRows.value.length > 0 ? qaPreviewRows.value : getDataPreviewRows();
  
  // Check each row for DDA terms with total indicators
  dataRows.forEach(row => {
    if (row.dda_term_id && row.ddaTerm) {
      const ddaTerm = row.ddaTerm.toLowerCase();
      const description = (row.description || '').toLowerCase();
      
      // Check if this is a total row
      const totalIndicators = ['total', 'subtotal', 'sub-total', 'sum', 'aggregate'];
      const isTotal = totalIndicators.some(indicator => 
        ddaTerm.includes(indicator) || description.includes(indicator)
      );
      
      if (isTotal) {
        // ENHANCED: Match by DDA term category ID first (most accurate)
        const ddaTermData = ddaTerms.value.find(term => term.id === row.dda_term_id);
        let categoryMatched = false;
        
        if (ddaTermData && ddaTermData.dda_term_category_id) {
          const categoryId = Number(ddaTermData.dda_term_category_id);
          
          ddaCategoryTotals.value.forEach(category => {
            if (category.categoryId === categoryId) {
              category.detected = true;
              category.count += 1;
              categoryMatched = true;
              console.log(`🎯 Category ID Match: "${ddaTerm}" → ${category.name} (ID: ${categoryId})`);
            }
          });
        }
        
        // Fallback to keyword matching if no category ID match found
        if (!categoryMatched) {
          ddaCategoryTotals.value.forEach(category => {
            const categoryKeywords = {
              'Hard Costs': ['hard', 'construction', 'trade'],
              'Soft Costs': ['soft', 'professional', 'design'],
              'Financing': ['financing', 'finance', 'loan', 'interest'],
              'Acquisition': ['acquisition', 'property', 'purchase', 'closing']
            };
            
            const keywords = categoryKeywords[category.name] || [];
            if (keywords.some(keyword => ddaTerm.includes(keyword) || description.includes(keyword))) {
              category.detected = true;
              category.count += 1;
              console.log(`🔤 Keyword Match: "${ddaTerm}" → ${category.name} (keyword fallback)`);
            }
          });
        }
      }
    }
  });
  
  console.log('🔍 Updated DDA Category Totals:', ddaCategoryTotals.value);
}

async function autoMatchDDATerms() {
  if (!ddaTerms.value || ddaTerms.value.length === 0) {
    console.log('No DDA terms available for auto-matching');
    return;
  }

  console.log(`Starting auto-match with ${ddaTerms.value.length} DDA terms`);
  console.log(`Classification items to match: ${classificationItems.value.length}`);
  
  // 🔥 RESPECT QA SELECTIONS: Check for manually set DDA terms from QA step
  const hasQASelections = qaPreviewRows.value && qaPreviewRows.value.length > 0;
  if (hasQASelections) {
    console.log('📝 Found QA selections, respecting manual term assignments');
    // Update classification items to reflect QA selections
    qaPreviewRows.value.forEach((qaRow, index) => {
      if (qaRow.ddaTermId && classificationItems.value[index]) {
        classificationItems.value[index].dda_term_id = qaRow.ddaTermId;
        classificationItems.value[index].dda_term_name = qaRow.ddaTerm;
        classificationItems.value[index].confidence = (qaRow.confidence || 100) / 100;
        console.log(`✅ Preserved QA selection: "${qaRow.description}" → "${qaRow.ddaTerm}"`);
      }
    });
  }
  
  // Use backend ML classification service for better matching
  try {
    const descriptions = classificationItems.value
      .filter(item => item.description && !item.dda_term_id)  // Skip items with existing DDA terms (from QA)
      .map(item => item.description);
    
    if (descriptions.length === 0) {
      console.log('No descriptions to classify');
      return;
    }
    
    console.log(`Sending ${descriptions.length} descriptions to backend for ML classification`);
    console.log('🔗 API Base URL:', apiBase);
    console.log('🔗 Backend Base URL:', apiBase.replace('/api/v1/', ''));
    console.log('🔗 Full classify URL:', `${apiBase.replace('/api/v1/', '')}/api/v1/dda_terms/classify`);
    console.log('📤 Sample descriptions:', descriptions.slice(0, 3));
    
    // Call backend classification endpoint
    const backendBaseUrl = apiBase.replace('/api/v1/', '');
    const response = await axios.post(`${backendBaseUrl}/api/v1/dda_terms/classify`, {
      descriptions: descriptions
    });
    
    console.log('📥 Classification response:', response.data);
    
    if (response.data && response.data.classifications && response.data.classifications.length > 0) {
      let matchedCount = 0;
      
      // Apply ML classifications
      classificationItems.value.forEach((item, index) => {
        if (item.description && !item.dda_term_id) {
          const classification = response.data.classifications.find(c => c.description === item.description);
          if (classification && classification.dda_term_id && classification.confidence > 0.3) {
            item.dda_term_id = classification.dda_term_id;
            item.auto_matched = true;
            item.ml_confidence = classification.confidence;
            
            // Try to find term in our loaded set first
            const fullTerm = ddaTerms.value.find(t => t.id === classification.dda_term_id);
            if (fullTerm) {
              item.selectedTerm = fullTerm;
              item.searchTerm = fullTerm.term;
            } else if (classification.dda_term_name) {
              // Use the term name from classification if not found in loaded terms
              item.selectedTerm = {
                id: classification.dda_term_id,
                term: classification.dda_term_name,
                category_name: 'Matched' // Default category
              };
              item.searchTerm = classification.dda_term_name;
            }
            
            matchedCount++;
            console.log(`ML-matched "${item.description}" to DDA term ID: ${classification.dda_term_id} (confidence: ${classification.confidence})`);
          }
        }
      });
      
      console.log(`ML auto-match complete: ${matchedCount} matched out of ${classificationItems.value.length} total items`);
      
      // Force refresh display for all matched items
      refreshTermDisplays();
    } else {
      // Fallback to enhanced keyword matching
      console.log('ML returned empty results, using enhanced keyword matching');
      fallbackKeywordMatching();
    }
  } catch (error) {
    console.error('❌ Error calling backend classification:', error);
    console.error('❌ Error details:', error.response?.data || error.message);
    console.error('❌ Error status:', error.response?.status);
    console.log('🔄 Falling back to keyword matching...');
    // Fallback to simple keyword matching
    fallbackKeywordMatching();
  }
}

function fallbackKeywordMatching() {
  let matchedCount = 0;
  let errorCount = 0;
  
  classificationItems.value.forEach((item, index) => {
    if (item.description && !item.dda_term_id) {
      try {
        const match = findBestDDAMatch(item.description, item.cost_code);
        if (match) {
          item.dda_term_id = match.id;
          item.auto_matched = true;
          item.selectedTerm = match;
          item.searchTerm = match.term;
          matchedCount++;
          console.log(`Keyword-matched "${item.description}" to DDA term: ${match.term}`);
        }
      } catch (error) {
        errorCount++;
        console.error(`Error matching item ${index} "${item.description}":`, error);
      }
    }
  });
  
  console.log(`Fallback keyword matching complete: ${matchedCount} matched, ${errorCount} errors`);
  
  // Force refresh display for all matched items
  refreshTermDisplays();
}

async function findBestDDAMatch(description, costCode) {
  if (!description || !ddaTerms.value) return null;
  
  // First try enhanced OpenAI matching for better accuracy (consistent with ProjectSummary)
  try {
    const openAIResult = await findBestDDATermWithOpenAI(description, ddaTerms.value);
    if (openAIResult && openAIResult.term) {
      console.log(`🤖 QA OpenAI match: "${description}" → "${openAIResult.term.term}" (${openAIResult.confidence})`);
      return openAIResult.term;
    }
  } catch (error) {
    console.log('QA OpenAI matching failed, falling back to keyword matching:', error);
  }
  
  // Fallback to existing keyword matching logic
  const desc = description.toLowerCase().trim();
  const code = costCode ? costCode.toLowerCase().trim() : '';
  
  let bestMatch = null;
  let bestScore = 0;
  
  // First pass: Try exact and substring matches
  for (const term of ddaTerms.value) {
    if (!term || !term.term) continue;
    
    const termLower = term.term.toLowerCase();
    let score = 0;
    
    // Exact match gets highest score
    if (termLower === desc) {
      score = 100;
    }
    // Check if description is contained in term or vice versa
    else if (termLower.includes(desc) && desc.length > 3) {
      score = 90 - (termLower.length - desc.length) * 0.1; // Penalize length difference
    }
    else if (desc.includes(termLower) && termLower.length > 3) {
      score = 85 - (desc.length - termLower.length) * 0.1;
    }
    // Word-by-word matching
    else {
      const termWords = termLower.split(/\s+/).filter(w => w.length > 2);
      const descWords = desc.split(/\s+/).filter(w => w.length > 2);
      const commonWords = termWords.filter(w => descWords.includes(w));
      
      if (commonWords.length > 0) {
        score = (commonWords.length / Math.max(termWords.length, descWords.length)) * 70;
        
        // Enhanced bonus for construction acronyms and important keywords (consistent with ProjectSummary)
        const importantWords = ['concrete', 'electrical', 'plumbing', 'hvac', 'steel', 'roofing', 'mep', 'aor', 'soe', 'expediting', 'facade', 'elevator', 'landscape'];
        const hasImportant = commonWords.some(w => importantWords.includes(w));
        if (hasImportant) score += 10;
      }
    }
    
    if (score > bestScore && score > 40) { // Minimum threshold
      bestScore = score;
      bestMatch = term;
    }
  }
  
  // Second pass: Enhanced keyword matching if no good direct match
  if (!bestMatch || bestScore < 60) {
    const keywordMatch = checkAdvancedKeywordMatch(desc, costCode);
    if (keywordMatch && (!bestMatch || keywordMatch.score > bestScore)) {
      bestMatch = keywordMatch.term;
      bestScore = keywordMatch.score;
    }
  }
  
  return bestMatch;
}

function checkAdvancedKeywordMatch(description, costCode) {
  // Enhanced keyword mappings with specific DDA term targets
  const advancedMappings = [
    // Professional Services
    {
      keywords: ['architect', 'design architect', 'architectural', 'aor'],
      targetTerms: ['architect', 'design', 'architectural'],
      score: 85
    },
    {
      keywords: ['interior design', 'interior', 'interiors'],
      targetTerms: ['interior', 'design'],
      score: 80
    },
    {
      keywords: ['mep', 'mechanical electrical plumbing', 'mechanical', 'electrical', 'plumbing'],
      targetTerms: ['mechanical', 'electrical', 'plumbing', 'mep'],
      score: 85
    },
    {
      keywords: ['structural', 'structure'],
      targetTerms: ['structural', 'structure'],
      score: 85
    },
    {
      keywords: ['expediting', 'expediter'],
      targetTerms: ['expediting', 'permit'],
      score: 75
    },
    {
      keywords: ['soe', 'support of excavation', 'excavation'],
      targetTerms: ['excavation', 'support', 'shoring'],
      score: 75
    },
    {
      keywords: ['inspection', 'inspections', 'monitoring', 'special inspections'],
      targetTerms: ['inspection', 'monitoring', 'testing'],
      score: 80
    },
    {
      keywords: ['permit', 'building permit', 'permits'],
      targetTerms: ['permit', 'license'],
      score: 85
    },
    {
      keywords: ['marketing', 'advertising'],
      targetTerms: ['marketing', 'advertising', 'sales'],
      score: 80
    },
    {
      keywords: ['ffe', 'furniture', 'fixtures', 'equipment', 'owner direct'],
      targetTerms: ['furniture', 'fixtures', 'equipment', 'furnishing'],
      score: 80
    },
    {
      keywords: ['ground lease', 'lease', 'rent'],
      targetTerms: ['lease', 'rent', 'ground'],
      score: 75
    },
    {
      keywords: ['re taxes', 'real estate taxes', 'taxes', 'property tax'],
      targetTerms: ['taxes', 'real estate', 'property'],
      score: 80
    },
    {
      keywords: ['hoa', 'operating reserve', 'reserve', 'homeowner'],
      targetTerms: ['reserve', 'operating', 'hoa'],
      score: 75
    },
    // Technical Systems
    {
      keywords: ['electrical', 'electric', 'lighting', 'power', 'wiring', 'circuit', 'outlet', 'switch', 'panel'],
      targetTerms: ['electrical', 'lighting', 'power', 'basic electrical systems'],
      score: 75
    },
    {
      keywords: ['plumbing', 'plumber', 'pipe', 'piping', 'water', 'sewer', 'drain', 'fixture', 'bathroom', 'toilet'],
      targetTerms: ['plumbing piping', 'plumbing fixtures', 'water treatment'],
      score: 75
    },
    {
      keywords: ['hvac', 'heating', 'cooling', 'ventilation', 'air conditioning', 'mechanical', 'ductwork'],
      targetTerms: ['heating, ventilating, and air conditioning', 'hvac piping', 'hvac air distribution'],
      score: 75
    },
    {
      keywords: ['concrete', 'cement', 'foundation', 'footing', 'slab', 'cast-in-place', 'reinforcement'],
      targetTerms: ['cast-in-place concrete', 'concrete accessories', 'concrete finishing'],
      score: 75
    },
    {
      keywords: ['steel', 'metal', 'beam', 'column', 'frame', 'structural', 'framing', 'joist'],
      targetTerms: ['structural metal framing', 'metal joists', 'metal decking', 'metal fabrications'],
      score: 75
    },
    {
      keywords: ['roof', 'roofing', 'shingle', 'membrane', 'gutter', 'flashing', 'waterproof'],
      targetTerms: ['membrane roofing', 'roofing and siding', 'flashing and sheet metal'],
      score: 75
    },
    {
      keywords: ['window', 'glazing', 'curtain wall', 'glass'],
      targetTerms: ['windows', 'glazing', 'curtain wall'],
      score: 70
    },
    {
      keywords: ['door', 'entrance', 'hardware', 'frame'],
      targetTerms: ['hollow metal doors', 'wood and plastic doors', 'hardware'],
      score: 70
    },
    {
      keywords: ['paint', 'painting', 'coating', 'finish'],
      targetTerms: ['painting and coating', 'wall finishes'],
      score: 65
    },
    {
      keywords: ['tile', 'tiling', 'ceramic', 'stone'],
      targetTerms: ['tiling'],
      score: 70
    },
    {
      keywords: ['carpet', 'flooring', 'floor', 'hardwood', 'vinyl'],
      targetTerms: ['flooring'],
      score: 65
    },
    {
      keywords: ['drywall', 'gypsum', 'sheetrock', 'plaster'],
      targetTerms: ['plaster and gypsum board'],
      score: 70
    },
    {
      keywords: ['insulation', 'thermal', 'vapor', 'barrier'],
      targetTerms: ['thermal protection', 'dampproofing and waterproofing'],
      score: 65
    },
    {
      keywords: ['masonry', 'brick', 'block', 'stone', 'mortar'],
      targetTerms: ['unit masonry', 'clay unit masonry', 'concrete unit masonry'],
      score: 70
    },
    {
      keywords: ['wood', 'lumber', 'timber', 'framing', 'carpentry', 'trim'],
      targetTerms: ['rough carpentry', 'finish carpentry', 'architectural woodwork'],
      score: 65
    },
    {
      keywords: ['landscape', 'landscaping', 'site work', 'grounds'],
      targetTerms: ['landscape', 'landscaping', 'site'],
      score: 75
    },
    {
      keywords: ['facade', 'façade', 'exterior', 'cladding'],
      targetTerms: ['facade', 'exterior', 'cladding'],
      score: 80
    },
    {
      keywords: ['elevator', 'lift', 'vertical transport'],
      targetTerms: ['elevator', 'lift'],
      score: 90
    },
    {
      keywords: ['acoustic', 'sound', 'noise', 'privacy'],
      targetTerms: ['acoustic', 'sound', 'privacy'],
      score: 80
    },
    {
      keywords: ['safety', 'site safety', 'safety plan'],
      targetTerms: ['safety', 'site safety'],
      score: 85
    },
    {
      keywords: ['insurance', 'title insurance', 'coverage'],
      targetTerms: ['insurance', 'title'],
      score: 85
    }
  ];
  
  let bestMatch = null;
  let bestScore = 0;
  
  for (const mapping of advancedMappings) {
    // Check if any keyword matches the description
    const hasKeyword = mapping.keywords.some(keyword => description.includes(keyword));
    if (!hasKeyword) continue;
    
    // Find matching DDA terms
    for (const targetTerm of mapping.targetTerms) {
      const matchingTerm = ddaTerms.value.find(term => 
        term.term && term.term.toLowerCase().includes(targetTerm.toLowerCase())
      );
      
      if (matchingTerm && mapping.score > bestScore) {
        bestMatch = matchingTerm;
        bestScore = mapping.score;
      }
    }
  }
  
  return bestMatch ? { term: bestMatch, score: bestScore } : null;
}

function checkKeywordMatch(description, target) {
  if (!description || !target) return false;
  
  // Define keyword mapping for construction terms
  const keywordMappings = {
    'concrete': ['concrete', 'cast-in-place', 'superstructure', 'foundation'],
    'steel': ['steel', 'metal', 'structural metal', 'framing'],
    'electrical': ['electrical', 'lighting', 'power', 'wiring', 'electric'],
    'plumbing': ['plumbing', 'piping', 'fixtures', 'water', 'sewer'],
    'hvac': ['hvac', 'heating', 'cooling', 'ventilation', 'air conditioning', 'mechanical'],
    'roofing': ['roofing', 'roof', 'membrane', 'waterproofing'],
    'masonry': ['masonry', 'brick', 'block', 'stone'],
    'carpentry': ['carpentry', 'framing', 'wood', 'lumber', 'finish carpentry'],
    'drywall': ['drywall', 'gypsum', 'sheetrock', 'plaster'],
    'flooring': ['flooring', 'floor', 'carpet', 'tile', 'hardwood'],
    'painting': ['painting', 'paint', 'coating', 'finish'],
    'insulation': ['insulation', 'thermal'],
    'windows': ['windows', 'glazing', 'curtain wall'],
    'doors': ['doors', 'hardware', 'frames'],
    'elevators': ['elevator', 'lift'],
    'fire': ['fire', 'suppression', 'sprinkler']
  };
  
  // Check if any keywords match
  for (const [category, keywords] of Object.entries(keywordMappings)) {
    if (keywords.some(keyword => description.includes(keyword)) && 
        (target.includes(category) || keywords.some(keyword => target.includes(keyword)))) {
      return true;
    }
  }
  
  return false;
}

function calculateSimilarityScore(str1, str2) {
  // Simple similarity scoring based on common words
  const words1 = str1.split(/\s+/);
  const words2 = str2.split(/\s+/);
  
  let commonWords = 0;
  words1.forEach(word1 => {
    if (word1.length > 2 && words2.some(word2 => word2.includes(word1) || word1.includes(word2))) {
      commonWords++;
    }
  });
  
  return commonWords / Math.max(words1.length, words2.length);
}

function getDDATermById(id) {
  return ddaTerms.value.find(term => term.id === id);
}

function getColumnMappingHighlight(columnIndex) {
  const classes = [];
  
  if (ddaMapping.value.description_columns.includes(columnIndex)) {
    classes.push('highlight-description');
  }
  if (ddaMapping.value.original_budget_column === columnIndex) {
    classes.push('highlight-original-budget');
  }
  if (ddaMapping.value.current_budget_column === columnIndex) {
    classes.push('highlight-current-budget');
  }
  if (ddaMapping.value.funded_at_closing_column === columnIndex) {
    classes.push('highlight-funded-closing');
  }
  if (ddaMapping.value.current_draw_column === columnIndex) {
    classes.push('highlight-current-draw');
  }
  if (ddaMapping.value.balance_to_complete_column === columnIndex) {
    classes.push('highlight-balance-complete');
  }
  if (ddaMapping.value.draw_columns.includes(columnIndex)) {
    classes.push('highlight-draw-columns');
  }
  if (ddaMapping.value.cost_code_column === columnIndex && hasCostCodeColumn.value) {
    classes.push('highlight-cost-code');
  }
  if (ddaMapping.value.company_column === columnIndex && hasCompanyColumn.value) {
    classes.push('highlight-company');
  }
  
  return classes.join(' ');
}

function getColumnSelectionHighlight(columnIndex) {
  const classes = [];
  
  // Highlight the column being selected in the current step
  switch (currentStep.value) {
    case 'main_category_column':
      if (ddaMapping.value.main_category_column === columnIndex) {
        classes.push('highlight-description');
      }
      break;
    case 'description_columns':
    case 'single_description_columns':
      if (ddaMapping.value.description_columns.includes(columnIndex)) {
        classes.push('highlight-description');
      }
      break;
    case 'original_budget_column':
      if (ddaMapping.value.original_budget_column === columnIndex) {
        classes.push('highlight-original-budget');
      }
      break;
    case 'current_budget_column':
      if (ddaMapping.value.current_budget_column === columnIndex) {
        classes.push('highlight-current-budget');
      }
      break;
    case 'funded_at_closing_column':
      if (ddaMapping.value.funded_at_closing_column === columnIndex) {
        classes.push('highlight-funded-closing');
      }
      break;
    case 'current_draw_column':
      // 🔥 MULTI-COLUMN SUPPORT: Highlight all selected columns
      if (ddaMapping.value.current_draw_column.includes(columnIndex)) {
        classes.push('highlight-current-draw');
      }
      break;
    case 'balance_to_complete_column':
      if (ddaMapping.value.balance_to_complete_column === columnIndex) {
        classes.push('highlight-balance-complete');
      }
      break;
    case 'draw_columns':
      if (ddaMapping.value.draw_columns.includes(columnIndex)) {
        classes.push('highlight-draw-columns');
      }
      break;
    case 'contractor_column':
      if (ddaMapping.value.company_column === columnIndex) {
        classes.push('highlight-company');
      }
      break;
  }
  
  return classes.join(' ');
}

function updatePreview() {
  // Trigger reactivity for preview updates
  // This will automatically update the computed ddaPreviewRows
}

// Force refresh term displays for all matched items
function refreshTermDisplays() {
  classificationItems.value.forEach(item => {
    if (item.dda_term_id) {
      const termName = getSelectedTermName(item.dda_term_id);
      if (termName !== 'Unknown Term') {
        item.searchTerm = termName;
        
        // Ensure selectedTerm is set for display
        if (!item.selectedTerm) {
          const term = ddaTerms.value.find(t => t.id == item.dda_term_id);
          if (term) {
            item.selectedTerm = term;
          }
        }
      }
    }
  });
  
  console.log(`Refreshed displays for ${classificationItems.value.filter(i => i.dda_term_id).length} matched items`);
}

// DDA Terms Autocomplete Functions
async function searchDDATerms(item, query) {
  if (!query || query.length < 2) {
    item.searchResults = [];
    item.showDropdown = false;
    return;
  }
  
  try {
    const backendBaseUrl = apiBase.replace('/api/v1/', '');
    const response = await axios.get(`${backendBaseUrl}/api/v1/dda_terms/search`, {
      params: { q: query, limit: 20 }
    });
    
    item.searchResults = response.data || [];
    item.showDropdown = true;
    console.log(`Search for "${query}" returned ${item.searchResults.length} results`);
  } catch (error) {
    console.error('Error searching DDA terms:', error);
    item.searchResults = [];
    item.showDropdown = false;
  }
}

function selectDDATermForItem(item, term) {
  console.log('Selecting DDA term:', term);
  item.dda_term_id = term.id;
  item.searchTerm = term.term;
  item.showDropdown = false;
  item.searchResults = [];
  
  // Store the full term info for display
  item.selectedTerm = term;
}

function getSelectedTermName(termId) {
  if (!termId) return 'Unknown Term';
  
  // First check if the item has a selectedTerm with the term info
  const classItem = classificationItems.value.find(i => i.dda_term_id == termId);
  if (classItem && classItem.selectedTerm && classItem.selectedTerm.term) {
    return classItem.selectedTerm.term;
  }
  
  // Check if the item has searchTerm set (from auto-matching)
  if (classItem && classItem.searchTerm) {
    return classItem.searchTerm;
  }
  
  // Try to find in loaded ddaTerms (use == for loose comparison since IDs might be strings or numbers)
  const term = ddaTerms.value.find(t => t.id == termId);
  if (term && term.term) {
    return term.term;
  }
  
  // For ML-matched terms that aren't in our loaded set, the term name should be stored
  // in selectedTerm or searchTerm, so we shouldn't reach here often
  console.log(`Could not find term for ID: ${termId}, ddaTerms has ${ddaTerms.value.length} items`);
  return `Loading term ${termId}...`;
}

function clearSelection(item) {
  item.dda_term_id = null;
  item.searchTerm = '';
  item.showDropdown = false;
  item.searchResults = [];
  item.selectedTerm = null;
}

function handleFocus(item) {
  // Clear search term when focusing to allow new search
  if (item.dda_term_id && item.searchTerm) {
    item.searchTerm = '';
  }
  
  // Show dropdown with recent results if available
  if (item.searchResults && item.searchResults.length > 0) {
    item.showDropdown = true;
  }
}

function handleBlur(item) {
  // Small delay to allow click events on dropdown items
  setTimeout(() => {
    item.showDropdown = false;
    
    // Restore the selected term name if nothing new was selected
    if (item.dda_term_id && (!item.searchTerm || item.searchTerm.trim() === '')) {
      const termName = getSelectedTermName(item.dda_term_id);
      if (termName !== 'Unknown Term') {
        item.searchTerm = termName;
      }
    }
  }, 150);
}

function openDocumentViewer() {
  // Navigate to dedicated document viewer page
  const documentId = documentData.value?.data?.document_id || templateInfo.value?.document_id;
  if (documentId) {
    const viewerUrl = `/document-viewer/${documentId}`;
    window.open(viewerUrl, '_blank');
  } else {
    console.warn('No document ID available for viewer. Available data:', {
      documentData: documentData.value?.data,
      templateInfo: templateInfo.value
    });
  }

  // ------ NEW VISUAL COLUMN MAPPING METHODS ------

  const getMappingFieldLabel = (field) => {
    const labels = {
      description: 'Description Columns',
      original_budget: 'Original Budget',
      current_budget: 'Current Budget',
      contractor: 'Contractor',
      spent_to_date: 'Spent to Date',
      remaining_budget: 'Remaining Budget',
      funded_at_closing: 'Funded at Closing'
    };
    return labels[field] || field;
  };



  const getExcelRowClasses = (rowIndex) => {
    const bounds = tabBoundaries.value[selectedTabs.value[0]] || {};
    const headerRow = bounds.headerRow || 0;
    
    // Adjust header row index to account for the sliced data
    const adjustedHeaderRow = headerRow - (bounds.startRow || 0);
    
    return {
      'header-row': rowIndex === adjustedHeaderRow,
      'data-row': rowIndex > adjustedHeaderRow
    };
  };

  const getMappingColumnClasses = (colIndex) => {
    const classes = ['mapping-column'];
    
    // Add mapped classes based on current mappings
    if (simpleColumnMappings.value.description.includes(colIndex)) {
      classes.push('mapped-description');
    }
    if (simpleColumnMappings.value.funded_at_closing.includes(colIndex)) {
      classes.push('mapped-funded-at-closing');
    }
    if (simpleColumnMappings.value.original_budget === colIndex) {
      classes.push('mapped-original-budget');
    }
    if (simpleColumnMappings.value.current_budget === colIndex) {
      classes.push('mapped-current-budget');
    }
    
    if (simpleColumnMappings.value.spent_to_date === colIndex) {
      classes.push('mapped-spent-to-date');
    }
    if (simpleColumnMappings.value.remaining_budget === colIndex) {
      classes.push('mapped-remaining-budget');
    }
    
    return classes.join(' ');
  };

  const toggleColumnMapping = (colIndex) => {
    const field = currentMappingField.value;
    
    if (field === 'description') {
      // Multiple columns allowed for description
      const currentIndex = simpleColumnMappings.value.description.indexOf(colIndex);
      if (currentIndex > -1) {
        simpleColumnMappings.value.description.splice(currentIndex, 1);
      } else {
        simpleColumnMappings.value.description.push(colIndex);
        simpleColumnMappings.value.description.sort((a, b) => a - b);
      }
    } else if (field === 'funded_at_closing') {
      // Multiple columns allowed for funded at closing
      const currentIndex = simpleColumnMappings.value.funded_at_closing.indexOf(colIndex);
      if (currentIndex > -1) {
        simpleColumnMappings.value.funded_at_closing.splice(currentIndex, 1);
      } else {
        simpleColumnMappings.value.funded_at_closing.push(colIndex);
        simpleColumnMappings.value.funded_at_closing.sort((a, b) => a - b);
      }
    } else if (field === 'current_draw') {
      // 🔥 MULTI-COLUMN SUPPORT: Multiple columns allowed for current draw (will be summed)
      // Handle case where AI set it as integer, convert to array first
      if (!Array.isArray(simpleColumnMappings.value.current_draw)) {
        const oldValue = simpleColumnMappings.value.current_draw;
        simpleColumnMappings.value.current_draw = oldValue !== null ? [oldValue] : [];
      }
      const currentIndex = simpleColumnMappings.value.current_draw.indexOf(colIndex);
      if (currentIndex > -1) {
        simpleColumnMappings.value.current_draw.splice(currentIndex, 1);
      } else {
        simpleColumnMappings.value.current_draw.push(colIndex);
        simpleColumnMappings.value.current_draw.sort((a, b) => a - b);
      }
    } else {
      // Single column for other fields - clear if already mapped, set if not
      if (simpleColumnMappings.value[field] === colIndex) {
        simpleColumnMappings.value[field] = null;
      } else {
        simpleColumnMappings.value[field] = colIndex;
      }
    }
  };



  // Get the enhanced headers for column mapping (with multi-row header detection)
  const getColumnMappingHeaders = () => {
    const bounds = tabBoundaries.value[selectedTabs.value[0]] || {};
    const data = documentData.value?.raw_excel_data?.rows || [];
    
    console.log('🔍 getColumnMappingHeaders Debug:');
    console.log('- bounds:', bounds);
    console.log('- selectedTabs:', selectedTabs.value);
    console.log('- data rows count:', data.length);
    
    if (!data || data.length === 0) {
      console.log('❌ No data available');
      return [];
    }
    
    // Use bounds if they exist, otherwise return the first row as headers
    const headerRowIndex = bounds.headerRow !== undefined ? bounds.headerRow : 0;
    console.log('- headerRowIndex:', headerRowIndex);
    
    if (headerRowIndex >= data.length || headerRowIndex < 0) {
      console.log('❌ Invalid header row index');
      return [];
    }
    
    const headerRow = data[headerRowIndex];
    console.log('- headerRow length:', headerRow.length);
    console.log('- first 10 headers:', headerRow.slice(0, 10));
    
    // Apply column boundaries if they exist
    const startCol = bounds.startCol !== undefined ? bounds.startCol : 0;
    const endCol = bounds.endCol !== undefined ? bounds.endCol : headerRow.length - 1;
    
    console.log('- column range:', startCol, 'to', endCol);
    
    const result = headerRow.slice(startCol, endCol + 1);
    console.log('- final headers count:', result.length);
    console.log('- final headers sample:', result.slice(0, 10));
    
    return result;
  };

// Helper function to check if we're in Excel workflow  
function isExcelWorkflow() {
  return documentData.value?.raw_excel_data && ['tab_selection', 'column_mapping', 'total_rows_setup'].includes(currentStep.value);
}

// Functions to get Excel data for Step 2
function getExcelHeaders() {
  if (documentData.value?.rows && documentData.value.rows.length > 0) {
    const firstRow = documentData.value.rows[0] || [];
    return firstRow.map((header, index) => header || `Column ${index + 1}`);
  }
  if (documentData.value?.raw_excel_data?.rows && documentData.value.raw_excel_data.rows.length > 0) {
    const firstRow = documentData.value.raw_excel_data.rows[0] || [];
    return firstRow.map((header, index) => header || `Column ${index + 1}`);
  }
  return [];
}

function getExcelRows() {
  if (documentData.value?.rows) {
    return documentData.value.rows;
  }
  if (documentData.value?.raw_excel_data?.rows) {
    return documentData.value.raw_excel_data.rows;
  }
  return [];
}

// Header and Total Row Selection Functions


// Update navigation for simplified workflow
function nextStepSimplified() {
  if (currentStep.value === 'tab_selection') {
    currentStep.value = 'column_mapping';
  } else if (currentStep.value === 'column_mapping') {
    currentStep.value = 'total_rows_setup';
  } else if (currentStep.value === 'total_rows_setup') {
    // Proceed to finalization or processing
    finalize();
  }
}
}
</script>





<style scoped lang="scss">
/* Multi-row header styling for Step 3 - consistent styling for all rows */
.visual-excel-table thead {
  tr[class*="header-row-"] th {
    background: #e2e8f0 !important;
    color: #374151 !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    border-bottom: 1px solid #cbd5e1 !important;
    padding: 8px 12px !important;
  }
  
  /* Only make the last row clickable */
  tr:last-child th {
    cursor: pointer;
    
    &:hover {
      background: #d1d5db !important;
    }
  }
  
  .row-header-spacer {
    background: #e2e8f0 !important;
    border-bottom: 1px solid #cbd5e1 !important;
    width: 40px !important;
  }
}

/* Clean and simple scrollbar styling */

.draw-template-setup-container {
  padding: 8px;
  max-width: 95vw;
  margin: 0 auto;
  width: calc(95vw - 16px);
}

.template-setup-header {
  margin-bottom: 20px;
  
  h2 {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 15px;
    color: #333;
  }
  
  .contractor-template-notice {
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
    border: 1px solid #ffc107;
    border-radius: 8px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    margin-bottom: 15px;
    color: #856404;
    
    .notice-icon {
      font-size: 18px;
    }
    
    .notice-text {
      font-weight: 500;
    }
  }
  
  .template-tabs {
    display: flex;
    border-bottom: 1px solid #e0e0e0;
    
    .tab {
      padding: 8px 20px;
      cursor: pointer;
      border-bottom: 3px solid transparent;
      color: #666;
      font-weight: 500;
      font-size: 14px;
      
      &.active {
        color: #4A5FD5;
        border-bottom-color: #4A5FD5;
      }
      
      &:hover:not(.active) {
        color: #333;
      }
    }
  }
}

.current-step {
  background: #f8f9ff;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 10px;
  border-left: 3px solid #4A5FD5;
  
  h3 {
    margin: 0 0 4px 0;
    color: #333;
    font-size: 12px;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    color: #666;
    line-height: 1.2;
    font-size: 10px;
  }
}

.document-preview-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 30px;
  overflow: hidden;
}

.document-preview {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  
  h5 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 16px;
    font-weight: 600;
  }
  
  .document-image-container {
    border: 1px solid #ddd;
    border-radius: 6px;
    overflow: hidden;
    background: #f9f9f9;
    
    .document-iframe {
      width: 100%;
      height: 500px;
      border: none;
    }
    
    .document-viewer-card {
      padding: 30px;
      text-align: center;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      
      .viewer-placeholder {
        max-width: 400px;
        margin: 0 auto;
        
        .document-icon {
          font-size: 48px;
          margin-bottom: 15px;
          opacity: 0.7;
        }
        
        h6 {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }
        
        .doc-info {
          color: #666;
          font-size: 14px;
          margin-bottom: 20px;
        }
        
        .viewer-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
          
          .btn-view-doc, .btn-viewer {
            padding: 10px 20px;
            border-radius: 6px;
            text-decoration: none;
            font-weight: 500;
            transition: all 0.3s ease;
            border: none;
            cursor: pointer;
            font-size: 14px;
          }
          
          .btn-view-doc {
            background: #4A5FD5;
            color: white;
            
            &:hover {
              background: #3949b3;
              transform: translateY(-1px);
            }
          }
          
          .btn-viewer {
            background: #28a745;
            color: white;
            
            &:hover {
              background: #218838;
              transform: translateY(-1px);
            }
          }
        }
      }
    }
  }
}

.spreadsheet-viewer {
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}

.excel-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  
  th, .data-cell {
    padding: 8px 12px;
    border: 1px solid #ddd;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
    
    &:hover {
      background-color: #f0f0f0;
    }
  }
  
  th {
    background-color: #f5f5f5;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .selected-column {
    background-color: #e3f2fd !important;
    border-color: #2196F3 !important;
  }
  
  .highlight-original-budget {
    background-color: #e8f5e8;
    border-color: #4caf50;
  }
  
  .highlight-current-budget {
    background-color: #fff3e0;
    border-color: #ff9800;
  }
  
  .highlight-total-spent {
    background-color: #f3e5f5;
    border-color: #9c27b0;
  }
  
  .highlight-previously-funded {
    background-color: #e1f5fe;
    border-color: #00bcd4;
  }
  
  .highlight-current-draw {
    background-color: #fce4ec;
    border-color: #e91e63;
  }
  
  .highlight-funded-closing {
    background-color: #f9fbe7;
    border-color: #8bc34a;
  }
  
  .highlight-balance-complete {
    background-color: #fff8e1;
    border-color: #ffc107;
  }
  
  .highlight-draw-columns {
    background-color: #f3e5f5;
    border-color: #9c27b0;
  }
}

.column-mapping-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
}

.mapping-step {
  .dda-term-column {
    h4 {
      margin: 0 0 15px 0;
      color: #333;
    }
    
    .term-list {
      max-height: 400px;
      overflow-y: auto;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
    }
    
    .dda-term-item {
      padding: 12px;
      border-bottom: 1px solid #f0f0f0;
      cursor: pointer;
      transition: background-color 0.2s;
      
      &:hover {
        background-color: #f8f9ff;
      }
      
      &.selected {
        background-color: #e3f2fd;
        border-color: #2196F3;
      }
      
      .term-code {
        font-weight: 600;
        color: #4A5FD5;
        margin-bottom: 4px;
      }
      
      .term-description {
        font-weight: 500;
        margin-bottom: 4px;
      }
      
      .term-company {
        color: #666;
        font-size: 14px;
        margin-bottom: 8px;
      }
      
      .term-values {
        display: flex;
        gap: 15px;
        font-size: 12px;
        
        span {
          color: #888;
        }
      }
    }
  }
}

.classification-step {
  .classification-table {
    overflow-x: auto;
    max-height: 500px;
    overflow-y: auto;
  }
  
  .classification-grid {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    
    th, .data-cell {
      padding: 8px;
      border: 1px solid #ddd;
      text-align: left;
      white-space: nowrap;
    }
    
    th {
      background-color: #f5f5f5;
      font-weight: 600;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    .concrete-item {
      background-color: #e8f5e8;
    }
    
    .superstructure-item {
      background-color: #e3f2fd;
    }
    
    .masonry-item {
      background-color: #fff3e0;
    }
    
    .metals-item {
      background-color: #f3e5f5;
    }
    
    .carpentry-item {
      background-color: #fff8e1;
    }
    
    .dda-term {
      font-weight: 500;
      color: #4A5FD5;
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  
  button {
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  .btn-secondary {
    background-color: #f0f0f0;
    color: #666;
    
    &:hover:not(:disabled) {
      background-color: #e0e0e0;
    }
  }
  
  .btn-primary {
    background-color: #4A5FD5;
    color: white;
    
    &:hover:not(:disabled) {
      background-color: #3d4ed8;
    }
  }
  
  .btn-success {
    background-color: #28a745;
    color: white;
    
    &:hover:not(:disabled) {
      background-color: #218838;
    }
  }
}

/* Raw Excel Data Styles */
.raw-excel-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 10px;
  
  h4 {
    margin: 0 0 4px 0;
    color: #333;
    font-size: 12px;
    font-weight: 600;
  }
  
  p {
    margin: 0 0 8px 0;
    color: #666;
    font-size: 10px;
  }
}

.header-selection-step {
  .excel-preview {
    h4 {
      margin: 0 0 10px 0;
      color: #333;
    }
    
    p {
      margin: 0 0 20px 0;
      color: #666;
    }
  }
  
  .raw-excel-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8px;
    max-height: 75vh;
    overflow-y: auto;
    overflow-x: auto;
    display: block;
    
    tbody {
      display: table;
      width: 100%;
    }
    
    tr {
      cursor: pointer;
      transition: background-color 0.2s;
      height: 18px;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      &.selected-header-row {
        background-color: #e3f2fd !important;
        border: 1px solid #2196F3;
      }
    }
    
    .row-number {
      background-color: #f5f5f5;
      padding: 2px 4px;
      border: 1px solid #ddd;
      font-weight: 600;
      width: 25px;
      text-align: center;
      position: sticky;
      left: 0;
      z-index: 2;
      font-size: 8px;
    }
    
    .excel-cell {
      padding: 2px 3px;
      border: 1px solid #ddd;
      white-space: nowrap;
      min-width: 60px;
      line-height: 1.1;
      font-size: 8px;
      
      &.highlight-description {
        background-color: #2196F3 !important;
        color: white !important;
        border: 2px solid #1976d2 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-original-budget {
        background-color: #4caf50 !important;
        color: white !important;
        border: 2px solid #388e3c !important;
        font-weight: 700 !important;
      }
      
      &.highlight-current-budget {
        background-color: #ff9800 !important;
        color: white !important;
        border: 2px solid #f57c00 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-current-draw {
        background-color: #9c27b0 !important;
        color: white !important;
        border: 2px solid #7b1fa2 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-company {
        background-color: #e91e63 !important;
        color: white !important;
        border: 2px solid #c2185b !important;
        font-weight: 700 !important;
      }
    }
  }
}

.column-selection-step {
  .excel-preview {
    h4 {
      margin: 0 0 10px 0;
      color: #333;
      font-size: 14px;
    }
    
    p {
      margin: 0 0 15px 0;
      color: #666;
      font-size: 12px;
    }
  }
  
  .raw-excel-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8px;
    max-height: 75vh;
    overflow-y: auto;
    overflow-x: auto;
    display: block;
    
    tbody {
      display: table;
      width: 100%;
    }
    
    tr {
      cursor: pointer;
      transition: background-color 0.2s;
      height: 18px;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      &.selected-header-row {
        background-color: #e3f2fd !important;
        border: 1px solid #2196F3;
      }
    }
    
    .row-number {
      background-color: #f5f5f5;
      padding: 2px 4px;
      border: 1px solid #ddd;
      font-weight: 600;
      width: 25px;
      text-align: center;
      position: sticky;
      left: 0;
      z-index: 2;
      font-size: 8px;
    }
    
    .excel-cell {
      padding: 2px 3px;
      border: 1px solid #ddd;
      white-space: nowrap;
      min-width: 60px;
      line-height: 1.1;
      font-size: 8px;
      
      &.highlight-description {
        background-color: #2196F3 !important;
        color: white !important;
        border: 2px solid #1976d2 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-original-budget {
        background-color: #4caf50 !important;
        color: white !important;
        border: 2px solid #388e3c !important;
        font-weight: 700 !important;
      }
      
      &.highlight-current-budget {
        background-color: #ff9800 !important;
        color: white !important;
        border: 2px solid #f57c00 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-current-draw {
        background-color: #9c27b0 !important;
        color: white !important;
        border: 2px solid #7b1fa2 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-company {
        background-color: #e91e63 !important;
        color: white !important;
        border: 2px solid #c2185b !important;
        font-weight: 700 !important;
      }
      
      &.highlight-funded-closing {
        background-color: #8bc34a !important;
        color: white !important;
        border: 2px solid #689f38 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-balance-complete {
        background-color: #ffc107 !important;
        color: black !important;
        border: 2px solid #ff8f00 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-draw-columns {
        background-color: #9c27b0 !important;
        color: white !important;
        border: 2px solid #7b1fa2 !important;
        font-weight: 700 !important;
      }
    }
  }
}

// Total Row Selection Styles
.total-row-selection {
  margin-top: 30px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;

  h4 {
    margin: 0 0 10px 0;
    color: #1f2937;
    font-size: 18px;
    font-weight: 600;
  }

  p {
    margin: 0 0 20px 0;
    color: #6b7280;
    font-size: 14px;
  }

  .total-category-buttons {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    flex-wrap: wrap;

    .total-category-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      background: white;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 14px;
      font-weight: 500;

      &:hover {
        border-color: #3b82f6;
        background: #f3f4f6;
      }

      &.active {
        border-color: #3b82f6;
        background: #dbeafe;
        color: #1e40af;
      }

      .category-icon {
        font-size: 16px;
      }

      .category-name {
        font-weight: 600;
      }

      .selection-count {
        font-size: 12px;
        color: #6b7280;
        background: #f3f4f6;
        padding: 2px 6px;
        border-radius: 4px;
      }

      &.active .selection-count {
        background: #bfdbfe;
        color: #1e40af;
      }
    }
  }

  .selection-instructions {
    margin-bottom: 20px;
    padding: 12px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 6px;

    .selection-mode-info {
      margin: 0;
      color: #1e40af;
      font-size: 14px;

      .instruction-text {
        color: #6b7280;
        font-weight: normal;
        margin-left: 8px;
      }
    }
  }

  .total-mappings-summary {
    h5 {
      margin: 0 0 10px 0;
      color: #374151;
      font-size: 14px;
      font-weight: 600;
    }

    .mappings-list {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .mapping-category {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 12px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 6px;

        .category-label {
          font-weight: 600;
          color: #374151;
          min-width: 100px;
        }

        .row-list {
          color: #6b7280;
          font-size: 14px;
        }
      }
    }
  }
}

// Row number styling for total row selection
.clickable-row-number {
  cursor: pointer !important;
  transition: all 0.2s ease;

  &:hover {
    background-color: #dbeafe !important;
    border-color: #3b82f6 !important;
  }

  &.selectable {
    border-width: 2px;
  }

  &.total-row-hard-costs {
    background-color: #dcfce7 !important;
    border-color: #16a34a !important;
    color: #15803d !important;
    font-weight: 700 !important;
  }

  &.total-row-soft-costs {
    background-color: #fef3c7 !important;
    border-color: #d97706 !important;
    color: #92400e !important;
    font-weight: 700 !important;
  }

  &.total-row-financing {
    background-color: #e0e7ff !important;
    border-color: #7c3aed !important;
    color: #5b21b6 !important;
    font-weight: 700 !important;
  }

  &.total-row-project-total {
    background-color: #fce7f3 !important;
    border-color: #db2777 !important;
    color: #be185d !important;
    font-weight: 700 !important;
  }
}

// QA Total Mappings Card styling
.total-mappings-card {
  .total-mappings-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .total-mapping-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 8px;

      .category-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .category-icon {
          font-size: 16px;
        }

        .category-name {
          font-weight: 600;
          color: #374151;
        }
      }

      .category-status {
        display: flex;
        align-items: center;
        gap: 6px;

        .status-icon {
          &.mapped {
            color: #16a34a;
            font-weight: bold;
          }

          &.not-mapped {
            color: #9ca3af;
          }
        }

        .status-text {
          font-size: 14px;
          color: #6b7280;
        }
      }

      .category-details {
        font-size: 12px;
        color: #6b7280;
        font-style: italic;
      }
    }
  }

  .no-mappings-warning {
    padding: 12px;
    background: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 6px;
    margin-top: 12px;

    p {
      margin: 0;
      color: #92400e;
      font-size: 14px;
    }
  }
}

.date-row-selection-step {
  .excel-preview {
    h4 {
      margin: 0 0 10px 0;
      color: #333;
      font-size: 14px;
    }
    
    p {
      margin: 0 0 15px 0;
      color: #666;
      font-size: 12px;
    }
  }
  
  .raw-excel-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8px;
    max-height: 75vh;
    overflow-y: auto;
    overflow-x: auto;
    display: block;
    
    tbody {
      display: table;
      width: 100%;
    }
    
    tr {
      cursor: pointer;
      transition: background-color 0.2s;
      height: 18px;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      &.selected-header-row {
        background-color: #e3f2fd !important;
        border: 1px solid #2196F3;
      }
      
      &.selected-date-row {
        background-color: #fff3cd !important;
        border: 1px solid #ffc107;
        font-weight: 600;
      }
      
      &.clickable-row {
        cursor: pointer;
      }
    }
    
    .row-number {
      background-color: #f5f5f5;
      padding: 2px 4px;
      border: 1px solid #ddd;
      font-weight: 600;
      width: 25px;
      text-align: center;
      position: sticky;
      left: 0;
      z-index: 2;
      font-size: 8px;
    }
    
    .excel-cell {
      padding: 2px 3px;
      border: 1px solid #ddd;
      white-space: nowrap;
      min-width: 60px;
      line-height: 1.1;
      font-size: 8px;
      
      &.highlighted-draw-column {
        background-color: #fff8e1;
        border: 1px solid #ffeaa7;
      }
    }
  }
  
  .date-row-info {
    margin-top: 15px;
    padding: 10px;
    background-color: #e8f5e8;
    border-radius: 4px;
    border: 1px solid #c3e6cb;
    
    p {
      margin: 5px 0;
      font-size: 12px;
      color: #155724;
    }
  }
}

.description-structure-step {
  .structure-choice {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 20px;
    
    h4 {
      margin: 0 0 8px 0;
      color: #333;
      font-size: 14px;
    }
    
    p {
      margin: 0 0 20px 0;
      color: #666;
      font-size: 12px;
    }
    
    .choice-options {
      display: flex;
      gap: 20px;
      
      .choice-option {
        flex: 1;
        padding: 20px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        
        &:hover {
          border-color: #4A5FD5;
          background-color: #f8f9ff;
        }
        
        &.selected {
          border-color: #4A5FD5;
          background-color: #e3f2fd;
        }
        
        h5 {
          margin: 0 0 8px 0;
          color: #333;
          font-size: 14px;
          font-weight: 600;
        }
        
        p {
          margin: 0 0 10px 0;
          color: #666;
          font-size: 12px;
        }
        
        .example {
          background: #f8f9fa;
          padding: 10px;
          border-radius: 4px;
          font-size: 11px;
          color: #555;
          border-left: 3px solid #4A5FD5;
        }
      }
    }
  }
}

.finalize-step {
  .mapping-summary {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 15px;
    
    h4 {
      margin: 0 0 8px 0;
      color: #333;
      font-size: 14px;
    }
    
    p {
      margin: 0 0 15px 0;
      color: #666;
      font-size: 12px;
    }
    
    .summary-list {
      margin-bottom: 20px;
      
      .summary-item {
        padding: 8px 12px;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 4px;
        margin-bottom: 8px;
        font-size: 12px;
        
        strong {
          color: #333;
        }
      }
    }
    
    .final-preview {
      h5 {
        margin: 0 0 10px 0;
        color: #333;
        font-size: 12px;
      }
    }
  }
}

.dda-column-mapping-step {
  .mapping-interface {
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
  }
  
  .excel-data-section {
    flex: 3;
    
    h4 {
      margin: 0 0 6px 0;
      color: #333;
      font-size: 14px;
    }
    
    p {
      margin: 0 0 10px 0;
      color: #666;
      font-size: 12px;
    }
  }
  
  .mapping-controls-section {
    flex: 1;
    min-width: 300px;
    
    h4 {
      margin: 0 0 6px 0;
      color: #333;
      font-size: 14px;
    }
    
    p {
      margin: 0 0 10px 0;
      color: #666;
      font-size: 12px;
    }
    
    h5 {
      margin: 15px 0 8px 0;
      color: #333;
      font-size: 13px;
    }
  }
  
  .excel-data-container {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 75vh;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    width: 100%;
    position: relative;
  }
  
  .excel-mapping-table {
    border-collapse: collapse;
    font-size: 8px;
    white-space: nowrap;
    
    tr {
      cursor: pointer;
      transition: background-color 0.2s;
      height: 18px;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      &.selected-header-row {
        background-color: #e3f2fd !important;
        border: 1px solid #2196F3;
      }
    }
    
    .row-number {
      background-color: #f5f5f5;
      padding: 2px 4px;
      border: 1px solid #ddd;
      font-weight: 600;
      width: 25px;
      text-align: center;
      position: sticky;
      left: 0;
      z-index: 2;
      font-size: 8px;
    }
    
    .excel-cell {
      padding: 2px 3px;
      border: 1px solid #ddd;
      white-space: nowrap;
      min-width: 60px;
      line-height: 1.1;
      
      &.highlight-description {
        background-color: #2196F3 !important;
        color: white !important;
        border: 2px solid #1976d2 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-original-budget {
        background-color: #4caf50 !important;
        color: white !important;
        border: 2px solid #388e3c !important;
        font-weight: 700 !important;
      }
      
      &.highlight-current-budget {
        background-color: #ff9800 !important;
        color: white !important;
        border: 2px solid #f57c00 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-current-draw {
        background-color: #9c27b0 !important;
        color: white !important;
        border: 2px solid #7b1fa2 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-cost-code {
        background-color: #fff8e1;
        border-color: #ffc107;
        font-weight: 600;
      }
      
      &.highlight-company {
        background-color: #e91e63 !important;
        color: white !important;
        border: 2px solid #c2185b !important;
        font-weight: 700 !important;
      }
    }
  }
  
  .required-mappings {
    margin-bottom: 15px;
    
    .mapping-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
      
      label {
        min-width: 120px;
        font-weight: 500;
        color: #333;
        font-size: 11px;
      }
      
      select {
        flex: 1;
        padding: 4px 8px;
        border: 1px solid #ddd;
        border-radius: 3px;
        font-size: 10px;
        
        &:focus {
          outline: none;
          border-color: #4A5FD5;
          box-shadow: 0 0 0 1px rgba(74, 95, 213, 0.1);
        }
      }
      
      &.highlight-original {
        background-color: #e8f5e8;
        padding: 6px;
        border-radius: 3px;
        border-left: 3px solid #4caf50;
        
        label {
          color: #2e7d32;
          font-weight: 600;
        }
      }
      
      &.highlight-current {
        background-color: #fff3e0;
        padding: 6px;
        border-radius: 3px;
        border-left: 3px solid #ff9800;
        
        label {
          color: #ef6c00;
          font-weight: 600;
        }
      }
      
      &.highlight-draw {
        background-color: #f3e5f5;
        padding: 6px;
        border-radius: 3px;
        border-left: 3px solid #9c27b0;
        
        label {
          color: #7b1fa2;
          font-weight: 600;
        }
      }
    }
  }
  
  .optional-mappings {
    .optional-mapping-row {
      margin-bottom: 10px;
      
      .checkbox-container {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 6px;
        
        input[type="checkbox"] {
          width: 12px;
          height: 12px;
          accent-color: #4A5FD5;
        }
        
        label {
          font-weight: 500;
          color: #333;
          cursor: pointer;
          font-size: 11px;
        }
      }
      
      select {
        width: 100%;
        padding: 4px 8px;
        border: 1px solid #ddd;
        border-radius: 3px;
        font-size: 10px;
        
        &:focus {
          outline: none;
          border-color: #4A5FD5;
          box-shadow: 0 0 0 1px rgba(74, 95, 213, 0.1);
        }
      }
    }
  }
  
  .mapping-preview-section {
    margin-top: 15px;
    
    h5 {
      margin: 0 0 8px 0;
      color: #333;
      font-size: 12px;
    }
  }

  .summary-list {
    margin-bottom: 20px;
    
    .summary-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      margin-bottom: 8px;
      background: #f8f9fa;
      border-radius: 4px;
      border-left: 3px solid #e0e0e0;
      
      strong {
        color: #333;
        font-size: 12px;
        min-width: 180px;
      }
      
      .column-mapping {
        font-size: 11px;
        padding: 4px 8px;
        border-radius: 3px;
        background: #fff;
        border: 1px solid #ddd;
        color: #666;
        
        &.mapped {
          background: #e8f5e8;
          border-color: #4caf50;
          color: #2e7d32;
          font-weight: 600;
        }
      }
    }
  }
  
  .dda-preview-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8px;
    
    th, .data-cell {
      padding: 2px 3px;
      border: 1px solid #ddd;
      text-align: left;
      white-space: nowrap;
      line-height: 1.1;
      height: 18px;
    }
    
    th {
      background-color: #f5f5f5;
      font-weight: 600;
      font-size: 8px;
    }
    
    .currency {
      text-align: right;
      font-weight: 500;
    }
  }
}

/* Loading Styles */
.loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 30px;
}

.loading-spinner {
  text-align: center;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #4A5FD5;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px auto;
  }
  
  p {
    color: #666;
    font-size: 16px;
    margin: 0;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* DDA Term Mapping Styles */
.dda-term-mapping-step {
  .mapping-interface {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
    
    h4 {
      margin: 0 0 10px 0;
      color: #333;
      font-size: 16px;
      font-weight: 600;
    }
    
    p {
      margin: 0 0 20px 0;
      color: #666;
      font-size: 12px;
      line-height: 1.4;
    }
  }
  
  .dda-mapping-table-container {
    max-height: 600px;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    /* Make scroll bars always visible and styled */
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
    
    &::-webkit-scrollbar {
      width: 12px;
      height: 12px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f7fafc;
      border-radius: 6px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #cbd5e0;
      border-radius: 6px;
      border: 2px solid #f7fafc;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: #a0aec0;
    }
    
    &::-webkit-scrollbar-corner {
      background: #f7fafc;
    }
  }
  
  .dda-term-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .auto-match-badge {
    background: #28a745;
    color: white;
    font-size: 9px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 3px;
    white-space: nowrap;
  }
  
  .dda-term-select.auto-matched {
    border-color: #28a745;
    background-color: #f8fff9;
  }
  
  .dda-mapping-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    
    th, .data-cell {
      padding: 8px 10px;
      border: 1px solid #e0e0e0;
      text-align: left;
      white-space: nowrap;
      vertical-align: middle;
    }
    
    th {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      font-weight: 600;
      position: sticky;
      top: 0;
      z-index: 10;
      color: #495057;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      
      &.dda-term-header {
        background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
        color: #1565c0;
        min-width: 220px;
      }
      
      &.description-header {
        background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
        color: #7b1fa2;
        min-width: 250px;
      }
      
      &.budget-header, &.billing-header {
        background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
        color: #2e7d32;
        min-width: 120px;
        text-align: center;
      }
      
      &.adjustment-header {
        background: linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%);
        color: #ef6c00;
        min-width: 120px;
        text-align: center;
      }
      
      &.percent-header {
        background: linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%);
        color: #c2185b;
        min-width: 100px;
        text-align: center;
      }
      
      &.cost-code-header, &.company-header {
        background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
        color: #616161;
        min-width: 100px;
      }
    }
    
    tbody tr {
      &:nth-child(even) {
        background-color: #fafafa;
      }
      
      &:hover {
        background-color: #f0f8ff !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      
      &.selected-row {
        background-color: #e3f2fd !important;
        border-left: 4px solid #2196f3;
      }
    }
    
    .dda-term-cell {
      min-width: 220px;
      
      .dda-term-select {
        width: 100%;
        padding: 4px 6px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 10px;
        background: white;
        color: #333;
        
        &:focus {
          outline: none;
          border-color: #4A5FD5;
          box-shadow: 0 0 0 2px rgba(74, 95, 213, 0.2);
        }
        
        option {
          padding: 4px;
          font-size: 10px;
        }
      }
    }
    
    .description-cell {
      max-width: 250px;
      word-wrap: break-word;
      white-space: normal;
      line-height: 1.3;
      color: #495057;
      font-weight: 500;
    }
    
    .currency {
      text-align: right;
      font-weight: 600;
      color: #2e7d32;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }
    
    .percent {
      text-align: center;
      font-weight: 600;
      color: #c2185b;
    }
  }
  
  .mapping-progress {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 15px;
    margin-top: 20px;
    
    .progress-info {
      margin-bottom: 10px;
      font-size: 12px;
      color: #495057;
      font-weight: 500;
    }
    
    .progress-bar {
      width: 100%;
      height: 8px;
      background-color: #e9ecef;
      border-radius: 4px;
      overflow: hidden;
      
      .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #4caf50 0%, #66bb6a 100%);
        transition: width 0.3s ease;
        border-radius: 4px;
      }
    }
  }
}

// DDA Terms Autocomplete Styles
.dda-autocomplete {
  position: relative;
  width: 100%;
  
  &.auto-matched {
    .dda-search-input {
      border-color: #4caf50;
      background-color: #f1f8f4;
    }
  }
}

.dda-search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.2s;
  
  &:focus {
    border-color: #4A5FD5;
    box-shadow: 0 0 0 2px rgba(74, 95, 213, 0.1);
  }
  
  &.has-selection {
    border-color: #4A5FD5;
    background-color: #f8f9ff;
  }
  
  &.auto-matched {
    border-color: #4caf50;
    background-color: #e8f5e8;
    font-weight: 500;
    
    &::placeholder {
      color: #2e7d32;
      font-weight: 500;
    }
  }
}

.dda-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
  margin-top: 2px;
}

.dda-option {
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f5f5f5;
  
  &:hover {
    background-color: #f8f9ff;
  }
  
  &.selected {
    background-color: #e3f2fd;
    border-left: 3px solid #4A5FD5;
  }
  
  &:last-child {
    border-bottom: none;
  }
}

.term-name {
  font-weight: 500;
  color: #333;
  font-size: 12px;
  line-height: 1.3;
}

.term-category {
  font-size: 10px;
  color: #666;
  margin-top: 2px;
  font-style: italic;
}

.selected-term-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: #e8f5e8;
  border-radius: 4px;
  margin-top: 4px;
  border: 1px solid #c8e6c9;
}

.selected-term-name {
  font-size: 11px;
  color: #2e7d32;
  font-weight: 500;
}

.clear-selection {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  padding: 0;
  margin-left: 8px;
  
  &:hover {
    color: #d32f2f;
  }
}

.no-results {
  padding: 12px;
  color: #666;
  font-size: 12px;
  text-align: center;
  font-style: italic;
}

.auto-match-badge {
  background: #4caf50;
  color: white;
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: bold;
  margin-right: 8px;
  display: inline-block;
}

// Processing Section Styles
.processing-section {
  padding: 40px 20px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
  margin: 20px 0;
}

.processing-content {
  max-width: 600px;
  margin: 0 auto;
}

.processing-spinner {
  margin-bottom: 20px;
  
  .spinner {
    width: 60px;
    height: 60px;
    border: 6px solid #f3f3f3;
    border-top: 6px solid #4A5FD5;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  }
}

.processing-content h3 {
  color: #333;
  margin-bottom: 10px;
  font-size: 24px;
}

.processing-status {
  color: #666;
  font-size: 16px;
  margin-bottom: 20px;
  font-style: italic;
}

.processing-progress {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  
  .progress-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    font-size: 14px;
    color: #555;
  }
  
  .progress-bar {
    width: 100%;
    height: 12px;
    background-color: #e9ecef;
    border-radius: 6px;
    overflow: hidden;
    
    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #4A5FD5 0%, #6c5ce7 100%);
      transition: width 0.3s ease;
      border-radius: 6px;
    }
  }
}

.processing-info {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  text-align: left;
  
  p {
    margin-bottom: 10px;
    color: #333;
  }
  
  ul {
    margin: 10px 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      color: #555;
      line-height: 1.4;
    }
  }
  
  .processing-note {
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 4px;
    padding: 12px;
    margin-top: 15px;
    color: #856404;
    font-size: 14px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// G703 Specific Styles
.raw-g703-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  
  h4 {
    color: #2e7d32;
    margin-bottom: 10px;
    font-size: 18px;
    font-weight: 600;
  }
  
  p {
    color: #666;
    margin-bottom: 15px;
    line-height: 1.5;
  }
}

.g703-table-setup-step,
.g703-column-mapping-step {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  
  .setup-header,
  .mapping-header {
    margin-bottom: 25px;
    
    h4 {
      color: #333;
      font-size: 18px;
      margin-bottom: 10px;
    }
    
    p {
      color: #666;
      margin-bottom: 10px;
      line-height: 1.5;
    }
    
    .setup-note {
      background: #e3f2fd;
      border: 1px solid #90caf9;
      border-radius: 4px;
      padding: 12px;
      color: #1565c0;
      font-size: 14px;
    }
  }
  
  .mapping-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-top: 15px;
    
    .legend-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      
      .legend-color {
        width: 16px;
        height: 16px;
        border-radius: 3px;
        display: inline-block;
      }
    }
  }
  
  .raw-table-container {
    overflow-x: scroll;
    max-height: 400px;
    overflow-y: scroll;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    margin-bottom: 20px;
    
    /* Simple scroll bars - always visible */
    &::-webkit-scrollbar {
      width: 16px;
      height: 16px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f5f9;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #94a3b8;
      border-radius: 8px;
    }
    
    &::-webkit-scrollbar-button {
      display: block;
      background: #cbd5e1;
      height: 16px;
      width: 16px;
    }
  }
  
  .raw-g703-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
    min-width: 600px;
    
    tr {
      cursor: pointer;
      transition: background-color 0.2s;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      &.selected-header-row {
        background-color: #e3f2fd !important;
        border: 2px solid #2196F3;
        font-weight: 600;
      }
    }
    
    .row-number {
      background-color: #f5f5f5;
      padding: 4px 6px;
      border: 1px solid #ddd;
      font-weight: 600;
      width: 40px;
      text-align: center;
      position: sticky;
      left: 0;
      z-index: 2;
      font-size: 10px;
    }
    
    .table-cell {
      padding: 4px 6px;
      border: 1px solid #ddd;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
      font-size: 10px;
      
      &.clickable-cell {
        cursor: pointer;
        transition: background-color 0.2s;
        
        &:hover {
          background-color: #f0f8ff;
        }
      }
      
      // Column highlighting
      &.highlight-description {
        background-color: #2196F3 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-scheduled_value {
        background-color: #4caf50 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-budget_transfer {
        background-color: #9c27b0 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-work_completed_previous {
        background-color: #ff9800 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-work_completed_this_period {
        background-color: #9c27b0 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-materials_stored {
        background-color: #f44336 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-total_completed {
        background-color: #607d8b !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-balance_to_finish {
        background-color: #795548 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-retainage {
        background-color: #e91e63 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-mapped-other {
        background-color: #e0e0e0 !important;
        color: #666 !important;
        font-weight: 500;
        border: 1px solid #bbb;
      }
      
      // Additional G703 field highlights
      &.highlight-trade_code {
        background-color: #673ab7 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-contractor_names {
        background-color: #009688 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-owner_previous_approved {
        background-color: #3f51b5 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-percent_complete {
        background-color: #ff5722 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-retainage_percent {
        background-color: #8bc34a !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-retainage_this_period {
        background-color: #cddc39 !important;
        color: #333 !important;
        font-weight: 600;
      }
      
      &.highlight-retainage_total {
        background-color: #ffeb3b !important;
        color: #333 !important;
        font-weight: 600;
      }
      
      &.highlight-adjusted_schedule_values {
        background-color: #00bcd4 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-subcontractor_completed {
        background-color: #ff6f00 !important;
        color: white !important;
        font-weight: 600;
      }
      
      &.highlight-owner_approved_completed {
        background-color: #1976d2 !important;
        color: white !important;
        font-weight: 600;
      }
    }
  }
}

.g703-column-mapping-step {
  .column-selection-interface {
    .current-mapping {
      margin-bottom: 25px;
      
      h5 {
        color: #333;
        margin-bottom: 15px;
      }
      
              .template-actions {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #dee2e6;
          
          .template-btn {
            padding: 8px 16px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            border: none;
            
            &.btn-secondary {
              background: #6c757d;
              color: white;
              
              &:hover {
                background: #5a6268;
                transform: translateY(-1px);
              }
            }
            
            &.btn-primary {
              background: #4A5FD5;
              color: white;
              
              &:hover {
                background: #3a4bc7;
                transform: translateY(-1px);
              }
            }
          }
        }
        
        .selection-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 15px;
        
        .mapping-btn {
          padding: 8px 12px;
          border: 2px solid #e0e0e0;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.3s ease;
          
          &:hover {
            border-color: #4A5FD5;
          }
          
          &.active {
            border-color: #4A5FD5;
            background: #4A5FD5;
            color: white;
            font-weight: 600;
          }
        }
      }
      
              .mapping-instruction {
          color: #666;
          font-size: 14px;
          margin: 0;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 4px;
          
          .current-mapping {
            color: #4caf50;
            font-weight: 600;
            margin-left: 10px;
          }
        }
    }
    
    .mapping-summary {
      margin-top: 25px;
      padding: 20px;
      background: #f8f9fa;
      border-radius: 6px;
      
      h5 {
        color: #333;
        margin-bottom: 15px;
      }
      
      .mapping-list {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 10px;
        
        .mapping-summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          background: white;
          border-radius: 4px;
          font-size: 12px;
          
          .field-label {
            font-weight: 600;
            color: #333;
          }
          
          .mapped-column {
            color: #999;
            
            &.mapped {
              color: #4caf50;
              font-weight: 600;
            }
          }
        }
      }
    }
  }
}

.g703-data-preview {
  margin-bottom: 20px;
  
  h4 {
    color: #333;
    font-size: 16px;
    margin-bottom: 10px;
  }
  
  p {
    color: #666;
    margin-bottom: 15px;
    font-size: 14px;
  }
}

.g703-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  th {
    background: linear-gradient(135deg, #4A5FD5 0%, #6c5ce7 100%);
    color: white;
    padding: 12px 8px;
    text-align: left;
    font-weight: 600;
    font-size: 11px;
    border: none;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  .data-cell {
    padding: 10px 8px;
    border-bottom: 1px solid #e0e0e0;
    vertical-align: top;
    
    &.description-cell {
      max-width: 250px;
      word-wrap: break-word;
      line-height: 1.3;
    }
    
    &.currency {
      text-align: right;
      font-family: 'Monaco', 'Courier New', monospace;
      font-size: 11px;
      white-space: nowrap;
    }
  }
  
  tbody tr:hover {
    background-color: #f8f9ff;
  }
  
  tbody tr:nth-child(even) {
    background-color: #fafafa;
  }
}

.g703-review-step {
  text-align: center;
  padding: 30px 20px;
  
  .review-actions {
    max-width: 600px;
    margin: 0 auto;
    
    p {
      font-size: 16px;
      color: #333;
      margin-bottom: 15px;
      line-height: 1.5;
    }
    
    .action-buttons {
      margin-top: 25px;
      
      .btn-primary {
        background: linear-gradient(135deg, #4A5FD5 0%, #6c5ce7 100%);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(74, 95, 213, 0.3);
        }
      }
    }
  }
}

.g703-correction-step {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  
  .correction-header {
    margin-bottom: 25px;
    
    h4 {
      color: #333;
      font-size: 18px;
      margin-bottom: 10px;
    }
    
    p {
      color: #666;
      margin-bottom: 10px;
      line-height: 1.5;
    }
    
    .correction-note {
      background: #fff3cd;
      border: 1px solid #ffeaa7;
      border-radius: 4px;
      padding: 12px;
      color: #856404;
      font-size: 14px;
    }
  }
  
  .editable-g703-table-container {
    overflow-x: scroll;  /* Always show horizontal scroll */
    margin-bottom: 25px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    max-height: 500px;
    overflow-y: scroll;  /* Always show vertical scroll */
    
    /* Make scroll bars always visible and styled */
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
    
    &::-webkit-scrollbar {
      width: 12px;
      height: 12px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f7fafc;
      border-radius: 6px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #cbd5e0;
      border-radius: 6px;
      border: 2px solid #f7fafc;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: #a0aec0;
    }
    
    &::-webkit-scrollbar-corner {
      background: #f7fafc;
    }
    border-radius: 6px;
  }
  
  .editable-g703-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    min-width: 800px;
    
    th {
      background: linear-gradient(135deg, #4A5FD5 0%, #6c5ce7 100%);
      color: white;
      padding: 12px 8px;
      text-align: left;
      font-weight: 600;
      font-size: 11px;
      border: none;
      position: sticky;
      top: 0;
      z-index: 1;
    }
    
    .editable-row {
      &:nth-child(even) {
        background-color: #fafafa;
      }
      
      &:hover {
        background-color: #f0f4ff;
      }
      
      .data-cell {
        padding: 8px;
        border-bottom: 1px solid #e0e0e0;
        vertical-align: top;
      }
    }
    
    .description-edit-cell {
      width: 40%;
      min-width: 200px;
      
      .description-input {
        width: 100%;
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 8px;
        font-size: 12px;
        resize: vertical;
        min-height: 40px;
        
        &:focus {
          outline: none;
          border-color: #4A5FD5;
          box-shadow: 0 0 0 2px rgba(74, 95, 213, 0.2);
        }
      }
    }
    
    .currency-edit-cell {
      width: 12%;
      min-width: 100px;
      
      .currency-input {
        width: 100%;
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 6px 8px;
        font-size: 12px;
        text-align: right;
        font-family: 'Monaco', 'Courier New', monospace;
        
        &:focus {
          outline: none;
          border-color: #4A5FD5;
          box-shadow: 0 0 0 2px rgba(74, 95, 213, 0.2);
        }
      }
    }
    
    .actions-cell {
      width: 60px;
      text-align: center;
      
      .delete-btn {
        background: none;
        border: none;
        font-size: 16px;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        transition: background-color 0.2s;
        
        &:hover {
          background-color: #ffebee;
        }
      }
    }
  }
  
  .correction-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 0;
    border-top: 1px solid #e0e0e0;
    
    .add-item-btn {
      background: #28a745;
      color: white;
      border: none;
      padding: 10px 16px;
      border-radius: 6px;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background: #218838;
        transform: translateY(-1px);
      }
    }
    
    .correction-summary {
      p {
        color: #333;
        margin: 0;
        font-size: 14px;
      }
    }
    
    .continue-btn {
      background: linear-gradient(135deg, #4A5FD5 0%, #6c5ce7 100%);
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(74, 95, 213, 0.3);
      }
    }
  }
}

.g703-dda-mapping-step {
  .mapping-interface {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
    
    h4 {
      color: #333;
      margin-bottom: 10px;
      font-size: 18px;
    }
    
    p {
      color: #666;
      margin-bottom: 20px;
      font-size: 14px;
    }
  }
}

/* Hierarchical Description Styling */
.hierarchical-description {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.full-hierarchy {
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-path {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
}

.hierarchy-info {
  display: flex;
  align-items: center;
  gap: 6px;
}

.depth-badge {
  background: #e3f2fd;
  color: #1976d2;
  font-size: 0.75em;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 500;
}

/* Raw Excel Data Styles - COPIED FROM WORKING VERSION */
.raw-excel-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 10px;
  
  h4 {
    margin: 0 0 4px 0;
    color: #333;
    font-size: 12px;
    font-weight: 600;
  }
  
  p {
    margin: 0 0 8px 0;
    color: #666;
    font-size: 10px;
  }
}

.tab-selection-step {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  padding: 32px;
  margin: 20px 0;
  
  h3 {
    color: #1e40af;
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  p {
    color: #64748b;
    font-size: 16px;
    margin: 0 0 32px 0;
    line-height: 1.6;
  }
  
  .tabs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 24px;
    margin-bottom: 32px;
  }
  
  .tab-card {
    background: #f8fafc;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    
    &:hover {
      border-color: #3b82f6;
      box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
      transform: translateY(-2px);
    }
    
    &.selected {
      border-color: #2563eb;
      background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
      box-shadow: 0 8px 25px rgba(37, 99, 235, 0.25);
      
      .tab-header {
        .tab-checkbox {
          background: #2563eb;
          border-color: #2563eb;
          
          &:after {
            content: '✓';
            color: white;
            font-weight: bold;
            font-size: 12px;
          }
        }
        
        .tab-name {
          color: #1e40af;
          font-weight: 600;
        }
      }
      
      .tab-stats {
        .stat {
          color: #1e40af;
          
          strong {
            color: #1d4ed8;
          }
        }
      }
    }
    
    .tab-header {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 20px;
      
      .tab-checkbox {
        width: 20px;
        height: 20px;
        border: 2px solid #cbd5e1;
        border-radius: 4px;
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        transition: all 0.2s ease;
      }
      
      .tab-name {
        font-size: 18px;
        font-weight: 600;
        color: #1e293b;
        flex: 1;
      }
    }
    
    .tab-stats {
      display: flex;
      gap: 24px;
      margin-bottom: 20px;
      
      .stat {
        text-align: center;
        color: #64748b;
        font-size: 14px;
        
        strong {
          display: block;
          font-size: 20px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 4px;
        }
      }
    }
    
    .tab-preview {
      background: white;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      overflow: hidden;
      
      .preview-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
        
        thead {
          background: #f9fafb;
          
          th {
            padding: 8px 12px;
            border-bottom: 1px solid #e5e7eb;
            text-align: left;
            font-weight: 600;
            color: #374151;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
        }
        
        tbody {
          .data-cell {
            padding: 8px 12px;
            border-bottom: 1px solid #f3f4f6;
            color: #6b7280;
            font-size: 12px;
            max-width: 120px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            
            &:last-child {
              color: #9ca3af;
              font-style: italic;
            }
          }
          
          tr:last-child .data-cell {
            border-bottom: none;
          }
        }
      }
    }
  }
  
  .step-1-footer {
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
    
    .selection-summary {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 20px 24px;
      
      .summary-text {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        font-weight: 600;
        color: #374151;
        
        .summary-icon {
          font-size: 18px;
        }
      }
      
      .continue-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        color: white;
        border: none;
        border-radius: 8px;
        padding: 12px 24px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        
        .btn-text {
          font-weight: 600;
        }
        
        .btn-icon {
          font-size: 16px;
        }
        
        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
        }
      }
    }
  }
  
  .excel-preview {
    h4 {
      margin: 0 0 10px 0;
      color: #333;
    }
    
    p {
      margin: 0 0 20px 0;
      color: #666;
    }
  }
  
  .raw-excel-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8px;
    max-height: 75vh;
    overflow-y: auto;
    overflow-x: auto;
    display: block;
    
    tbody {
      display: table;
      width: 100%;
    }
    
    tr {
      cursor: pointer;
      transition: background-color 0.2s;
      height: 18px;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      &.selected-header-row {
        background-color: #e3f2fd !important;
        border: 1px solid #2196F3;
      }
    }
    
    .row-number {
      background-color: #f5f5f5;
      padding: 2px 4px;
      border: 1px solid #ddd;
      font-weight: 600;
      width: 25px;
      text-align: center;
      position: sticky;
      left: 0;
      z-index: 2;
      font-size: 8px;
    }
    
    .excel-cell {
      padding: 2px 3px;
      border: 1px solid #ddd;
      white-space: nowrap;
      min-width: 60px;
      line-height: 1.1;
      font-size: 8px;
      
      &.highlight-description {
        background-color: #2196F3 !important;
        color: white !important;
        border: 2px solid #1976d2 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-original-budget {
        background-color: #4caf50 !important;
        color: white !important;
        border: 2px solid #388e3c !important;
        font-weight: 700 !important;
      }
      
      &.highlight-current-budget {
        background-color: #ff9800 !important;
        color: white !important;
        border: 2px solid #f57c00 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-current-draw {
        background-color: #9c27b0 !important;
        color: white !important;
        border: 2px solid #7b1fa2 !important;
        font-weight: 700 !important;
      }
      
      &.highlight-company {
        background-color: #e91e63 !important;
        color: white !important;
        border: 2px solid #c2185b !important;
        font-weight: 700 !important;
      }
    }
  }
}

.boundary-definition-step {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 24px;
  margin: 16px 0;
  
  h3 {
    color: #2563eb;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }
  
  p {
    color: #6b7280;
    font-size: 14px;
    margin: 0 0 24px 0;
    line-height: 1.5;
  }
  
  .boundary-instructions {
    margin-bottom: 24px;
    
    .instruction-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 16px;
      
      .instruction-step {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        .step-number {
          background: #2563eb;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          margin-right: 12px;
          flex-shrink: 0;
        }
        
        .step-text {
          color: #374151;
          font-size: 14px;
          line-height: 1.4;
          
          .highlight-blue {
            color: #2563eb;
            font-weight: 600;
          }
        }
      }
    }
    
    .current-selection {
      background: #e0f2fe;
      border: 1px solid #0ea5e9;
      border-radius: 6px;
      padding: 12px;
      font-size: 13px;
      color: #0c4a6e;
      
      strong {
        color: #0369a1;
      }
      
      .multi-header-indicator {
        margin-top: 10px;
        padding: 10px 15px;
        background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
        border: 1px solid #3b82f6;
        border-radius: 6px;
        color: #1e40af;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 8px;
        
        strong {
          color: #1d4ed8;
        }
      }
    }
  }
  
  .full-excel-view {
    .excel-table-container {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: scroll !important;  /* FORCE scroll bars */
      max-height: 60vh;
      background: white;
      margin-bottom: 20px;
      
      /* NUCLEAR FORCE scroll bars inside Excel containers */
      &::-webkit-scrollbar {
        width: 20px !important;
        height: 20px !important;
        -webkit-appearance: none !important;
        background: #e2e8f0 !important;
        display: block !important;
      }
      
      &::-webkit-scrollbar-track {
        background: #e2e8f0 !important;
        border: 2px solid #cbd5e1 !important;
        border-radius: 0 !important;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #64748b !important;
        border: 2px solid #e2e8f0 !important;
        border-radius: 0 !important;
        min-height: 40px !important;
      }
      
      &::-webkit-scrollbar-thumb:hover {
        background: #475569 !important;
      }
      
      &::-webkit-scrollbar-button {
        display: block !important;
        height: 20px !important;
        width: 20px !important;
        background: #cbd5e1 !important;
        border: 1px solid #94a3b8 !important;
        border-radius: 0 !important;
      }
      
      &::-webkit-scrollbar-button:hover {
        background: #94a3b8 !important;
      }
      
      &::-webkit-scrollbar-button:vertical:start:decrement {
        background: #cbd5e1 url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23475569"><path d="M7 14l5-5 5 5z"/></svg>') center no-repeat !important;
        background-size: 12px !important;
      }
      
      &::-webkit-scrollbar-button:vertical:end:increment {
        background: #cbd5e1 url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23475569"><path d="M7 10l5 5 5-5z"/></svg>') center no-repeat !important;
        background-size: 12px !important;
      }
      
      &::-webkit-scrollbar-button:horizontal:start:decrement {
        background: #cbd5e1 url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23475569"><path d="M14 7l-5 5 5 5z"/></svg>') center no-repeat !important;
        background-size: 12px !important;
      }
      
      &::-webkit-scrollbar-button:horizontal:end:increment {
        background: #cbd5e1 url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23475569"><path d="M10 7l5 5-5 5z"/></svg>') center no-repeat !important;
        background-size: 12px !important;
      }
      
      &::-webkit-scrollbar-corner {
        background: #e2e8f0 !important;
        border: 1px solid #cbd5e1 !important;
      }
      
      /* FORCE Firefox scroll bars */
      scrollbar-width: auto !important;
      scrollbar-color: #64748b #e2e8f0 !important;
      
      &::-webkit-scrollbar {
        width: 12px;
        height: 12px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f7fafc;
        border-radius: 6px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #cbd5e0;
        border-radius: 6px;
        border: 2px solid #f7fafc;
      }
      
      &::-webkit-scrollbar-thumb:hover {
        background: #a0aec0;
      }
      
      &::-webkit-scrollbar-corner {
        background: #f7fafc;
      }
      
      .visual-excel-table {
        width: auto;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 11px;
        table-layout: auto; /* Let browser optimize column widths */
        
                 .excel-row {
           background-color: white;
           
           &:hover {
             background-color: #f8fafc;
           }
           
           &.header-row {
             background-color: #3b82f6 !important;
             border: 2px solid #1e40af;
             
             .excel-cell {
               font-weight: 600;
               color: white;
             }
           }
           
                     &.data-row {
            background-color: #dbeafe;
            border-left: 3px solid #2563eb;
            
            .excel-cell {
              color: #1e40af;
            }
          }

          &.total-row {
            background-color: #3b82f6 !important;
            border-left: 3px solid #1d4ed8 !important;
            
            .data-cell {
              color: white !important;
              font-weight: 600 !important;
              background-color: #3b82f6 !important;
            }
            
            .row-number {
              background-color: #1d4ed8 !important;
              color: white !important;
            }
          }
          
           
           &.selecting {
             background-color: #fbbf24 !important;
             border: 2px solid #f59e0b;
             
             .excel-cell {
               font-weight: 600;
               color: #92400e;
             }
           }
         }
        
        .row-number {
          background: #f1f5f9;
          color: #64748b;
          padding: 4px 8px;
          border-right: 1px solid #e2e8f0;
          font-weight: 600;
          text-align: center;
          width: 50px;
          position: sticky;
          left: 0;
          z-index: 2;
        }
        
        .excel-cell {
          padding: 4px 8px;
          border-right: 1px solid #f1f5f9;
          border-bottom: 1px solid #f1f5f9;
          cursor: pointer;
          transition: all 0.15s ease;
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          
          &:hover {
            background-color: #e0f2fe;
          }
        }
      }
    }
    
    .selection-summary {
      .summary-card {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 20px;
        border-radius: 8px;
        text-align: center;
        
        h4 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
        }
        
        .preview-stats {
          display: flex;
          justify-content: space-around;
          gap: 16px;
          
          .stat {
            display: flex;
            flex-direction: column;
            align-items: center;
            
            .stat-number {
              font-size: 24px;
              font-weight: 700;
              margin-bottom: 4px;
            }
            
            .stat-label {
              font-size: 12px;
              opacity: 0.9;
            }
          }
        }
      }
    }
  }
  
  .boundary-tabs {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .tab-selector {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .tab-btn {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background: #e5e7eb;
      border-color: #9ca3af;
    }
    
    &.active {
      background: #3b82f6;
      border-color: #3b82f6;
      color: white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
  }
  
  .boundary-controls {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
    
    h4 {
      color: #1e293b;
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 16px 0;
    }
  }
  
  .controls-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
  
  .control-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    
    label {
      font-size: 12px;
      font-weight: 500;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: 0.025em;
    }
    
    input {
      background: white;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      padding: 8px 12px;
      font-size: 14px;
      color: #111827;
      transition: border-color 0.2s;
      
      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
  }
  
  .boundary-preview {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    
    .preview-table-container {
      max-height: 300px;
      overflow: auto;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
    }
    
    .preview-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      
      th {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        padding: 8px 12px;
        text-align: left;
        font-weight: 600;
        color: #374151;
        position: sticky;
        top: 0;
        z-index: 10;
      }
      
      .data-cell {
        border: 1px solid #e2e8f0;
        padding: 6px 12px;
        color: #111827;
      }
      
      tr:nth-child(even) {
        background: #f9fafb;
      }
    }
    
    .preview-stats {
      display: flex;
      gap: 24px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #e2e8f0;
      
      .stat {
        color: #6b7280;
        font-size: 14px;
        
        strong {
          color: #111827;
          font-weight: 600;
        }
      }
    }
  }
}

.clean-data-display-step {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 24px;
  margin: 16px 0;
  
  h3 {
    color: #059669;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }
  
  p {
    color: #6b7280;
    font-size: 14px;
    margin: 0 0 24px 0;
    line-height: 1.5;
  }
  
  .clean-data-tabs {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .tab-selector {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .tab-btn {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background: #e5e7eb;
      border-color: #9ca3af;
    }
    
    &.active {
      background: #10b981;
      border-color: #10b981;
      color: white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
  }
  
  .clean-configuration {
    display: flex;
    flex-direction: column;
    gap: 24px;
    
    .config-panel {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 20px;
      
      .config-section {
        margin-bottom: 24px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        h4 {
          color: #374151;
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 16px 0;
        }
        
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          
          .summary-item {
            background: white;
            padding: 12px;
            border-radius: 6px;
            border: 1px solid #e5e7eb;
            
            .label {
              color: #6b7280;
              font-size: 12px;
              display: block;
              margin-bottom: 4px;
            }
            
            .value {
              color: #059669;
              font-size: 18px;
              font-weight: 600;
            }
          }
        }
        
        .total-rows-config {
          p {
            color: #374151;
            font-size: 14px;
            margin: 0 0 16px 0;
            line-height: 1.5;
          }
          
          .total-row-categories {
            .category-legend {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 12px;
              margin-bottom: 20px;
              padding: 16px;
              background: white;
              border-radius: 6px;
              border: 1px solid #e5e7eb;

              .legend-item {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 12px;
                color: #374151;

                .legend-color {
                  width: 16px;
                  height: 16px;
                  border-radius: 3px;
                  border: 1px solid #ccc;

                  &.hard-costs { background: #FF6B6B; }
                  &.soft-costs { background: #4ECDC4; }
                  &.acquisition { background: #45B7D1; }
                  &.financing { background: #96CEB4; }
                  &.project-total { background: #FECA57; }
                  &.unmapped { background: #DDD6FE; }
                }
              }
            }

            .total-mapping-controls {
              padding: 16px;
              background: white;
              border-radius: 6px;
              border: 1px solid #e5e7eb;
              margin-bottom: 20px;

              label {
                font-weight: 500;
                color: #374151;
                font-size: 14px;
                display: block;
                margin-bottom: 12px;
              }

              .mapping-mode-buttons {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
                margin-bottom: 12px;

                .mapping-mode-btn {
                  padding: 8px 12px;
                  border: 2px solid #e5e7eb;
                  border-radius: 6px;
                  background: white;
                  cursor: pointer;
                  font-weight: 500;
                  font-size: 13px;
                  transition: all 0.2s;
                  
                  &:hover {
                    border-color: #9ca3af;
                    background: #f9fafb;
                  }

                  &.active {
                    color: white;
                    border-color: transparent;
                    transform: scale(1.05);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                  }

                  &.hard-costs.active {
                    background: #DC2626;
                  }

                  &.soft-costs.active {
                    background: #059669;
                  }

                  &.acquisition.active {
                    background: #2563EB;
                  }

                  &.financing.active {
                    background: #D97706;
                  }

                  &.project-total.active {
                    background: #7C2D12;
                  }

                  &.clear.active {
                    background: #6B7280;
                  }
                }
              }

              .ai-status-indicator {
                padding: 8px 12px;
                background: #f0f9ff;
                border: 1px solid #0ea5e9;
                color: #0369a1;
                border-radius: 4px;
                text-align: left;
                font-size: 12px;
                font-weight: 400;
                margin-top: 8px;
                
                .ai-indicator {
                  display: inline-block;
                  animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.6; }
                }
              }
            }

            .mapped-totals-summary {
              background: white;
              border-radius: 6px;
              border: 1px solid #e5e7eb;
              padding: 16px;
              
              h5 {
                margin: 0 0 12px 0;
                font-size: 14px;
                color: #374151;
                font-weight: 600;
              }

              .mapped-totals-list {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .mapped-total-item {
                  display: flex;
                  align-items: center;
                  gap: 12px;
                  padding: 8px 12px;
                  background: #f9fafb;
                  border: 1px solid #e5e7eb;
                  border-radius: 4px;

                  .row-indicator {
                    font-weight: 500;
                    color: #6b7280;
                    min-width: 60px;
                    font-size: 12px;
                  }

                  .total-type {
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 500;
                    flex: 1;

                    &.hard_costs { background: #FEE2E2; color: #DC2626; }
                    &.soft_costs { background: #D1FAE5; color: #059669; }
                    &.acquisition { background: #DBEAFE; color: #2563EB; }
                    &.financing { background: #D1F2EB; color: #047857; }
                    &.project_total { background: #FEF3C7; color: #D97706; }
                  }

                  .remove-mapping {
                    background: #EF4444;
                    color: white;
                    border: none;
                    border-radius: 3px;
                    width: 20px;
                    height: 20px;
                    cursor: pointer;
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.2s;

                    &:hover {
                      background: #DC2626;
                    }
                  }
                }
              }
            }
          }
          
          .total-mapping-status {
            .pattern-status {
              padding: 12px 16px;
              background: #f0f9ff;
              border: 1px solid #bae6fd;
              border-radius: 6px;
              margin-bottom: 16px;
              
              .status-indicator {
                color: #0369a1;
                font-size: 14px;
                font-weight: 500;
              }
            }
            
            .mapped-totals-summary {
              .mapped-totals-list {
                .total-type-simple {
                  padding: 4px 8px;
                  border-radius: 4px;
                  font-size: 11px;
                  font-weight: 500;
                  background: #e0f2fe;
                  color: #0369a1;
                  flex: 1;
                }
              }
            }
          }
        }
      }
    }
    
    .full-clean-data-view {
      h4 {
        color: #374151;
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 16px 0;
      }
      
      .clean-table-container {
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        overflow: auto;
        max-height: 60vh;
        background: white;
        margin-bottom: 16px;
        
        .compact-data-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 10px;
          
          .row-header {
            background: #374151;
            color: white;
            padding: 4px 6px;
            text-align: center;
            font-weight: 600;
            position: sticky;
            left: 0;
            z-index: 3;
            width: 30px;
            font-size: 9px;
          }
          
          .data-header {
            background: #059669;
            color: white;
            padding: 4px 6px;
            font-weight: 600;
            position: sticky;
            top: 0;
            z-index: 2;
            max-width: 80px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 9px;
          }
          
          .row-number {
            background: #f9fafb;
            color: #6b7280;
            padding: 2px 4px;
            text-align: center;
            font-weight: 600;
            border-right: 1px solid #e5e7eb;
            position: sticky;
            left: 0;
            z-index: 1;
            font-size: 8px;
          }
          
          .data-cell {
            padding: 2px 4px;
            border-right: 1px solid #f3f4f6;
            border-bottom: 1px solid #f3f4f6;
            max-width: 80px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: #374151;
            font-size: 9px;
            
            &:last-child {
              border-right: none;
            }
          }
          
          .clickable-row {
            cursor: pointer;
            transition: background-color 0.15s ease;
            
            &:hover {
              background-color: #f8fafc;
            }
          }
          
          tr.total-hard_costs {
            .data-cell {
              background: #FEE2E2;
              color: #DC2626;
              font-weight: 500;
            }
          }
          
          tr.total-soft_costs {
            .data-cell {
              background: #D1FAE5;
              color: #059669;
              font-weight: 500;
            }
          }
          
          tr.total-acquisition {
            .data-cell {
              background: #DBEAFE;
              color: #2563EB;
              font-weight: 500;
            }
          }
          
          tr.total-financing {
            .data-cell {
              background: #D1F2EB;
              color: #047857;
              font-weight: 500;
            }
          }
          
          tr.total-project_total {
            .data-cell {
              background: #FEF3C7;
              color: #D97706;
              font-weight: 500;
            }
            
            .row-number {
              background: #fbbf24;
              color: #92400e;
            }
          }

          tr.total-row {
            .data-cell {
              background: #3b82f6 !important;
              color: white !important;
              font-weight: 600 !important;
            }

            .row-number {
              background: #1d4ed8 !important;
              color: white !important;
              font-weight: 600 !important;
            }
          }          
          tbody tr:hover .data-cell {
            background: #f0fdf4;
          }
        }
      }
      
      .data-info {
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
        border-radius: 6px;
        padding: 12px;
        
        .info-item {
          color: #065f46;
          font-size: 14px;
          
          strong {
            color: #047857;
          }
          
          span {
            color: #6b7280;
            font-size: 12px;
          }
        }
      }
    }
  }
}

.column-mapping-step {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 24px;
  margin: 16px 0;
  
  h3 {
    color: #7c3aed;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }
  
  p {
    color: #6b7280;
    font-size: 14px;
    margin: 0 0 24px 0;
    line-height: 1.5;
  }
  
  .multi-header-context {
    margin-bottom: 24px;
    
    .context-card {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
      border: 1px solid #0ea5e9;
      border-radius: 8px;
      padding: 20px;
      
      .context-icon {
        font-size: 24px;
        min-width: 32px;
      }
      
      .context-content {
        flex: 1;
        
        h4 {
          margin: 0 0 8px 0;
          color: #0c4a6e;
          font-size: 16px;
          font-weight: 600;
        }
        
        p {
          margin: 0 0 12px 0;
          color: #0369a1;
          font-size: 14px;
        }
        
        .header-examples {
          display: flex;
          flex-direction: column;
          gap: 8px;
          
          .header-example {
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(255, 255, 255, 0.7);
            border-radius: 6px;
            padding: 8px 12px;
            font-family: monospace;
            font-size: 13px;
            
            .original {
              color: #64748b;
              font-weight: 500;
            }
            
            .arrow {
              color: #0ea5e9;
              font-weight: 600;
            }
            
            .combined {
              color: #0c4a6e;
              font-weight: 600;
            }
          }
        }
      }
    }
  }
  
  .mapping-interface {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  
  .mapping-legend {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    
    .legend-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #374151;
      
      .legend-color {
        width: 16px;
        height: 16px;
        border-radius: 4px;
        
        &.required {
          background: #ef4444;
        }
        
        &.optional {
          background: #10b981;
        }
        
        &.mapped {
          background: #8b5cf6;
        }
      }
    }
  }
  
  .mapping-areas {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 24px;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
  
  .source-columns {
    .section-title {
      color: #1e293b;
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 16px 0;
    }
    
    .columns-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-height: 400px;
      overflow-y: auto;
      padding: 16px;
      background: #f9fafb;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
    }
    
    .column-item {
      background: white;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      padding: 12px;
      font-size: 14px;
      color: #374151;
      cursor: grab;
      transition: all 0.2s;
      
      &:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        cursor: grabbing;
      }
    }
  }
  
  .target-mappings {
    .section-title {
      color: #1e293b;
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 16px 0;
    }
    
    .mapping-targets {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    
    .mapping-target {
      border: 2px dashed #d1d5db;
      border-radius: 8px;
      background: #f9fafb;
      transition: all 0.2s;
      
      &.required {
        border-color: #ef4444;
        background: #fef2f2;
      }
      
      &.optional {
        border-color: #10b981;
        background: #f0fdf4;
      }
      
      .target-header {
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 600;
        color: #374151;
        background: rgba(255,255,255,0.8);
        border-bottom: 1px solid #e2e8f0;
      }
      
      .drop-zone {
        padding: 16px;
        min-height: 60px;
        
        &.single-drop {
          min-height: 80px;
        }
        
        .drop-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #9ca3af;
          font-style: italic;
          font-size: 14px;
        }
        
        .mapped-column {
          background: white;
          border: 1px solid #8b5cf6;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 8px;
          font-size: 14px;
          color: #374151;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: grab;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          .remove-btn {
            background: #ef4444;
            color: white;
            border: none;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            font-size: 12px;
            cursor: pointer;
            transition: background-color 0.2s;
            
            &:hover {
              background: #dc2626;
            }
          }
        }
      }
    }
  }

  // Field Selector for Visual Column Mapping
  .field-selector {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;

    h4 {
      margin: 0 0 16px 0;
      color: #1e293b;
      font-size: 16px;
      font-weight: 600;
    }

    .field-buttons {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 16px;

      .field-btn {
        padding: 10px 16px;
        border: 2px solid #e2e8f0;
        border-radius: 6px;
        background: white;
        cursor: pointer;
        font-weight: 500;
        font-size: 14px;
        transition: all 0.2s;
        
        &:hover {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        &.active {
          border-color: #3b82f6;
          background: #dbeafe;
          color: #1e40af;
        }

        &.required.active {
          border-color: #ef4444;
          background: #fee2e2;
          color: #dc2626;
        }

        &.optional.active {
          border-color: #10b981;
          background: #d1fae5;
          color: #059669;
        }
      }
    }

    .mapping-instruction {
      font-size: 14px;
      color: #6b7280;
      
      strong {
        color: #374151;
      }
    }
  }

  .auto-detect-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

     // Excel Grid for Column Mapping
   .excel-grid-mapping {
     background: white;
     border: 1px solid #e2e8f0;
     border-radius: 8px;
     padding: 20px;
     margin-bottom: 24px;

     .column-headers-display {
       margin-bottom: 20px;

       h5 {
         margin: 0 0 12px 0;
         color: #374151;
         font-size: 14px;
         font-weight: 600;
       }

       .headers-grid {
         display: grid;
         grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
         gap: 8px;
         max-height: 200px;
         overflow-y: auto;

         .header-item {
           background: #f8fafc;
           border: 2px solid #e2e8f0;
           border-radius: 6px;
           padding: 8px 12px;
           cursor: pointer;
           transition: all 0.2s;

           &:hover {
             background: #f1f5f9;
             border-color: #cbd5e1;
           }

           .column-number {
             font-size: 11px;
             color: #6b7280;
             font-weight: 500;
           }

           .column-header {
             font-size: 12px;
             color: #374151;
             font-weight: 500;
             margin-top: 2px;
           }

           // Column mapping classes for headers
           &.mapped-description {
             background: #dbeafe;
             border-color: #3b82f6;
             color: #1e40af;
           }

           &.mapped-funded-at-closing {
             background: #fef3c7;
             border-color: #f59e0b;
             color: #92400e;
           }

           &.mapped-original-budget {
             background: #fee2e2;
             border-color: #ef4444;
             color: #dc2626;
           }

           &.mapped-current-budget {
             background: #fef3c7;
             border-color: #f59e0b;
             color: #d97706;
           }

           &.mapped-contractor {
             background: #d1fae5;
             border-color: #10b981;
             color: #059669;
           }

           &.mapped-spent-to-date {
             background: #e9d5ff;
             border-color: #8b5cf6;
             color: #7c3aed;
           }
           
                     &.mapped-remaining-budget {
            background: #fdf2f8;
            border-color: #ec4899;
            color: #be185d;
          }
           
           &.mapped-funded-at-closing {
             background: #eef2ff;
             border-color: #6366f1;
             color: #4338ca;
           }
           
           &.mapped-current-draw {
             background: #f0fdfa;
             border-color: #14b8a6;
             color: #0f766e;
           }
         }
       }
     }

    .excel-table-container {
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      overflow: scroll !important;
      max-height: 60vh;
      max-width: 100%;
      background: white;
      
      /* FORCE visible scroll bars - NUCLEAR OVERRIDE */
      -webkit-overflow-scrolling: auto !important;
      scrollbar-width: auto !important;
      
      &::-webkit-scrollbar {
        -webkit-appearance: none !important;
        width: 22px !important;
        height: 22px !important;
        background: #e2e8f0 !important;
        display: block !important;
      }
      
      &::-webkit-scrollbar-track {
        background: #f1f5f9 !important;
        border: 2px solid #cbd5e1 !important;
        display: block !important;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #64748b !important;
        border-radius: 4px !important;
        border: 1px solid #e2e8f0 !important;
        display: block !important;
        min-height: 30px !important;
        min-width: 30px !important;
      }
      
      &::-webkit-scrollbar-button {
        -webkit-appearance: none !important;
        display: block !important;
        background: #94a3b8 !important;
        height: 22px !important;
        width: 22px !important;
        border: 2px solid #64748b !important;
        border-radius: 0 !important;
      }
      
      &::-webkit-scrollbar-button:single-button {
        display: block !important;
        height: 22px !important;
        width: 22px !important;
      }
      
      &::-webkit-scrollbar-button:vertical:start:decrement {
        display: block !important;
        background: #94a3b8 !important;
        border: 2px solid #64748b !important;
      }
      
      &::-webkit-scrollbar-button:vertical:end:increment {
        display: block !important;
        background: #94a3b8 !important;
        border: 2px solid #64748b !important;
      }
      
      &::-webkit-scrollbar-button:horizontal:start:decrement {
        display: block !important;
        background: #94a3b8 !important;
        border: 2px solid #64748b !important;
      }
      
      &::-webkit-scrollbar-button:horizontal:end:increment {
        display: block !important;
        background: #94a3b8 !important;
        border: 2px solid #64748b !important;
      }
      
      &::-webkit-scrollbar {
        width: 12px;
        height: 12px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f7fafc;
        border-radius: 6px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #cbd5e0;
        border-radius: 6px;
        border: 2px solid #f7fafc;
      }
      
      &::-webkit-scrollbar-thumb:hover {
        background: #a0aec0;
      }
      
      &::-webkit-scrollbar-corner {
        background: #f7fafc;
      }

      .visual-excel-table {
        width: max-content !important;
        min-width: 100% !important;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 12px;
        display: table !important; /* Ensure it's treated as a table */

        .row-number {
          background: #374151;
          color: white;
          padding: 4px 8px;
          text-align: center;
          font-weight: 600;
          position: sticky;
          left: 0;
          min-width: 40px;
          border-right: 1px solid #6b7280;
        }

        .excel-cell {
          padding: 4px 8px;
          border: 1px solid #e5e7eb;
          background: white;
          cursor: pointer;
          min-width: 80px;
          transition: all 0.2s;

          &:hover {
            background: #f3f4f6;
          }

          // Column mapping classes
          &.mapped-description {
            background: #dbeafe !important;
            border-color: #3b82f6 !important;
            color: #1e40af;
            font-weight: 500;
          }

          &.mapped-funded-at-closing {
            background: #fef3c7 !important;
            border-color: #f59e0b !important;
            color: #92400e;
            font-weight: 500;
          }

          &.mapped-original-budget {
            background: #fee2e2 !important;
            border-color: #ef4444 !important;
            color: #dc2626;
            font-weight: 500;
          }

          &.mapped-current-budget {
            background: #fef3c7 !important;
            border-color: #f59e0b !important;
            color: #d97706;
            font-weight: 500;
          }

          &.mapped-contractor {
            background: #d1fae5 !important;
            border-color: #10b981 !important;
            color: #059669;
            font-weight: 500;
          }

          &.mapped-spent-to-date {
            background: #e9d5ff !important;
            border-color: #8b5cf6 !important;
            color: #7c3aed;
            font-weight: 500;
          }
          
          &.mapped-remaining-budget {
            background: #fdf2f8 !important;
            border-color: #ec4899 !important;
            color: #be185d;
            font-weight: 500;
          }
          
          &.mapped-funded-at-closing {
            background: #eef2ff !important;
            border-color: #6366f1 !important;
            color: #4338ca;
            font-weight: 500;
          }
          
          &.mapped-current-draw {
            background: #f0fdfa !important;
            border-color: #14b8a6 !important;
            color: #0f766e;
            font-weight: 500;
          }
        }

        .header-row {
          .excel-cell {
            background: #f8fafc;
            font-weight: 600;
            border-color: #cbd5e1;
          }
        }
      }
    }
  }

  // Mapping Summary
  .mapping-summary {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;

    h4 {
      margin: 0 0 16px 0;
      color: #1e293b;
      font-size: 16px;
      font-weight: 600;
    }

    .mapping-summary-grid {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .mapping-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 6px;

        .field-label {
          font-weight: 600;
          color: #374151;
          min-width: 140px;
        }

        .mapped-columns {
          color: #6b7280;
          font-size: 14px;
        }
      }
    }
  }
}

// Step Progress Indicator
.step-progress {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 24px;
  margin: 24px 0;
  
  .progress-bar {
    background: #f1f5f9;
    height: 6px;
    border-radius: 3px;
    margin-bottom: 20px;
    overflow: hidden;
    
    .progress-fill {
      background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
      height: 100%;
      border-radius: 3px;
      transition: width 0.3s ease;
    }
  }
  
  .step-indicators {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .step-indicator {
      display: flex;
      flex-direction: column;
      align-items: center;
      flex: 1;
      position: relative;
      
      &:not(:last-child)::after {
        content: '';
        position: absolute;
        top: 18px;
        left: 60%;
        right: -40%;
        height: 2px;
        background: #e2e8f0;
        z-index: 1;
      }
      
      &.completed:not(:last-child)::after {
        background: #3b82f6;
      }
      
      .step-circle {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 600;
        background: #f1f5f9;
        border: 2px solid #e2e8f0;
        color: #64748b;
        position: relative;
        z-index: 2;
        transition: all 0.3s ease;
      }
      
      .step-label {
        margin-top: 8px;
        font-size: 12px;
        font-weight: 500;
        color: #64748b;
        text-align: center;
        line-height: 1.3;
        max-width: 120px;
      }
      
      &.completed {
        .step-circle {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
        }
        
        .step-label {
          color: #3b82f6;
          font-weight: 600;
        }
      }
      
      &.active {
        .step-circle {
          background: #2563eb;
          border-color: #2563eb;
          color: white;
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.2);
        }
        
        .step-label {
          color: #2563eb;
          font-weight: 700;
        }
      }
      
      &.upcoming {
        .step-circle {
          background: white;
          border-color: #e2e8f0;
          color: #94a3b8;
        }
      }
    }
  }
}

// Action buttons at bottom
.action-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  
  .spacer {
    flex: 1;
  }
  
  .modern-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.2s ease;
    cursor: pointer;
    border: 2px solid transparent;
    min-height: 44px;
    
    .btn-content {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .btn-icon {
      font-size: 16px;
      
      &.spinning {
        animation: spin 1s linear infinite;
      }
    }
    
    .btn-text {
      font-weight: 600;
    }
    
    &.btn-primary {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      color: white;
      
      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
      }
      
      &:disabled {
        background: #94a3b8;
        cursor: not-allowed;
        opacity: 0.6;
      }
    }
    
    &.btn-secondary {
      background: #f8fafc;
      color: #475569;
      border-color: #e2e8f0;
      
      &:hover {
        background: #f1f5f9;
        border-color: #cbd5e1;
        transform: translateY(-1px);
      }
    }
    
    &.btn-success {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      color: white;
      
      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
      }
      
      &:disabled {
        background: #94a3b8;
        cursor: not-allowed;
        opacity: 0.6;
      }
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// NEW: PDF Page Selection Styles
.pdf-page-selection-step {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 24px;
  margin: 24px 0;

  .page-selection-header {
    margin-bottom: 24px;

    h3 {
      color: #1e293b;
      margin: 0 0 8px 0;
      font-size: 20px;
      font-weight: 600;
    }

    p {
      color: #64748b;
      margin: 0 0 20px 0;
      font-size: 14px;
    }

    .selection-summary {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 16px;

      .summary-stats {
        display: flex;
        gap: 24px;
        flex-wrap: wrap;

        .stat-item {
          font-size: 14px;
          color: #475569;

          strong {
            color: #1e293b;
            font-weight: 600;
          }
        }
      }

      .summary-actions {
        display: flex;
        gap: 8px;

        .btn-select-all, .btn-clear-all {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid #e2e8f0;
        }

        .btn-select-all {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;

          &:hover {
            background: #2563eb;
          }
        }

        .btn-clear-all {
          background: white;
          color: #64748b;

          &:hover {
            background: #f1f5f9;
          }
        }
      }
    }
  }

  .pdf-page-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
    gap: 30px;
    margin-bottom: 24px;

    .pdf-page-card {
      background: white;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
      transition: all 0.2s ease;

      &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }

      .page-preview {
        position: relative;
        background: #f8fafc;
        cursor: pointer;

        .page-image {
          width: 100%;
          height: 500px;
          object-fit: contain;
          background: white;
          transition: transform 0.3s ease, filter 0.3s ease;

          &:hover {
            transform: scale(1.05);
            filter: brightness(1.1);
          }
        }

        .page-number {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(0,0,0,0.8);
          color: white;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          z-index: 2;
        }

        .magnify-hint {
          position: absolute;
          bottom: 8px;
          right: 8px;
          background: rgba(0,0,0,0.8);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 2;
        }

        &:hover .magnify-hint {
          opacity: 1;
        }
      }

      .page-controls {
        padding: 20px;

        .page-checkbox {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
          cursor: pointer;

          input[type="checkbox"] {
            width: 18px;
            height: 18px;
          }

          .checkbox-label {
            font-weight: 500;
            color: #374151;
            font-size: 16px;
          }
        }

        .doc-type-checkboxes {
          margin-bottom: 12px;

          .checkbox-group {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }

          .doc-type-checkbox {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            background: #ffffff;
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 80px;
            font-size: 14px;

            &:hover {
              border-color: #3b82f6;
              background: #f8fafc;
            }

            input[type="checkbox"] {
              margin: 0;
              accent-color: #3b82f6;
            }

            input[type="checkbox"]:checked + .checkbox-label {
              font-weight: 500;
              color: #3b82f6;
            }

            &:has(input:checked) {
              border-color: #3b82f6;
              background: #eff6ff;
            }

            &:has(input:disabled) {
              opacity: 0.5;
              cursor: not-allowed;
              background: #f9fafb;
            }

            .checkbox-label {
              color: #374151;
              font-size: 14px;
              cursor: pointer;
            }
          }
        }

        .ai-suggestion {
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 6px;
          padding: 12px;
          font-size: 14px;

          .btn-accept-ai {
            background: #f59e0b;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 13px;
            margin-left: 10px;
            cursor: pointer;
          }
        }
      }
    }
  }

  .selected-pages-summary {
    background: #f0f9ff;
    border: 1px solid #0ea5e9;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 24px;

    h4 {
      color: #0c4a6e;
      margin: 0 0 12px 0;
      font-size: 16px;
    }

    .selected-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .selected-item {
        display: flex;
        align-items: center;
        gap: 8px;
        background: white;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        padding: 8px 12px;
        font-size: 14px;

        .page-info {
          font-weight: 500;
          color: #374151;
        }

        .doc-type-badge {
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 11px;
          font-weight: 500;

          &.type-gc_703 {
            background: #dbeafe;
            color: #1e40af;
          }

          &.type-sub_702 {
            background: #dcfce7;
            color: #166534;
          }

          &.type-gc_702 {
            background: #fef3c7;
            color: #92400e;
          }
        }

        .btn-remove {
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 12px;
        }
      }
    }
  }

  .page-selection-actions {
    text-align: center;

    .btn-process-pages {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
      }

      &:disabled {
        background: #94a3b8;
        cursor: not-allowed;
        opacity: 0.6;
      }
    }

    .selection-warning {
      color: #f59e0b;
      font-size: 14px;
      margin-top: 8px;
      font-weight: 500;
    }
  }
}

/* Image Modal Styles */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.image-modal-content {
  background: white;
  border-radius: 12px;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  animation: slideIn 0.3s ease;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }

  .modal-close {
    background: none;
    border: none;
    font-size: 28px;
    color: #6b7280;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
      background: #f3f4f6;
      color: #374151;
    }
  }
}

.modal-image-container {
  flex: 1;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  .modal-image {
    max-width: 100%;
    max-height: 75vh;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
}

.modal-footer {
  padding: 16px 24px;
  text-align: center;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;

  p {
    margin: 0;
    color: #6b7280;
    font-size: 14px;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to { 
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Close modal with Escape key */
.image-modal-overlay.escape-pressed {
  animation: fadeOut 0.2s ease forwards;
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* AI Mapping Section Styles */
.ai-mapping-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  text-align: center;

  .ai-mapping-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px rgba(102, 126, 234, 0.25);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(102, 126, 234, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .ai-mapping-help {
    margin: 8px 0 0 0;
    font-size: 12px;
    color: #64748b;
  }
}

/* Field Dots - Match Exact Grid Colors */
.field-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: middle;
  border: 1px solid rgba(0, 0, 0, 0.1);

  &.description-dot {
    background-color: #3b82f6; /* Blue - matches mapped-description */
  }

  &.funded-at-closing-dot {
    background-color: #f59e0b; /* Amber - matches mapped-funded-at-closing */
  }

  &.original-budget-dot {
    background-color: #ef4444; /* Red - matches mapped-original-budget */
  }

  &.current-budget-dot {
    background-color: #f59e0b; /* Orange - matches mapped-current-budget */
  }

  &.contractor-dot {
    background-color: #10b981; /* Green - matches mapped-contractor */
  }

  &.spent-to-date-dot {
    background-color: #8b5cf6; /* Purple - matches mapped-spent-to-date */
  }

  &.remaining-budget-dot {
    background-color: #ec4899; /* Pink - matches mapped-remaining-budget */
  }

  &.funded-at-closing-dot {
    background-color: #6366f1; /* Indigo - matches mapped-funded-at-closing */
  }

  &.current-draw-dot {
    background-color: #14b8a6; /* Teal - matches mapped-current-draw */
  }
}

/* QA Step Configuration Cards */
.template-qa-step {
  .qa-config-cards {
    display: flex;
    gap: 20px;
    margin: 24px 0;
    
    .config-card {
      flex: 1;
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      
      .card-header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        padding: 16px;
        
        h3 {
          color: white;
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }
      }
      
      .card-content {
        padding: 20px;
        
        .config-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f1f5f9;
          
          &:last-child {
            margin-bottom: 0;
            padding-bottom: 0;
            border-bottom: none;
          }
          
          .label {
            font-weight: 500;
            color: #374151;
          }
          
          .value {
            font-weight: 600;
            color: #1f2937;
            text-align: right;
            max-width: 60%;
            word-break: break-word;
          }
        }
        
        .total-rows-section {
          flex-direction: column;
          align-items: stretch;
          
          .total-rows-list {
            margin-top: 8px;
            
            .total-row-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 6px 12px;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              margin-bottom: 4px;
              
              &:last-child {
                margin-bottom: 0;
              }
              
              .row-description {
                font-weight: 500;
                color: #374151;
                flex: 1;
              }
              
              .row-number {
                font-size: 12px;
                color: #6b7280;
                background: #e2e8f0;
                padding: 2px 6px;
                border-radius: 4px;
              }
            }
            
            .no-totals {
              color: #9ca3af;
              font-style: italic;
              text-align: center;
              padding: 8px;
            }
          }
        }
        
        .all-mappings {
          .mapping-item {
            margin-bottom: 16px;
            
            &:last-child {
              margin-bottom: 0;
            }
            
            .mapping-field {
              font-weight: 600;
              color: #374151;
              margin-bottom: 6px;
              font-size: 14px;
            }
            
            .mapping-columns {
              .column-mapping {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 4px;
                margin-bottom: 4px;
                
                &:last-child {
                  margin-bottom: 0;
                }
                
                .column-name {
                  font-weight: 500;
                  color: #1f2937;
                  flex: 1;
                }
                
                .column-letter {
                  font-size: 11px;
                  color: #6b7280;
                  background: #e2e8f0;
                  padding: 1px 4px;
                  border-radius: 3px;
                  font-family: monospace;
                }
              }
              
              .not-mapped {
                color: #9ca3af;
                font-style: italic;
                font-size: 13px;
                padding: 4px 8px;
              }
            }
          }
        }
      }
      
      // Category Totals Card Styles
      &.category-totals-card {
        .card-header {
          background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
        }
        
        .card-description {
          color: #6b7280;
          font-size: 14px;
          margin-bottom: 16px;
        }
        
        .category-totals-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
          
          .category-total-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 16px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            transition: all 0.2s ease;
            
            &:hover {
              background: #f1f5f9;
              border-color: #cbd5e1;
            }
            
            .category-info {
              display: flex;
              align-items: center;
              gap: 12px;
              
              .category-icon {
                font-size: 20px;
              }
              
              .category-name {
                font-weight: 600;
                color: #374151;
                font-size: 14px;
              }
            }
            
            .category-status {
              display: flex;
              align-items: center;
              gap: 8px;
              
              .status-icon {
                width: 20px;
                height: 20px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                font-weight: bold;
                
                &.detected {
                  background: #dcfce7;
                  color: #16a34a;
                }
                
                &.not-detected {
                  background: #fef2f2;
                  color: #dc2626;
                }
              }
              
              .status-text {
                font-size: 13px;
                font-weight: 500;
                color: #6b7280;
              }
            }
            
            .category-details {
              font-size: 12px;
              color: #9ca3af;
              font-style: italic;
            }
          }
        }
        
        .no-totals-warning {
          margin-top: 16px;
          padding: 12px;
          background: #fef3cd;
          border: 1px solid #f59e0b;
          border-radius: 6px;
          
          p {
            margin: 0;
            color: #92400e;
            font-size: 13px;
            line-height: 1.4;
          }
        }
      }
    }
  }
  
  .qa-dda-section {
    margin: 32px 0;
    
    h3 {
      color: #1f2937;
      margin-bottom: 8px;
      font-size: 18px;
      font-weight: 600;
    }
    
    .dda-subtitle {
      color: #6b7280;
      margin-bottom: 20px;
      font-size: 14px;
    }
    
    .auto-match-btn {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 6px rgba(102, 126, 234, 0.25);
      white-space: nowrap;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 12px rgba(102, 126, 234, 0.4);
      }
    }
    
    .dda-preview-grid {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
      
      .grid-header {
        display: grid;
        grid-template-columns: 80px 2fr 1.2fr 1.5fr 100px;
        background: #f8fafc;
        border-bottom: 2px solid #e2e8f0;
        
        .grid-cell.header {
          padding: 16px 12px;
          font-weight: 600;
          color: #374151;
          font-size: 14px;
          border-right: 1px solid #e2e8f0;
          
          &:last-child {
            border-right: none;
          }
        }
      }
      
      .grid-rows {
        .grid-row {
          display: grid;
          grid-template-columns: 80px 2fr 1.2fr 1.5fr 100px;
          border-bottom: 1px solid #f1f5f9;
          
          &:last-child {
            border-bottom: none;
          }
          
          &:nth-child(even) {
            background: #fafbfc;
          }
          
          .grid-cell {
            padding: 12px;
            font-size: 13px;
            border-right: 1px solid #e2e8f0;
            display: flex;
            align-items: center;
            
            &:last-child {
              border-right: none;
            }
            
            &.row-number {
              justify-content: center;
              background: #f3f4f6;
              font-weight: 600;
              color: #6b7280;
              font-size: 12px;
            }
            
            &.budget {
              font-family: monospace;
              justify-content: flex-end;
              color: #059669;
              font-weight: 500;
            }
            
            &.dda-term {
              color: #6366f1;
              font-weight: 500;
              font-style: italic;
            }
            
            &.confidence {
              justify-content: center;
              
              .confidence-high {
                color: #059669;
                font-weight: 600;
              }
              
              .confidence-medium {
                color: #d97706;
                font-weight: 600;
              }
              
              .confidence-low {
                color: #dc2626;
                font-weight: 600;
              }
              
              .confidence-pending {
                color: #6b7280;
                font-style: italic;
                font-size: 12px;
              }
            }
          }
        }
      }
      
      .no-preview {
        padding: 40px;
        text-align: center;
        color: #6b7280;
        font-style: italic;
        background: #f9fafb;
      }
      
      .show-more {
        padding: 16px;
        text-align: center;
        background: #f8fafc;
        border-top: 1px solid #e2e8f0;
        color: #6b7280;
        font-size: 13px;
      }
      
      // Edit DDA button styling
      .dda-term {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        
        .edit-dda-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
          
          &:hover {
            background: #2563eb;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
          }
          
          &:active {
            transform: translateY(0);
          }
        }
      }
    }
  }
}

/* DDA Term Modal Styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.contractor-modal.dda-term-modal {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90vw;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  animation: slideIn 0.3s ease;
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    
    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: white;
    }
    
    .close-button {
      background: none;
      border: none;
      font-size: 24px;
      color: white;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: background 0.2s ease;
      
      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }
  
  .modal-body {
    padding: 24px;
    max-height: 60vh;
    overflow-y: auto;
    
    .auto-match-info {
      margin-bottom: 20px;
      
      .match-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        
        &.ml-match {
          background: #dcfce7;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        }
        
        &.keyword-match {
          background: #fef3c7;
          color: #d97706;
          border: 1px solid #fed7aa;
        }
        
        .confidence {
          background: rgba(0, 0, 0, 0.1);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 12px;
        }
      }
    }
    
    .form-section {
      margin-bottom: 20px;
      
      .form-label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #374151;
      }
      
      .contractor-search-container {
        position: relative;
        
        .contractor-search-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s ease;
          
          &:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
        }
        
        .contractor-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          max-height: 200px;
          overflow-y: auto;
          z-index: 10;
          
          .contractor-option {
            padding: 12px;
            cursor: pointer;
            border-bottom: 1px solid #f3f4f6;
            transition: background 0.2s ease;
            
            &:hover {
              background: #f9fafb;
            }
            
            &.selected {
              background: #eff6ff;
              border-color: #dbeafe;
            }
            
            .contractor-name {
              font-weight: 500;
              color: #1f2937;
              margin-bottom: 4px;
            }
            
            .contractor-address {
              font-size: 12px;
              color: #6b7280;
            }
          }
        }
        
        .no-results {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 12px;
          text-align: center;
          color: #6b7280;
          font-style: italic;
        }
      }
    }
    
    .selected-contractor-section {
      margin-top: 20px;
      
      h4 {
        margin: 0 0 12px 0;
        font-size: 16px;
        color: #374151;
      }
      
      .selected-contractor-card {
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 16px;
        
        .contractor-name {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
        }
        
        .contractor-address {
          color: #6b7280;
          font-size: 14px;
          margin-bottom: 4px;
        }
        
        .clear-selection-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          margin-top: 8px;
          transition: background 0.2s ease;
          
          &:hover {
            background: #dc2626;
          }
        }
      }
    }
  }
  
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
    
    .btn-secondary {
      background: #6b7280;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.2s ease;
      
      &:hover {
        background: #4b5563;
      }
    }
    
    .btn-primary {
      background: #3b82f6;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.2s ease;
      
      &:hover {
        background: #2563eb;
      }
      
      &:disabled {
        background: #d1d5db;
        cursor: not-allowed;
      }
    }
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* QA Data Section Styling */
.qa-data-section {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.qa-data-section .section-header {
  margin-bottom: 20px;
}

.qa-data-section .section-header h3 {
  margin: 0 0 8px 0;
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
}

.qa-data-section .section-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.data-summary-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.data-summary-grid {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

.grid-header {
  display: grid;
  grid-template-columns: 60px 2fr 120px 120px 120px 120px 140px;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}

.grid-header .grid-cell.header {
  padding: 14px 12px;
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  text-align: left;
  border-right: 1px solid #e2e8f0;
}

.grid-header .grid-cell.header:last-child {
  border-right: none;
}

.grid-rows {
  max-height: 500px;
  overflow-y: auto;
}

.grid-row {
  display: grid;
  grid-template-columns: 60px 2fr 120px 120px 120px 120px 140px;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s ease;
}

.grid-row.total-row {
  background-color: #fef3c7;
  font-weight: 600;
}

.grid-row.total-row:hover {
  background-color: #fde68a;
}

.grid-row:hover {
  background-color: #f8fafc;
}

.grid-row:last-child {
  border-bottom: none;
}

.grid-cell {
  padding: 12px;
  font-size: 13px;
  border-right: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
}

.grid-cell:last-child {
  border-right: none;
}

.grid-cell.row-number {
  color: #64748b;
  font-weight: 500;
  justify-content: center;
  background: #fafbfc;
}

.grid-cell.budget {
  color: #059669;
  font-weight: 500;
  justify-content: flex-end;
  font-family: 'Monaco', 'Menlo', monospace;
}

.grid-cell.status {
  justify-content: center;
}

.status-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.ready {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.category-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  text-transform: capitalize;
  
  &.hard_costs {
    background: #fee2e2;
    color: #dc2626;
  }
  
  &.soft_costs {
    background: #dcfce7;
    color: #16a34a;
  }
  
  &.financing {
    background: #fef3c7;
    color: #d97706;
  }
  
  &.acquisition {
    background: #e0e7ff;
    color: #4338ca;
  }
  
  &.project_total {
    background: #ede9fe;
    color: #7c3aed;
  }
}

.show-more {
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  text-align: center;
  font-size: 13px;
  color: #64748b;
}

.no-preview {
  text-align: center;
  padding: 40px 20px;
}

.empty-state {
  max-width: 300px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-state p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Clean Progress Bar Styling (mockup style) */
.step-header-layout {
  margin-bottom: 40px;
  padding: 30px 20px;
  background: #f8fafc;
  border-radius: 12px;
}

.progress-container {
  padding: 0;
  margin: 0;
}

.progress-steps {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  padding: 0 50px;
}

.progress-steps::before {
  content: '';
  position: absolute;
  top: 25px;
  left: 80px;
  right: 80px;
  height: 3px;
  background: #e2e8f0;
  z-index: 0;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  
  .step-circle {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: white;
    border: 3px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
    color: #94a3b8;
    margin-bottom: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .step-title {
    font-size: 14px;
    color: #64748b;
    text-align: center;
    white-space: nowrap;
    font-weight: 500;
  }
  
  &.active {
    .step-circle {
      background: #3b82f6;
      border-color: #3b82f6;
      color: white;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
      transform: scale(1.1);
    }
    
    .step-title {
      color: #2563eb;
      font-weight: 700;
      font-size: 15px;
    }
  }
  
  &.completed {
    .step-circle {
      background: #10b981;
      border-color: #10b981;
      color: white;
      font-size: 22px;
    }
    
    .step-title {
      color: #374151;
      font-weight: 600;
    }
  }
}

/* Step 4: Total Rows Selection Styles (matches Step 3) */
.total-rows-selection-step {
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.total-rows-interface {
  margin-top: 20px;
}

.category-selector {
  margin-bottom: 20px;
  
  h4 {
    margin: 0 0 15px 0;
    color: #374151;
    font-size: 16px;
    font-weight: 600;
  }
}

.category-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}

.category-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  transition: all 0.2s ease;
  position: relative;
  min-width: 200px;
  max-width: 260px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  
  &:hover {
    background: #fafafa;
    border-color: #d1d5db;
    transform: translateY(-3px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
  
  &.active {
    background: #ffffff;
    border-color: currentColor;
    border-width: 3px;
    font-weight: 700;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.15), 0 4px 8px -2px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  
  span {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
  }
  
  .tooltip-hint {
    display: block;
    font-size: 12px;
    color: #6b7280;
    font-weight: 400;
    font-style: italic;
    margin-top: 3px;
    line-height: 1.2;
  }
  
  // Category-specific colors
  &.hard-costs {
    &.active {
      color: #dc2626;
      border-color: #dc2626;
      background: #fef2f2;
    }
  }
  
  &.soft-costs {
    &.active {
      color: #16a34a;
      border-color: #16a34a;
      background: #f0fdf4;
    }
  }
  
  &.financing {
    &.active {
      color: #d97706;
      border-color: #d97706;
      background: #fffbeb;
    }
  }
  
  &.acquisition {
    &.active {
      color: #4338ca;
      border-color: #4338ca;
      background: #f0f9ff;
    }
  }
  
  &.project-total {
    &.active {
      color: #7c3aed;
      border-color: #7c3aed;
      background: #faf5ff;
    }
  }
}

.category-icon-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  border-radius: 10px;
  flex: 0 0 40px;
  font-size: 20px;
  transition: all 0.2s ease;
  
  &.hard_costs-badge {
    background: #ef4444;
    color: white;
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
  }
  
  &.soft_costs-badge {
    background: #22c55e;
    color: white;
    box-shadow: 0 2px 4px rgba(34, 197, 94, 0.3);
  }
  
  &.financing-badge {
    background: #f59e0b;
    color: white;
    box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
  }
  
  &.acquisition-badge {
    background: #6366f1;
    color: white;
    box-shadow: 0 2px 4px rgba(99, 102, 241, 0.3);
  }
  
  &.project_total-badge {
    background: #8b5cf6;
    color: white;
    box-shadow: 0 2px 4px rgba(139, 92, 246, 0.3);
  }
  
  &.contingencies-badge {
    background: #06b6d4;
    color: white;
    box-shadow: 0 2px 4px rgba(6, 182, 212, 0.3);
  }
  
  i {
    font-size: 20px;
  }
}

.category-btn.active {
  &.hard_costs {
    border-color: #ef4444;
    background: #fef2f2;
  }
  
  &.soft_costs {
    border-color: #22c55e;
    background: #f0fdf4;
  }
  
  &.financing {
    border-color: #f59e0b;
    background: #fffbeb;
  }
  
  &.acquisition {
    border-color: #6366f1;
    background: #f5f3ff;
  }
  
  &.project_total {
    border-color: #8b5cf6;
    background: #faf5ff;
  }
  
  .category-icon-badge {
    transform: scale(1.1);
    
    &.hard_costs-badge {
      background: #dc2626;
      box-shadow: 0 3px 6px rgba(220, 38, 38, 0.4);
    }
    
    &.soft_costs-badge {
      background: #16a34a;
      box-shadow: 0 3px 6px rgba(22, 163, 74, 0.4);
    }
    
    &.financing-badge {
      background: #ea580c;
      box-shadow: 0 3px 6px rgba(234, 88, 12, 0.4);
    }
    
    &.acquisition-badge {
      background: #4f46e5;
      box-shadow: 0 3px 6px rgba(79, 70, 229, 0.4);
    }
    
    &.project_total-badge {
      background: #7c3aed;
      box-shadow: 0 3px 6px rgba(124, 58, 237, 0.4);
    }
  }
}

// Step 4 Total Row Grid Styling (matches Step 3)
.total-rows-selection-step .excel-grid-mapping {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.total-rows-selection-step .excel-table-container {
  background: white;
  max-height: 600px;
  overflow: auto;
}

.total-rows-selection-step .visual-excel-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: white;
  
  .row-header {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    padding: 8px 12px;
    text-align: center;
    font-weight: 600;
    color: #64748b;
    position: sticky;
    left: 0;
    z-index: 10;
    min-width: 60px;
  }
  
  .row-header-spacer {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    padding: 8px 12px;
    position: sticky;
    left: 0;
    z-index: 10;
    min-width: 60px;
  }
  
  .data-header {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 8px 12px;
    text-align: center;
    font-weight: 600;
    color: #374151;
    font-size: 12px;
  }
  
  .clickable-total-row-number {
    background: #f9fafb;
    border: 1px solid #e2e8f0;
    padding: 8px 12px;
    text-align: center;
    font-weight: 600;
    cursor: pointer;
    position: sticky;
    left: 0;
    z-index: 5;
    transition: all 0.2s ease;
    min-width: 60px;
    
    &:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }
    
    // Row number selection colors
    &.hard_costs-selected {
      background: #fecaca;
      border-color: #f87171;
      color: #dc2626;
      font-weight: 700;
    }
    
    &.soft_costs-selected {
      background: #bbf7d0;
      border-color: #4ade80;
      color: #16a34a;
      font-weight: 700;
    }
    
    &.financing-selected {
      background: #fed7aa;
      border-color: #fb923c;
      color: #d97706;
      font-weight: 700;
    }
    
    &.acquisition-selected {
      background: #c7d2fe;
      border-color: #818cf8;
      color: #4338ca;
      font-weight: 700;
    }
    
    &.project_total-selected {
      background: #d8b4fe;
      border-color: #a855f7;
      color: #7c3aed;
      font-weight: 700;
    }
  }
  
  .excel-cell {
    border: 1px solid #e2e8f0;
    padding: 8px 12px;
    font-size: 12px;
    color: #374151;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    
    // Full row highlighting
    &.hard_costs-cell-selected {
      background: #fef2f2;
      border-color: #fecaca;
    }
    
    &.soft_costs-cell-selected {
      background: #f0fdf4;
      border-color: #bbf7d0;
    }
    
    &.financing-cell-selected {
      background: #fffbeb;
      border-color: #fed7aa;
    }
    
    &.acquisition-cell-selected {
      background: #eff6ff;
      border-color: #c7d2fe;
    }
    
    &.project_total-cell-selected {
      background: #faf5ff;
      border-color: #d8b4fe;
    }
  }
  
  // Full row highlighting classes
  tr.total-row-selected {
    &.hard_costs-row-selected {
      background: #fef2f2;
    }
    
    &.soft_costs-row-selected {
      background: #f0fdf4;
    }
    
    &.financing-row-selected {
      background: #fffbeb;
    }
    
    &.acquisition-row-selected {
      background: #eff6ff;
    }
    
    &.project_total-row-selected {
      background: #faf5ff;
    }
  }
}

.total-mappings-summary {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  
  h4 {
    margin: 0 0 12px 0;
    color: #374151;
    font-size: 16px;
    font-weight: 600;
  }
}

.mappings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.mapping-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  
  .category-label {
    font-weight: 600;
    color: #374151;
  }
  
  .assigned-rows {
    color: #6b7280;
    font-size: 14px;
  }
}

.mappings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.mapping-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  
  .category-label {
    font-weight: 600;
    color: #374151;
  }
  
  .assigned-rows {
    color: #6b7280;
    font-size: 14px;
  }
}

</style>
